
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User } from '@/api/entities';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, LineChart, Line
} from 'recharts';
import { MapContainer, Marker, Polyline, Tooltip as LeafletTooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import DarkBasemap from '@/components/maps/DarkBasemap';
import { cityCoordinates } from '../components/cityCoordinates';
import { getCityName } from '../components/airportUtils';
import RoyalAmbientBackground from '@/components/ui/RoyalAmbientBackground';

// Fix for Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

import { Activity, AlertTriangle, Brain, Loader2, TrendingDown, Shield, Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { backerTermsById, BACKER_MODEL_BASE_FARE, BACKER_MODEL_SERVICE_FEE, INVESTOR_FEE_RATE_ON_SERVICE_FEE } from '@/utils/backerEarnings';
import { format, subDays, addDays, parseISO, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subYears, differenceInCalendarDays, isBefore, isEqual } from 'date-fns';
import {
  calculatePaymentSchedule,
  SLICE_PAY_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
  DEFERA_ZERO_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
  DEFERA_CAPITAL_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
  SLICE_PAY_HOLD_FEE_RATE,
  DEFERA_CAPITAL_HOLD_FEE_RATE,
  getDefaultInstallmentDepositPercentForLeadDays,
} from '@/utils/paymentScheduleCalculator';
import { useInstallmentsCopy } from '@/hooks/useInstallmentsCopy';
import { resolveCarrierLogo, resolveAirlineIata } from '@/utils/airlineBranding';
import { seatPurchaseMarginContribution } from '@/utils/seatPurchaseEconomics';
import { referralSourceLabel } from '@/utils/referralSource';
import AcquisitionGeography from '@/components/analytics/AcquisitionGeography';
import TravelInsuranceSales from '@/components/analytics/TravelInsuranceSales';
import { FAYE_INSURANCE_ENABLED } from '@/utils/fayeFlag';
import { fmtAmount } from '@/utils/money';
import { buildInsuranceBasis } from '@/utils/analyticsInsurance';
import { MILES_LIABILITY_CENTS_PER_MILE } from '../../supabase/functions/_shared/miles-redemption.js';
import { retainedExtensionFeesCents } from '@/utils/paymentExtensionChoices';
import VipBadge from '@/components/admin/VipBadge';

// Currency formatter - ensures consistent formatting everywhere
const formatCurrency = (value) => {
  const num = parseFloat(value) || 0;
  return num.toLocaleString('en-US', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  });
};

/**
 * Calculate how much Slice Pay has recovered from a booking based on payment schedule.
 * Uses deposit + installments due by today. Real-time: even confirmed bookings show partial recovery.
 */
// When actual cost is set (we paid more than customer), use it for cost/expense tracking
function getBaseForCost(flight) {
  const fd = flight.flight_data || flight.flightData || {};
  const ac = parseFloat(fd._actual_cost ?? fd.actual_cost ?? 0) || null;
  if (ac != null && ac > 0) return ac;
  return parseFloat(flight.totalPrice || flight.total_price || flight.total_amount || 0);
}

// A trip is only a real loss if it was actually booked with the airline/hotel.
// Without a confirmation number on file, no funds were ever paid out, so a
// "defaulted" status means we kept the customer payments without any offsetting cost.
function hasConfirmationNumber(flight) {
  const fd = flight.flight_data || flight.flightData || {};
  const conf =
    flight?.confirmation_number ||
    flight?.confirmationNumber ||
    fd?.confirmation_number ||
    fd?.airline_confirmation;
  return !!(conf && String(conf).trim() !== '');
}

// Trip has at least one entry in change_flight_history — i.e. the customer or
// admin changed the booked flight at least once after checkout. Used to surface
// fare-change P&L on unconfirmed trips (Defera hasn't paid the airline yet, so
// the difference between the customer's locked-in base and the new base lands
// on Defera's books as profit/loss the moment the trip is actually booked).
function hasChangeFlightHistory(flight) {
  const hist = flight?.change_flight_history || flight?.changeFlightHistory;
  return Array.isArray(hist) && hist.length > 0;
}

function isNmiProcessor(flight) {
  const fd = flight.flight_data || flight.flightData || {};
  return fd._payment_processor === 'nmi';
}

// Defera Capital is the only product where Defera fronts the airline/hotel cost,
// so it's the only one that can lose money on a default. Covers both processor
// tracks (Stripe subscriptions and NMI). Pre-Capital trips — `installments`
// (Slice Pay) and the unmarked late-2025 bookings — were funded by the
// processor, so a default there costs Defera nothing.
function isDeferaCapital(flight) {
  const fd = flight.flight_data || flight.flightData || {};
  const paymentMethod = fd._payment_method || fd.payment_method || flight.payment_method;
  return paymentMethod === 'defera_capital';
}

function calculateRecoveredFromBooking(flight, today) {
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const status = flight.status || flight.trip_status || 'pending';
  const flightData = flight.flight_data || flight.flightData || {};
  const ps = flightData.payment_schedule || {};
  const getDepartureDate = () => flight.departure_date || flight.departureDate || flightData?.departure_date || flightData?.departureDate || ps?.departure_date || ps?.departureDate || flightData?.slices?.[0]?.segments?.[0]?.departing_at || null;
  const base = parseFloat(flight.totalPrice || flight.total_price || flight.total_amount || 0);
  const stayAmount = flight.stay_data ? (flight.stay_data.totalAmount || flight.stay_data.total_amount || 0) : 0;
  const stay = flight.stay_data || (flight.stays && flight.stays[0]);
  const isFlightHotel = !!(stay && (stay.totalAmount || stay.total_amount));
  const paymentMethod = flightData._payment_method || flightData.payment_method || flight.payment_method || 'installments';
  const isReserveBooking = paymentMethod === 'reserve';
  const isDeferaZeroBooking = paymentMethod === 'defera_zero';
  const isDeferaCapitalBooking = paymentMethod === 'defera_capital';
  const lastPaymentDaysBeforeDeparture =
    isDeferaZeroBooking
      ? DEFERA_ZERO_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE
      : isDeferaCapitalBooking
        ? DEFERA_CAPITAL_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE
        : SLICE_PAY_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE;
  const serviceFee = isFlightHotel ? 0 : parseFloat(flight.service_fee || 0);
  const stayHoldRate = isDeferaCapitalBooking ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE;
  const holdFee = isReserveBooking ? 0 : (parseFloat(flight.hold_fee || 0) + (stayAmount > 0 ? stayAmount * stayHoldRate : 0));
  const cost = base + stayAmount + serviceFee;
  const totalWithHold = cost + holdFee;

  // Completed: full recovery
  if (status === 'completed') return totalWithHold;

  // Cancelled/Defaulted: deposit + 1/4 of installments (default scenario)
  // If no payment schedule, use fraction from grand total (deposit % + 1/4 of installments)
  if (status === 'cancelled' || status === 'defaulted') {
    const grandTotalWithHold = parseFloat(flight.grand_total_with_hold || flight.grandTotalWithHold || totalWithHold) || totalWithHold;
    const preHoldTotal = isReserveBooking
      ? grandTotalWithHold
      : isDeferaCapitalBooking
        ? grandTotalWithHold / (1 + DEFERA_CAPITAL_HOLD_FEE_RATE)
        : grandTotalWithHold / 1.12;
    const depositPct = ps.deposit_percentage ?? ps.depositPercentage ?? 20;
    const deposit = round2(preHoldTotal * (depositPct / 100));
    const installments = round2(grandTotalWithHold - deposit);
    const recoveredFromFraction = round2(deposit + 0.25 * installments);

    const departureDate = getDepartureDate();
    const bookingDate = flight.created_at || flight.createdAt;
    if (!departureDate || !bookingDate) return recoveredFromFraction;

    try {
      const computed = calculatePaymentSchedule({
        bookingDate: typeof bookingDate === 'string' ? bookingDate.split('T')[0] : format(parseISO(bookingDate), 'yyyy-MM-dd'),
        departureDate: typeof departureDate === 'string' ? departureDate.split('T')[0] : format(parseISO(departureDate), 'yyyy-MM-dd'),
        totalWithFee: preHoldTotal,
        depositPercentage: depositPct,
        customDepositAmount: ps.custom_deposit_amount ?? ps.customDepositAmount ?? null,
        daysBeforeDepartureForLastPayment: lastPaymentDaysBeforeDeparture,
        deferaCapitalDepositTiers: isDeferaCapitalBooking,
        holdFeeRate: isDeferaCapitalBooking ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE,
        // Locked at booking time — don't let the recompute snap us into a higher tier as the trip nears departure.
        respectStoredDepositPercentage: Number(ps.deposit_amount_cents) > 0,
      });
      if (!computed?.available) return recoveredFromFraction;
      const depositFromSchedule = computed.depositAmount || round2(preHoldTotal * (depositPct / 100));
      const installmentsFromSchedule = round2(grandTotalWithHold - depositFromSchedule);
      return round2(depositFromSchedule + 0.25 * installmentsFromSchedule);
    } catch (e) {
      return recoveredFromFraction;
    }
  }

  // Confirmed/Pending: use payment schedule + due dates vs today
  const departureDate = getDepartureDate();
  const bookingDate = flight.created_at || flight.createdAt;
  const grandTotalWithHold = parseFloat(flight.grand_total_with_hold || flight.grandTotalWithHold || totalWithHold) || totalWithHold;

  if (!departureDate || !bookingDate) return 0;

  const preHoldTotal = isReserveBooking
    ? grandTotalWithHold
    : isDeferaCapitalBooking
      ? grandTotalWithHold / (1 + DEFERA_CAPITAL_HOLD_FEE_RATE)
      : grandTotalWithHold / 1.12;
  const depositPct = ps.deposit_percentage ?? ps.depositPercentage ?? 20;
  const paymentPlan = ps.payment_plan ?? ps.paymentPlan ?? 'weekly';

  try {
    const computed = calculatePaymentSchedule({
      bookingDate: typeof bookingDate === 'string' ? bookingDate.split('T')[0] : format(parseISO(bookingDate), 'yyyy-MM-dd'),
      departureDate: typeof departureDate === 'string' ? departureDate.split('T')[0] : format(parseISO(departureDate), 'yyyy-MM-dd'),
      totalWithFee: preHoldTotal,
        depositPercentage: depositPct,
        customDepositAmount: ps.custom_deposit_amount ?? ps.customDepositAmount ?? null,
        daysBeforeDepartureForLastPayment: lastPaymentDaysBeforeDeparture,
        deferaCapitalDepositTiers: isDeferaCapitalBooking,
        holdFeeRate: isDeferaCapitalBooking ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE,
        // Same locked-deposit guard as the cancelled/defaulted branch above.
        respectStoredDepositPercentage: Number(ps.deposit_amount_cents) > 0,
    });

    if (!computed?.available) return round2(preHoldTotal * (depositPct / 100)); // Deposit only fallback

    const activePlan = paymentPlan === 'weekly' ? computed.weeklyPlan : computed.biweeklyPlan;
    if (!activePlan?.schedule?.length) return computed.depositAmount || 0;

    const depositAmount = computed.depositAmount || round2(preHoldTotal * (depositPct / 100));
    const paymentAmount = activePlan.paymentAmount || 0;
    const todayStart = startOfDay(today);

    let paidInstallments = 0;
    for (const dateStr of activePlan.schedule) {
      const dueDate = startOfDay(parseISO(dateStr));
      if (isBefore(dueDate, todayStart) || isEqual(dueDate, todayStart)) {
        paidInstallments++;
      } else break;
    }

    const installmentTotal = round2(paidInstallments * paymentAmount);
    const recovered = Math.min(depositAmount + installmentTotal, grandTotalWithHold);
    return round2(recovered);
  } catch (e) {
    return round2(preHoldTotal * (depositPct / 100)); // Deposit only fallback
  }
}

const stripAnalysisLabels = (text) => {
  if (typeof text !== 'string') return text;
  return text.replace(/\b(AIRLINE|DOB INTERACTIONS|DAY OF WEEK|FINANCIAL|ORIGIN & DESTINATION|LEAD TIME|PASSENGER DEEP DIVE|PASSENGER GROUP DYNAMICS|NUMEROLOGY|MOON PHASE|DEMOGRAPHICS|TRAVEL TIMES|CONSISTENCY & STATS|ZODIAC|ROUTE|AGE|AGES):\s*/gi, '').replace(/\n{3,}/g, '\n\n').trim();
};

export default function Analytics() {
  const ic = useInstallmentsCopy();
  const [flights, setFlights] = useState([]);
  const [users, setUsers] = useState([]);
  const [abandonedTrips, setAbandonedTrips] = useState([]);
  const [userCount, setUserCount] = useState(0);
  const [abandonedCount, setAbandonedCount] = useState(0);
  const [referralSourceData, setReferralSourceData] = useState([]);
  /** Acquisition & geography report from /api/admin/acquisition-geo — channel
   *  mix, origin + billing geography, weekly cohorts and detected signup waves. */
  const [acquisitionData, setAcquisitionData] = useState(null);
  // Faye travel insurance sales (POST /api/admin/insurance-summary): every
  // policy with how it was bought; null until loaded.
  const [insurancePolicies, setInsurancePolicies] = useState(null);
  const [insuranceLoadState, setInsuranceLoadState] = useState('idle');
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month_to_date');
  const [currentUser, setCurrentUser] = useState(null);
  const [conversionTimelineVisible, setConversionTimelineVisible] = useState(10);
  const [customerRankings, setCustomerRankings] = useState({ byVolume: [], byTrips: [] });
  const [customerRankingView, setCustomerRankingView] = useState('volume'); // 'volume' or 'trips'
  const [isLoadingRankings, setIsLoadingRankings] = useState(false);
  const [cancellationAnalysis, setCancellationAnalysis] = useState(null);
  const [isLoadingCancellationAnalysis, setIsLoadingCancellationAnalysis] = useState(false);
  const [cancellationAnalysisError, setCancellationAnalysisError] = useState(null);
  const [aiProjection, setAiProjection] = useState(null);
  const [isLoadingAiProjection, setIsLoadingAiProjection] = useState(false);
  const [aiProjectionError, setAiProjectionError] = useState(null);
  const [financialBreakdownTab, setFinancialBreakdownTab] = useState('defera'); // 'defera' | 'slicepay'
  // What-if scenario controls for the "Projected Cash & Net Profit by Month"
  // table — let admins model (a) a flat-8% SF schedule for fares ≥$200 and
  // (b) layering hypothetical NEW DC bookings on top of the existing book so
  // they can see the cash-cycling impact (airfare outflows at booking vs
  // installment recovery) on the running balance.
  const [whatIfServiceFeeMode, setWhatIfServiceFeeMode] = useState('current'); // 'current' | 'flat8AboveTwoHundred'
  const [whatIfNewBookingsPerMonth, setWhatIfNewBookingsPerMonth] = useState(0);
  const [whatIfNewBookingsAvgBase, setWhatIfNewBookingsAvgBase] = useState(1335);
  // Map of bookingId -> actual amount paid (cents), unified across Stripe
  // AND NMI/Vector via /api/admin/bookings-paid-amounts (Defera Capital
  // NMI-track bookings resolve via nmi_installment_attempts +
  // defera_capital_nmi_pending_checkouts on the server). Bookings without any
  // processor subscription (or with no cache/live data yet) resolve to 0.
  const [bookingsPaidCents, setBookingsPaidCents] = useState({});
  // 'idle' before fetch starts, 'loading' while in flight, 'loaded' on success
  // (even if every booking returned 0), 'unavailable' if Stripe isn't configured,
  // 'error' on any other failure. Drives the Defera Capital breakdown's loading state
  // so it never gets stuck on "Loading…" forever.
  const [paidLoadState, setPaidLoadState] = useState('idle');
  // Telemetry from /api/admin/bookings-paid-amounts: how many bookings came back
  // from each tier (memory cache, DB cache fallback, live Stripe, failed). Used
  // to surface a subtle freshness indicator next to "Total Recovered" so admins
  // know when data is from a stale fallback vs a live Stripe fetch.
  const [paidMeta, setPaidMeta] = useState(null);
  // Map of bookingId -> { deposit_credit_cents, installment_credit_total_cents,
  // installment_credit_applied_cents } sourced from /api/admin/credit-redemptions-by-booking.
  // Powers the "Credit Redeemed" P&L Adjustment line — the symmetric counterpart
  // of "Profit from Cancellation Credit". Loaded async on top of bookings so the
  // rest of Analytics renders immediately; missing entries resolve to 0 (no
  // allocation on that booking).
  const [bookingsCreditRedeemedCents, setBookingsCreditRedeemedCents] = useState({});

  useEffect(() => {
    const loadData = async () => {
      try {
        const user = await User.me();
        setCurrentUser(user);

        // SECURITY: Check app_metadata.roles array for admin or investor
        // Fallback to user.role if app_metadata is not yet populated
        const roles = user?.app_metadata?.roles || [];
        const isAdmin = roles.includes('admin') || user?.role === 'admin';
        const isInvestor = roles.includes('investor') || user?.role === 'investor';
        
        if (!user || (!isAdmin && !isInvestor)) {
          setIsLoading(false);
          return;
        }

        // Get session for authenticated API calls
        const { supabase } = await import('../lib/supabase');
        const { data: { session } } = await supabase.auth.getSession();

        // Fetch all analytics data from server (using service role to bypass RLS)
        const fetchAnalyticsData = async () => {
          if (!session?.access_token) return { userCount: 0, abandonedCount: 0, referralSources: [], users: [], abandonedTrips: [], bookings: [] };
          
          const headers = {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          };

          const [analyticsDataRes, referralRes, bookingsRes, acquisitionRes] = await Promise.all([
            fetch('/api/admin/analytics-data', { method: 'POST', headers }),
            fetch('/api/admin/referral-sources', { method: 'POST', headers }),
            // Paged (2026-09-16): the single reply had outgrown Vercel's 4.5 MB
            // response cap. Resolves to the same { bookings } shape.
            import('../api/adminBookingsPager.js')
              .then(({ fetchAllAdminBookings }) => fetchAllAdminBookings(headers.Authorization.replace(/^Bearer /, '')))
              .then((r) => ({ ok: true, json: async () => ({ bookings: r.bookings }) }))
              .catch(() => ({ ok: false })),
            /* Acquisition & geography. Fetched alongside the rest rather than
               lazily on scroll: it shares the same auth round-trip, and a
               half-loaded Analytics page that fills in later reads as broken. */
            fetch('/api/admin/acquisition-geo', { method: 'POST', headers })
          ]);

          const analyticsData = analyticsDataRes.ok ? await analyticsDataRes.json() : { users: [], trips: [], userCount: 0, tripCount: 0 };
          const referral = referralRes.ok ? await referralRes.json() : { referralSources: [] };
          const bookingsData = bookingsRes.ok ? await bookingsRes.json() : { bookings: [] };
          const acquisition = acquisitionRes.ok ? await acquisitionRes.json() : null;

          return {
            userCount: analyticsData.userCount || 0,
            abandonedCount: analyticsData.tripCount || 0,
            referralSources: referral.referralSources || [],
            users: analyticsData.users || [],
            abandonedTrips: analyticsData.trips || [],
            bookings: bookingsData.bookings || [],
            acquisition
          };
        };

        // Fetch all data from server (bypasses RLS for production compatibility)
        const analyticsData = await fetchAnalyticsData();
        const flightData = analyticsData.bookings;

        console.log('📊 Analytics Data Loaded:', {
          flights: flightData?.length || 0,
          users: analyticsData.users?.length || 0,
          usersTotal: analyticsData.userCount,
          abandoned: analyticsData.abandonedTrips?.length || 0,
          abandonedTotal: analyticsData.abandonedCount,
          referralSources: analyticsData.referralSources?.length || 0
        });

        setFlights(flightData);
        setUsers(analyticsData.users || []);
        setAbandonedTrips(analyticsData.abandonedTrips || []);
        setUserCount(analyticsData.userCount);
        setAbandonedCount(analyticsData.abandonedCount);
        setReferralSourceData(analyticsData.referralSources);
        setAcquisitionData(analyticsData.acquisition || null);

        // Fetch actual customer-paid amounts per booking (unified across
        // Stripe AND NMI/Vector — the endpoint resolves NMI-track Defera
        // Capital bookings via nmi_installment_attempts + pending-checkouts).
        // Used by the Defera Capital financial breakdown for Total Recovered /
        // Outstanding / Hold Fees / Cancellation Refunds / Default Losses.
        // Done in the background so the rest of Analytics renders immediately;
        // the breakdown progressively fills in once the data arrives.
        // Travel insurance sales: small, independent, and never allowed to hold
        // up (or break) the rest of the page.
        if (session?.access_token) {
          (async () => {
            setInsuranceLoadState('loading');
            try {
              const resp = await fetch('/api/admin/insurance-summary', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                body: '{}',
              });
              if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
              const json = await resp.json();
              setInsurancePolicies(Array.isArray(json?.policies) ? json.policies : []);
              setInsuranceLoadState('loaded');
            } catch (err) {
              console.warn('insurance-summary failed:', err?.message);
              setInsuranceLoadState('error');
            }
          })();
        }

        if (Array.isArray(flightData) && flightData.length > 0 && session?.access_token) {
          (async () => {
            setPaidLoadState('loading');
            try {
              const ids = flightData.map(b => b.id).filter(Boolean);
              if (ids.length === 0) {
                setPaidLoadState('loaded');
                return;
              }
              const resp = await fetch('/api/admin/bookings-paid-amounts', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${session.access_token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ booking_ids: ids })
              });
              if (!resp.ok) {
                console.warn('bookings-paid-amounts endpoint returned error:', resp.status);
                setPaidLoadState('error');
                return;
              }
              const json = await resp.json();
              setBookingsPaidCents(json.paid || {});
              setPaidMeta(json.meta || null);
              setPaidLoadState(json.stripe_unavailable ? 'unavailable' : 'loaded');
            } catch (e) {
              console.warn('Failed to fetch actual paid amounts (Stripe + NMI):', e);
              setPaidLoadState('error');
            }
          })();

          // Fetch per-booking Defera Credit redemptions (committed-at-checkout).
          // Powers the "Credit Redeemed" P&L Adjustment line. Failures degrade
          // to an empty map — Analytics still renders without the offset.
          (async () => {
            try {
              const ids = flightData.map(b => b.id).filter(Boolean);
              if (ids.length === 0) return;
              const resp = await fetch('/api/admin/credit-redemptions-by-booking', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${session.access_token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ booking_ids: ids }),
              });
              if (!resp.ok) {
                console.warn('credit-redemptions-by-booking endpoint returned error:', resp.status);
                return;
              }
              const json = await resp.json();
              setBookingsCreditRedeemedCents(json.redemptions || {});
            } catch (e) {
              console.warn('Failed to fetch credit redemptions:', e);
            }
          })();
        } else {
          // No bookings to fetch payments for — keep UI from spinning indefinitely.
          setPaidLoadState('loaded');
        }

        // Load customer rankings
        if (customerRankings.byVolume.length === 0) {
          setIsLoadingRankings(true);
          try {
            const { supabase } = await import('../lib/supabase');
            const { data: { session } } = await supabase.auth.getSession();
            
            if (session?.access_token) {
              const response = await fetch('/api/admin/customer-rankings', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${session.access_token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (response.ok) {
                const data = await response.json();
                setCustomerRankings(data);
              } else {
                console.warn('Customer rankings endpoint returned error:', response.status);
              }
            }
          } catch (error) {
            console.error('Error fetching customer rankings:', error);
          } finally {
            setIsLoadingRankings(false);
          }
        }
      } catch (error) {
        console.error('Error loading analytics data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  if (currentUser) {
    const roles = currentUser.app_metadata?.roles || [];
    const isAdmin = roles.includes('admin') || currentUser.role === 'admin';
    const isInvestor = roles.includes('investor') || currentUser.role === 'investor';
    
    if (!isAdmin && !isInvestor) {
      return (
        <div className="min-h-[70vh] flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-light text-white mb-4">Access Denied</h2>
            <p className="text-white/60 font-normal">You need admin or investor privileges to view analytics.</p>
          </div>
        </div>
      );
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <Activity className="w-8 h-8 animate-spin text-white mx-auto mb-4" />
          <p className="text-white/60 font-normal">Loading analytics...</p>
        </div>
      </div>
    );
  }

  // Calculate date range
  const now = new Date();
  let startDate;
  let endDate = endOfDay(now);
  
  if (timeRange === 'all') {
    startDate = new Date('2020-01-01');
  } else if (timeRange === '0') {
    startDate = startOfDay(now);
  } else if (timeRange === 'yesterday') {
    const yesterday = subDays(now, 1);
    startDate = startOfDay(yesterday);
    endDate = endOfDay(yesterday);
  } else if (timeRange === 'this_week') {
    startDate = startOfWeek(now, { weekStartsOn: 1 }); // Monday
    endDate = endOfDay(now);
  } else if (timeRange === 'last_week') {
    const lastWeek = subDays(now, 7);
    startDate = startOfWeek(lastWeek, { weekStartsOn: 1 });
    endDate = endOfWeek(lastWeek, { weekStartsOn: 1 });
  } else if (timeRange === 'this_month') {
    startDate = startOfMonth(now);
    endDate = endOfMonth(now);
  } else if (timeRange === 'last_month') {
    const lastMonth = subMonths(now, 1);
    startDate = startOfMonth(lastMonth);
    endDate = endOfMonth(lastMonth);
  } else if (timeRange === 'this_year') {
    startDate = startOfYear(now);
    endDate = endOfYear(now);
  } else if (timeRange === 'last_year') {
    const lastYear = subYears(now, 1);
    startDate = startOfYear(lastYear);
    endDate = endOfYear(lastYear);
  } else if (timeRange === 'month_to_date') {
    startDate = startOfMonth(now);
    endDate = endOfDay(now);
  } else if (timeRange === 'year_to_date') {
    startDate = startOfYear(now);
    endDate = endOfDay(now);
  } else {
    // For numeric ranges (7, 30, 90, 365), calculate exact day count
    startDate = startOfDay(subDays(now, parseInt(timeRange) - 1));
    endDate = endOfDay(now);
  }

  // Faye travel insurance (migration 156): a premium rides inside a booking's
  // totals but is collected for Faye. Every booked-value, fee, base-fare,
  // margin and loss figure below runs on TRAVEL rows; cash metrics strip the
  // premium's share of the cash (travelCashCentsOf); processing fees keep it
  // (cashCentsOf / premiumDollarsOf). Rows without a premium are untouched.
  const insurance = buildInsuranceBasis(flights, bookingsPaidCents);
  const travelFlights = insurance.travelFlights;

  // Filter flights by date range
  const filteredFlights = travelFlights.filter((flight) => {
    const flightDate = parseISO(flight.created_at || flight.createdAt);
    return flightDate >= startDate && flightDate <= endDate;
  });

  const statusOf = (f) => f.status || f.trip_status || 'pending';
  // 'canceled' is accepted alongside 'cancelled' to match the server's
  // CANCELLED_STATUSES set — the DB only writes the British spelling today,
  // but a single-l row must never slip back into the revenue lines.
  const isCancelledOrRefunded = (f) => {
    const s = statusOf(f);
    return s === 'cancelled' || s === 'canceled' || s === 'refunded';
  };
  // COGS basis: cancelled/refunded trips don't add Defera-side cost (airline credits us back).
  // Defaulted: Defera still paid airline → cost is real; Slice Pay never gets hold fee back.
  const flightsExclCancelled = filteredFlights.filter(f => !isCancelledOrRefunded(f));
  const flightsExclCancelledAndDefaulted = filteredFlights.filter(f => !isCancelledOrRefunded(f) && statusOf(f) !== 'defaulted');
  // Revenue basis: ALL non-pending bookings (Confirmed + Cancelled + Refunded + Defaulted + Completed).
  // We show gross customer billing across the period and offset cancellations/defaults
  // with explicit Cancellation Refunds and Default Losses lines on the expense side.
  const flightsForFinancials = filteredFlights.filter(f => statusOf(f) !== 'pending');
  const nowForRecovery = new Date();

  // Defaulted trips split into two buckets (used by both Defera and Defera Capital tabs):
  //   - Real defaults: confirmation # → trip was booked, unrecovered cost is a real loss.
  //   - Phantom defaults: no confirmation # → trip was never booked, so customer payments
  //     are pure profit (loss prevented), not a loss.
  // Scoped to Defera Capital: only there does Defera front the cost, so only there
  // can a default land on our books. Slice Pay defaults are the processor's loss.
  const defaultedFlights = filteredFlights.filter(f => statusOf(f) === 'defaulted' && isDeferaCapital(f));
  const realDefaultedFlights = defaultedFlights.filter(hasConfirmationNumber);
  const phantomDefaultedFlights = defaultedFlights.filter(f => !hasConfirmationNumber(f));
  const phantomDefaultedCount = phantomDefaultedFlights.length;

  // Filter abandoned trips by date range
  const filteredAbandonedTrips = abandonedTrips.filter((trip) => {
    const tripDate = parseISO(trip.created_at || trip.createdAt);
    return tripDate >= startDate && tripDate <= endDate;
  });

  // Calculate metrics
  const totalTrips = filteredFlights.length;
  const confirmedTrips = filteredFlights.filter((f) => f.status === 'confirmed').length;
  const pendingTrips = filteredFlights.filter((f) => f.status === 'pending').length;
  const totalAbandonedCarts = filteredAbandonedTrips.length;
  const totalCarts = pendingTrips + totalAbandonedCarts;
  
  const conversionRate = (confirmedTrips + totalCarts) > 0 
    ? ((confirmedTrips / (confirmedTrips + totalCarts)) * 100).toFixed(1) 
    : 0;
  
  // Calculate user-based conversion rate (users who have booked / total users)
  // Only count confirmed/completed flights and filter out entries without valid user IDs
  const usersWhoBooked = new Set(
    filteredFlights
      .filter(f => (f.status === 'confirmed' || f.status === 'completed') && (f.user_id || f.userId))
      .map(f => f.user_id || f.userId)
  ).size;
  
  // Count users created within the selected date range (not all historical users)
  const totalUsersInRange = users.filter((user) => {
    try {
      const userDate = parseISO(user.created_at || user.createdAt);
      return userDate >= startDate && userDate <= endDate;
    } catch (e) {
      return false;
    }
  }).length;
  
  const userConversionRate = totalUsersInRange > 0 
    ? ((usersWhoBooked / totalUsersInRange) * 100).toFixed(1) 
    : 0;

  // Booked value of one trip, as billed to the customer.
  const bookedValueOf = (f) => Number(f.grand_total_with_hold || f.grandTotalWithHold || 0) || 0;

  // Headline revenue/volume EXCLUDES cancelled and refunded trips.
  //
  // These lines used to filter on date alone. That was defensible when the
  // P&L's Cancellation Refunds line was expected to offset them — but that
  // line only counts cash a customer actually paid AND got back, so a
  // Defera Zero booking (0% down) that cancels before its first payment
  // contributes its full billed value with nothing against it. One customer
  // booking and cancelling the same route daily was enough to put ~20% of a
  // month's reported revenue behind trips that billed $0.
  //
  // Gross and the cancelled slice are computed alongside so the headline
  // stays reconcilable (net + cancelled === gross) instead of just smaller.
  // `defaulted` is deliberately still counted: those customers kept paying,
  // and the Default Losses expense line already carries the other side.
  const monthToDateStart = startOfMonth(now);
  const monthToDateFlights = travelFlights.filter((f) => {
    const flightDate = parseISO(f.created_at || f.createdAt);
    return flightDate >= monthToDateStart && flightDate <= now;
  });
  const monthToDateRevenue = monthToDateFlights
    .filter((f) => !isCancelledOrRefunded(f))
    .reduce((sum, f) => sum + bookedValueOf(f), 0);

  // Selected time period (main display).
  const grossRevenue = filteredFlights.reduce((sum, f) => sum + bookedValueOf(f), 0);
  const cancelledFlights = filteredFlights.filter(isCancelledOrRefunded);
  const cancelledTrips = cancelledFlights.length;
  const cancelledRevenue = cancelledFlights.reduce((sum, f) => sum + bookedValueOf(f), 0);
  const totalRevenue = grossRevenue - cancelledRevenue;

  // COLLECTED CASH behind the headline.
  //
  // `totalRevenue` is BOOKED value — what the customer agreed to pay, counted
  // the moment the trip is created. On installment plans (and Defera Zero at
  // 0% down especially) the cash arrives over the following months, so a large
  // uncollected share is the product working as designed, NOT a shortfall.
  // It is surfaced only so "revenue" is never mistaken for "cash in".
  //
  // Sourced from the same unified Stripe + NMI feed the Defera Capital
  // breakdown uses. Rendered only when that feed actually loaded — a partial
  // or failed fetch would otherwise read as $0 collected, which is worse than
  // showing nothing.
  const collectedCash = filteredFlights
    .filter((f) => !isCancelledOrRefunded(f))
    // Travel cash, against travel booked value: the premium's share is Faye's.
    .reduce((sum, f) => sum + (insurance.travelCashCentsOf(f) / 100), 0);
  const collectedIsReady = paidLoadState === 'loaded';
  const collectedPct = totalRevenue > 0 ? (collectedCash / totalRevenue) * 100 : 0;

  // Financial Breakdown Calculations (from bookings ONLY) - Balance Sheet Style
  //
  // REVENUE side uses `flightsForFinancials` (all non-pending) so the period totals
  // reflect ALL booking activity. Cancelled and defaulted trips are then offset by
  // explicit Cancellation Refunds + Default Losses expense lines.
  // EXPENSE COGS items (base costs, investor fees, backer fees, Defera Capital fees)
  // stay on `flightsExclCancelled` because cancelled trips have no Defera-side cost
  // (airline credits us back).

  // === AIRFARE REVENUE (gross customer billing across all non-pending) ===
  const airfareBaseFares = flightsForFinancials.reduce((sum, flight) => {
    return sum + (parseFloat(flight.totalPrice || flight.total_price || flight.total_amount || 0));
  }, 0);
  const airfareServiceFees = flightsForFinancials.reduce((sum, flight) => {
    const stay = flight.stay_data || (flight.stays && flight.stays[0]);
    const isFlightHotel = !!(stay && (stay.totalAmount || stay.total_amount));
    return sum + (isFlightHotel ? 0 : (flight.service_fee || 0));
  }, 0);

  // Hold fees revenue: customer was billed across ALL non-pending bookings (cancelled
  // and defaulted included; refunds/losses captured as separate expense lines).
  // CRITICAL: flight.hold_fee is the SINGLE hold fee for the whole booking (flight+hotel).
  // Allocate proportionally to avoid double-counting.
  const { airfareHoldFees, staysHoldFees } = flightsForFinancials.reduce(
    (acc, flight) => {
      const fd = flight.flight_data || flight.flightData || {};
      const pm = fd._payment_method || fd.payment_method || flight.payment_method || 'installments';
      if (pm === 'reserve') return acc;

      const fullHoldFee = flight.hold_fee || 0;
      const flightBase = parseFloat(flight.totalPrice || flight.total_price || flight.total_amount || 0);
      const stay = flight.stay_data || (flight.stays && flight.stays[0]);
      const stayAmount = stay ? (stay.totalAmount || stay.total_amount || 0) : 0;
      const isFlightHotel = !!(stay && (stay.totalAmount || stay.total_amount));
      const serviceFee = isFlightHotel ? 0 : parseFloat(flight.service_fee || 0);
      const preHoldTotal = flightBase + stayAmount + serviceFee || 1;

      if (fullHoldFee > 0 && preHoldTotal > 0) {
        const airfareShare = (flightBase + serviceFee) / preHoldTotal;
        const staysShare = stayAmount / preHoldTotal;
        return {
          airfareHoldFees: acc.airfareHoldFees + fullHoldFee * airfareShare,
          staysHoldFees: acc.staysHoldFees + fullHoldFee * staysShare,
        };
      }
      return acc;
    },
    { airfareHoldFees: 0, staysHoldFees: 0 }
  );

  // === STAYS REVENUE (gross customer billing across all non-pending) ===
  const staysBaseCosts = flightsForFinancials.reduce((sum, flight) => {
    const stayAmount = flight.stay_data ? (flight.stay_data.totalAmount || flight.stay_data.total_amount || 0) : 0;
    return sum + stayAmount;
  }, 0);
  // Flight+hotel: service fee WAIVED - stays have 0 service fee
  const staysServiceFees = flightsForFinancials.reduce((sum, flight) => {
    const stay = flight.stay_data || (flight.stays && flight.stays[0]);
    const stayAmount = stay ? (stay.totalAmount || stay.total_amount || 0) : 0;
    if (stayAmount === 0) return sum;
    const isFlightHotel = !!stay;
    if (isFlightHotel) return sum;
    const flightBase = parseFloat(flight.totalPrice || flight.total_price || flight.total_amount || 0);
    const totalBase = flightBase + stayAmount;
    if (totalBase === 0) return sum;
    const serviceFeeRate = (flight.service_fee || 0) / (flightBase || 1);
    return sum + (stayAmount * serviceFeeRate);
  }, 0);

  // === SKIP PAYMENT FEE REVENUE ===
  // $20-per-skip fees collected via Skip Upcoming Payment (Defera Capital). Stored on the
  // booking ledger as `purpose: 'skip_payment_fee'` with the fee in `fee_cents` (`amount`
  // is 0 — the fee never counts toward the trip balance, so it's pure additive revenue on
  // top of the booking's grand total). Attributed to the booking's period (created_at),
  // consistent with every other revenue line on this page.
  const skipFeesRevenue = flightsForFinancials.reduce((sum, flight) => {
    const fd = flight.flight_data || flight.flightData || {};
    const oneTimes = Array.isArray(fd._one_time_payments_cents) ? fd._one_time_payments_cents : [];
    const feeCents = oneTimes.reduce(
      (s, p) => s + (p?.purpose === 'skip_payment_fee' ? (Number(p?.fee_cents) || 0) : 0),
      0,
    );
    return sum + feeCents / 100;
  }, 0);

  // === POST-BOOKING SEAT PURCHASE REVENUE ===
  // Seats bought after booking are charged on their own Stripe PI (5% fee on top
  // of the airline's price), entirely outside the installment plan — so no line
  // above sees them.
  //
  // Recognized NET of the airline's seat cost, not gross. The customer's payment
  // is overwhelmingly a pass-through Defera immediately owes the carrier; booking
  // the gross here while the matching cost sits in no expense line would inflate
  // margin by the seat price. Keeping it as one net term makes that asymmetry
  // structurally impossible rather than a thing to remember. Same collection as
  // every other revenue line so the basis can't diverge either.
  const seatPurchaseNetRevenue = flightsForFinancials.reduce(
    (sum, flight) => sum + seatPurchaseMarginContribution(flight.flight_data || flight.flightData || {}),
    0,
  );

  // === REVENUE TOTALS ===
  const airfareRevenueTotal = airfareBaseFares + airfareServiceFees + airfareHoldFees;
  const staysRevenueTotal = staysBaseCosts + staysServiceFees + staysHoldFees;
  const revenueGrandTotal = airfareRevenueTotal + staysRevenueTotal + skipFeesRevenue + seatPurchaseNetRevenue;

  // === EXPENSES (COGS basis: flightsExclCancelled — Defera incurs no cost on cancelled trips) ===
  // Airfare base costs: actual airline cost (uses _actual_cost override when set).
  const airfareBaseCosts = flightsExclCancelled.reduce((sum, flight) => sum + getBaseForCost(flight), 0);

  // Slice Pay Hold Fees expense (legacy installments era, pre-April 2026):
  // before Defera Capital launched, customer hold fees were passed through to
  // Slice Pay as their loan-origination fee — so they were Defera's expense,
  // not revenue. Defera Capital bookings keep the hold fee as revenue
  // (already in airfareHoldFees / staysHoldFees) so we only deduct for the
  // legacy `installments` payment method. Cancelled bookings are excluded to
  // match the existing COGS basis (assume Slice Pay refunded their fee on
  // cancellation).
  const isSlicePayEraBooking = (flight) => {
    const fd = flight.flight_data || flight.flightData || {};
    const pm = fd._payment_method || fd.payment_method || fd.booking_option || flight.payment_method || 'installments';
    return pm === 'installments' || pm === 'miles_installments';
  };
  const slicePayHoldFeeFlights = flightsExclCancelled.filter(isSlicePayEraBooking);
  const slicePayHoldFeesExpense = slicePayHoldFeeFlights.reduce((sum, flight) => {
    return sum + (parseFloat(flight.hold_fee) || 0);
  }, 0);
  const slicePayHoldFeesCount = slicePayHoldFeeFlights.length;

  // Investor Fees: 14% of service fees (excluding Reserve and Miles bookings — no investor for those).
  const airfareNonReserveServiceFees = flightsExclCancelled.reduce((sum, flight) => {
    const flightData = flight.flightData || flight.flight_data || {};
    const pm = flightData._payment_method || flightData.payment_method || flightData.booking_option || flight.payment_method || 'installments';
    if (pm === 'reserve' || pm === 'miles') return sum;
    const stay = flight.stay_data || (flight.stays && flight.stays[0]);
    const isFlightHotel = !!(stay && (stay.totalAmount || stay.total_amount));
    return sum + (isFlightHotel ? 0 : (flight.service_fee || 0));
  }, 0);
  const airfareInvestorFees = airfareNonReserveServiceFees * INVESTOR_FEE_RATE_ON_SERVICE_FEE;

  // Backer Fees: per-trip — Ravi Teja gets 15% of Base Fare on his trips; all other
  // backers get 25% of Service Fee. Trips with no backer contribute $0 → Defera keeps
  // the full Hold + Service Fee on those, only the standard Investor Fee + Defera
  // Capital Fee come out of revenue.
  /** Backer terms, read from each backer's own recorded deal rather than
   *  inferred from who they are. This used to match Ravi Teja by hardcoded
   *  email address to pick 15%-of-base over 25%-of-service-fee — right today,
   *  wrong the moment he changes address, changes rate, or a second
   *  base-fare-share backer exists. `users` now carries the terms (see the
   *  analytics-data payload in server.js). */
  const backerTermsDefera = backerTermsById(users);
  const backerTermsFor = (flight) =>
    backerTermsDefera.get(flight.backer_id || flight.backerId) || null;
  const isBaseFareBacker = (flight) => backerTermsFor(flight)?.model === BACKER_MODEL_BASE_FARE;
  const airfareBackerFees = flightsExclCancelled.reduce((sum, flight) => {
    const backerId = flight.backer_id || flight.backerId;
    if (!backerId) return sum;
    const terms = backerTermsFor(flight) || { model: BACKER_MODEL_SERVICE_FEE, rate: 0.25 };
    if (terms.model === BACKER_MODEL_BASE_FARE) {
      return sum + (getBaseForCost(flight) * terms.rate);
    }
    const stay = flight.stay_data || (flight.stays && flight.stays[0]);
    const isFlightHotel = !!(stay && (stay.totalAmount || stay.total_amount));
    const serviceFee = isFlightHotel ? 0 : parseFloat(flight.service_fee || 0);
    return sum + (serviceFee * terms.rate);
  }, 0);

  const airfareExpensesTotal = airfareBaseCosts + airfareInvestorFees + airfareBackerFees;

  // Stays expenses (same COGS basis).
  const staysServiceFeesForExpense = flightsExclCancelled.reduce((sum, flight) => {
    const stay = flight.stay_data || (flight.stays && flight.stays[0]);
    const stayAmount = stay ? (stay.totalAmount || stay.total_amount || 0) : 0;
    if (stayAmount === 0) return sum;
    const isFlightHotel = !!stay;
    if (isFlightHotel) return sum;
    const flightBase = parseFloat(flight.totalPrice || flight.total_price || flight.total_amount || 0);
    const totalBase = flightBase + stayAmount;
    if (totalBase === 0) return sum;
    const serviceFeeRate = (flight.service_fee || 0) / (flightBase || 1);
    return sum + (stayAmount * serviceFeeRate);
  }, 0);
  const staysInvestorFees = staysServiceFeesForExpense * INVESTOR_FEE_RATE_ON_SERVICE_FEE;
  const staysBackerFees = flightsExclCancelled.reduce((sum, flight) => {
    const backerId = flight.backer_id || flight.backerId;
    if (!backerId) return sum;
    const stay = flight.stay_data || (flight.stays && flight.stays[0]);
    const stayAmount = stay ? (stay.totalAmount || stay.total_amount || 0) : 0;
    if (stayAmount === 0) return sum;
    const isFlightHotel = !!stay;
    if (isFlightHotel) return sum;
    const terms = backerTermsFor(flight) || { model: BACKER_MODEL_SERVICE_FEE, rate: 0.25 };
    if (terms.model === BACKER_MODEL_BASE_FARE) {
      // Base-fare-share cut on the stay base portion (consistent with airfare side).
      return sum + (stayAmount * terms.rate);
    }
    const flightBase = parseFloat(flight.totalPrice || flight.total_price || flight.total_amount || 0);
    const serviceFeeRate = (flight.service_fee || 0) / (flightBase || 1);
    const stayServiceFee = stayAmount * serviceFeeRate;
    return sum + (stayServiceFee * terms.rate);
  }, 0);
  const staysBaseCostsForExpense = flightsExclCancelled.reduce((sum, flight) => {
    return sum + (flight.stay_data ? (flight.stay_data.totalAmount || flight.stay_data.total_amount || 0) : 0);
  }, 0);
  const staysExpensesTotal = staysBaseCostsForExpense + staysInvestorFees + staysBackerFees;
  
  // === MILES LIABILITY CALCULATION ===
  // Calculate total miles earned across all bookings (2 miles per $1), each booked at its trip value (1.25¢)
  // Use milesEarned from FlightBooking entity which already normalizes all field variations
  const milesEarnedTotal = flightsExclCancelled.reduce((sum, flight) => {
    const miles = flight.milesEarned || 0; // FlightBooking entity already handles all field variations
    return sum + (parseFloat(miles) || 0);
  }, 0);
  const milesLiabilityCost = (milesEarnedTotal * MILES_LIABILITY_CENTS_PER_MILE) / 100; // the most a Mile can cost Defera

  // Cancelled-trip volume (base + stay + service, no hold) — for Slice Pay view / mix only. Not an expense: revenue is already absent for cancelled bookings.
  const totalCancelledAmount = filteredFlights
    .filter(f => isCancelledOrRefunded(f))
    .reduce((sum, flight) => {
      const base = getBaseForCost(flight);
      const stayAmount = flight.stay_data ? (flight.stay_data.totalAmount || flight.stay_data.total_amount || 0) : 0;
      const stay = flight.stay_data || (flight.stays && flight.stays[0]);
      const isFlightHotel = !!(stay && (stay.totalAmount || stay.total_amount));
      const serviceFee = isFlightHotel ? 0 : parseFloat(flight.service_fee || 0);
      return sum + base + stayAmount + serviceFee;
    }, 0);
  
  // === PAYMENT PROCESSING FEES ===
  // Stripe: 2.9% + $0.30 per transaction + 1.5% instant transfer → 4.4% effective.
  //   Defera Capital / Zero (Stripe track): deposit + ~5 installments (~6 charges).
  //   Reserve: 2 Stripe transactions per booking (top-up + debit).
  // NMI (Vector Payments): Defera Capital bookings on the NMI processor track pay 3.95%
  //   of volume with no rolling reserve holds (unlike Stripe May–Jun 2026 reserve window).
  // Slice Pay-era / Miles: not through our processors — excluded.
  const STRIPE_PROCESSING_RATE = 0.044; // 2.9% Stripe + 1.5% instant transfer
  const NMI_PROCESSING_RATE = 0.0395;
  const STRIPE_FIXED_FEE = 0.30;
  const DC_DZ_AVG_TRANSACTIONS = 6;     // 1 deposit + ~5 installments
  // Processing is charged on every dollar the customer pays, a Faye premium
  // included, so these add the premium back to the travel total.
  const stripeProcessingFees = flightsExclCancelled.reduce((sum, flight) => {
    if (isNmiProcessor(flight)) return sum;
    const flightData = flight.flightData || flight.flight_data || {};
    const pm = flightData._payment_method || flightData.payment_method || flightData.booking_option || flight.payment_method || 'installments';
    const grandTotal = parseFloat(flight.grand_total_with_hold || flight.grandTotalWithHold || flight.grand_total || flight.grandTotal || flight.totalPrice || 0)
      + insurance.premiumDollarsOf(flight);
    if (grandTotal <= 0) return sum;

    if (pm === 'reserve') {
      return sum + (grandTotal * STRIPE_PROCESSING_RATE + STRIPE_FIXED_FEE) * 2;
    }
    if (pm === 'defera_capital' || pm === 'defera_zero') {
      return sum + grandTotal * STRIPE_PROCESSING_RATE + DC_DZ_AVG_TRANSACTIONS * STRIPE_FIXED_FEE;
    }
    return sum;
  }, 0);
  const nmiProcessingFees = flightsExclCancelled.reduce((sum, flight) => {
    if (!isNmiProcessor(flight)) return sum;
    const flightData = flight.flightData || flight.flight_data || {};
    const pm = flightData._payment_method || flightData.payment_method || flightData.booking_option || flight.payment_method || 'installments';
    if (pm !== 'defera_capital') return sum;
    const grandTotal = parseFloat(flight.grand_total_with_hold || flight.grandTotalWithHold || flight.grand_total || flight.grandTotal || flight.totalPrice || 0)
      + insurance.premiumDollarsOf(flight);
    if (grandTotal <= 0) return sum;
    return sum + grandTotal * NMI_PROCESSING_RATE;
  }, 0);
  const paymentProcessingFees = stripeProcessingFees + nmiProcessingFees;
  const stripeProcessingFeeBookings = flightsExclCancelled.filter(flight => {
    if (isNmiProcessor(flight)) return false;
    const flightData = flight.flightData || flight.flight_data || {};
    const pm = flightData._payment_method || flightData.payment_method || flightData.booking_option || flight.payment_method || 'installments';
    return pm === 'reserve' || pm === 'defera_capital' || pm === 'defera_zero';
  }).length;
  const nmiProcessingFeeBookings = flightsExclCancelled.filter(flight => {
    if (!isNmiProcessor(flight)) return false;
    const flightData = flight.flightData || flight.flight_data || {};
    const pm = flightData._payment_method || flightData.payment_method || flightData.booking_option || flight.payment_method || 'installments';
    return pm === 'defera_capital';
  }).length;

  // === AFTER-THE-FACT EXPENSE ADJUSTMENTS ===
  // Default Losses: customer billing on real defaulted trips minus what we
  // ACTUALLY recovered via Stripe/NMI. Writes off the gross billed revenue
  // we counted (base + stay + service fee + hold fee) but won't actually
  // collect. Uses `bookingsPaidCents` (unified Stripe + NMI cash figure) once
  // the async paid-amounts fetch has finished — that's an exact number,
  // instead of a "deposit + 1/4 installments" heuristic. Before the fetch
  // resolves (empty `bookingsPaidCents`), fall back to the same schedule
  // estimate we use for the DC tab so `expensesGrandTotal` / Net Profit stay
  // stable during the initial load instead of flashing a phantom "everything
  // was lost" figure.
  const paidReadyForAdjustments =
    paidLoadState !== 'idle'
    && paidLoadState !== 'loading'
    && paidLoadState !== 'unavailable'
    && paidLoadState !== 'error';
  const defaultLossesExpense = realDefaultedFlights.reduce((sum, flight) => {
    const base = getBaseForCost(flight);
    const stayAmount = flight.stay_data ? (flight.stay_data.totalAmount || flight.stay_data.total_amount || 0) : 0;
    const stay = flight.stay_data || (flight.stays && flight.stays[0]);
    const isFlightHotel = !!(stay && (stay.totalAmount || stay.total_amount));
    const serviceFee = isFlightHotel ? 0 : parseFloat(flight.service_fee || 0);
    const holdFee = parseFloat(flight.hold_fee || 0);
    const billed = base + stayAmount + serviceFee + holdFee;
    // Travel cash recovered: a Faye premium's share is never a recovery.
    const recovered = paidReadyForAdjustments
      ? (insurance.travelCashCentsOf(flight) / 100)
      : calculateRecoveredFromBooking(flight, nowForRecovery);
    return sum + Math.max(0, billed - recovered);
  }, 0);

  // Manual admin refund override — set on a booking's `flight_data._amount_refunded`
  // (dollars) from the Edit Flight Details modal when an admin issues a cash
  // refund that doesn't match the processor-inferred estimate below (e.g. a
  // partial refund on a cancelled trip). When present it REPLACES the inferred
  // figure for that booking so the trip's recognized profit is exact rather
  // than guessed.
  const getManualRefundAmount = (flight) => {
    const fd = flight.flight_data || flight.flightData || {};
    return parseFloat(fd._amount_refunded ?? 0) || 0;
  };

  // Cancellation Refunds: only what customers actually paid AND got back as CASH
  // on cancelled/refunded trips (sourced from Stripe AND NMI/Vector via
  // bookingsPaidCents — both processors are unified in the paid-amounts feed).
  // Customers who paid $0 before cancelling don't generate a refund. We subtract
  // any `_cancelled_for_credit_cents` set by the three credit-cancel endpoints
  // because that slice never left Defera as cash — it was converted to Defera
  // Credit and is surfaced under P&L Adjustments → Profit from Cancellation
  // Credit. If paid data isn't loaded yet (`bookingsPaidCents` empty) the
  // Math.max clamp keeps this line at $0 rather than going negative.
  const cancellationRefundsExpense = filteredFlights
    .filter(isCancelledOrRefunded)
    .reduce((sum, flight) => {
      const manualRefund = getManualRefundAmount(flight);
      if (manualRefund > 0) return sum + manualRefund;
      // Travel cash only: a Faye premium refunded on a cancelled trip never
      // counted as revenue, so giving it back is not an expense either.
      const cents = insurance.travelCashCentsOf(flight);
      const fd = flight.flight_data || flight.flightData || {};
      const creditCents = Number(fd._cancelled_for_credit_cents) || 0;
      /** An Extension Fee (moving a payment) is kept on a cancel: neither credit nor refund. */
      const feeKeptCents = Math.min(retainedExtensionFeesCents(fd), Math.max(0, cents - creditCents));
      const refundedCents = Math.max(0, cents - creditCents - feeKeptCents);
      return sum + refundedCents / 100;
    }, 0);

  // Manual refunds recorded on trips OUTSIDE the cancelled/refunded cohort
  // (e.g. a goodwill refund on an otherwise active or completed trip) aren't
  // covered by the Stripe-derived calculation above, so they're tracked here
  // as a standalone expense line. Same non-pending basis as revenue
  // (flightsForFinancials) so an expense can't appear without its
  // corresponding revenue.
  const manualRefundsOutsideCancellation = flightsForFinancials
    .filter((f) => !isCancelledOrRefunded(f))
    .reduce((sum, flight) => sum + getManualRefundAmount(flight), 0);

  // Credit Redeemed: revenue offset for the slice of gross booking revenue
  // that wasn't new cash but a previously-issued Defera Credit being burned
  // down. Sourced from the async `bookingsCreditRedeemedCents` map (committed
  // amount per allocation, non-reversed). Pre-load (empty map) → 0 → no
  // distortion. The symmetric "+ Profit from Cancellation Credit" on the
  // issue side and this offset on the redemption side net to $0 over a full
  // credit lifetime, leaving only the new trip's true margin (service + hold
  // − processing/investor/backer) as recognised profit. Inlined here so
  // expensesGrandTotal stays self-contained; the equivalent cohort lookup is
  // exposed as `creditRedeemedAmount` / `dcCreditRedeemedAmount` further down
  // for the P&L Adjustments display rows.
  const creditRedeemedExpense = flightsForFinancials.reduce((sum, flight) => {
    const r = bookingsCreditRedeemedCents[flight.id];
    if (!r) return sum;
    const cents = (Number(r.deposit_credit_cents) || 0) + (Number(r.installment_credit_total_cents) || 0);
    return sum + cents / 100;
  }, 0);

  const expensesGrandTotal =
    airfareExpensesTotal + staysExpensesTotal + milesLiabilityCost + paymentProcessingFees
    + defaultLossesExpense + cancellationRefundsExpense + manualRefundsOutsideCancellation
    + slicePayHoldFeesExpense + creditRedeemedExpense;

  // === NET PROFIT CALCULATION ===
  const netProfit = revenueGrandTotal - expensesGrandTotal;

  // === FUTURE NET PROFIT BY MONTH ===
  // Projects the slice of accrued Net Profit that gets realized in each upcoming
  // month, based on the scheduled installments of currently-active Defera Capital
  // and Defera Zero bookings. For each booking we compute its end-to-end Net Profit
  // contribution using the SAME formula as the headline Net Profit row:
  //   bookingNet = revenue − airfare/stay cost − investor − backer − Stripe − miles
  // and then allocate it to months in proportion to the installment amount due that
  // month: (installment / grandTotal) × bookingNet. Summing across every installment
  // for a booking (past + future) ≈ that booking's full Net contribution; summing
  // only the *future* months gives the portion of the headline Net Profit that
  // hasn't been realized yet.
  //
  // Past-due installments roll into the current month so they don't get hidden.
  // Slice Pay-era / Miles / Reserve bookings collected from Defera at booking time
  // (no future installments) — their Net contribution is already realized and is
  // not part of this forward-looking view. Cancelled/completed trips are excluded.
  // What-if scenario builder. Defaults reproduce the existing-book projection.
  // Pass `serviceFeeMode: 'flat8AboveTwoHundred'` to retroactively model the
  // existing book at a flat 8% SF on base fares >= $200 (≥$200 stays use the
  // same rate; bookings below $200 keep their current rate). Pass
  // `newBookingsPerMonth > 0` with `newBookingsAvgBase` to layer hypothetical
  // forward-looking bookings on top of the existing book — this models the
  // cash drain from fronting airline tickets vs the installment recovery.
  const buildFutureProjection = (opts = {}) => {
    const {
      serviceFeeMode = 'current',          // 'current' | 'flat8AboveTwoHundred'
      newBookingsPerMonth = 0,             // hypothetical NEW bookings to layer in
      newBookingsAvgBase = 0,              // avg base fare per hypothetical booking
      newBookingsLookaheadMonths = 12,     // how many future cohorts to spawn
      newBookingsLeadDaysToDeparture = 60, // assumed time from booking to departure
    } = opts;

    const map = new Map();
    const todayDate = startOfDay(nowForRecovery);

    // === Stripe rolling reserve (effective May 14 – Jun 28, 2026) ===
    // 25% of each transaction settled within the window is held for 45 days
    // before disbursement to Defera. Transactions outside the window settle
    // normally. The reserve shifts CASH and (proportionally) NET timing —
    // it does not change totals, only when the money lands.
    const STRIPE_RESERVE_START = startOfDay(new Date(2026, 4, 14));
    const STRIPE_RESERVE_END = endOfDay(new Date(2026, 5, 28));
    const STRIPE_RESERVE_PCT = 0.25;
    const STRIPE_RESERVE_DAYS = 45;

    // Defera books unconfirmed trips roughly a month before departure (a July
    // departure gets booked late June, etc.). Adjust here if the lead time shifts.
    const UNBOOKED_AIRFARE_DAYS_BEFORE_DEPARTURE = 30;

    const addToMonth = (date, cashDelta, netDelta) => {
      const bucketDate = date < todayDate ? todayDate : date;
      const monthKey = format(bucketDate, 'yyyy-MM');
      const prev = map.get(monthKey) || { cash: 0, net: 0 };
      map.set(monthKey, { cash: prev.cash + cashDelta, net: prev.net + netDelta });
    };

    // Splits a Stripe-settled transaction 75/25 if it lands in the reserve window:
    // 75% disburses on settlement date, 25% releases 45 days later.
    // NMI settlements skip reserve treatment entirely.
    const applyStripeTransaction = (txDate, cashAmount, netAmount) => {
      const inReserveWindow = txDate >= STRIPE_RESERVE_START && txDate <= STRIPE_RESERVE_END;
      if (!inReserveWindow) {
        addToMonth(txDate, cashAmount, netAmount);
        return;
      }
      const immediate = 1 - STRIPE_RESERVE_PCT;
      addToMonth(txDate, cashAmount * immediate, netAmount * immediate);
      addToMonth(addDays(txDate, STRIPE_RESERVE_DAYS), cashAmount * STRIPE_RESERVE_PCT, netAmount * STRIPE_RESERVE_PCT);
    };
    const applyPaymentSettlement = (txDate, cashAmount, netAmount, usesNmi) => {
      if (usesNmi) {
        addToMonth(txDate, cashAmount, netAmount);
      } else {
        applyStripeTransaction(txDate, cashAmount, netAmount);
      }
    };

    // === EXISTING BOOKINGS ===
    flightsExclCancelled.forEach((flight) => {
      const fd = flight.flight_data || flight.flightData || {};
      const pm = fd._payment_method || fd.payment_method || fd.booking_option || flight.payment_method || 'installments';
      if (pm !== 'defera_capital' && pm !== 'defera_zero') return;
      const status = flight.status || flight.trip_status;
      if (status === 'completed' || isCancelledOrRefunded(flight)) return;

      const grandTotalWithHold = parseFloat(
        flight.grand_total_with_hold || flight.grandTotalWithHold ||
        flight.grand_total || flight.grandTotal || flight.totalPrice || 0
      );
      if (grandTotalWithHold <= 0) return;

      // --- Per-booking financials (mirrors Defera-tab aggregate Net Profit math) ---
      const stay = flight.stay_data || (flight.stays && flight.stays[0]);
      const isFlightHotel = !!(stay && (stay.totalAmount || stay.total_amount));
      const stayAmount = stay ? (stay.totalAmount || stay.total_amount || 0) : 0;
      const flightBase = parseFloat(flight.totalPrice || flight.total_price || flight.total_amount || 0);
      const isDC = pm === 'defera_capital';
      const holdFeeRate = isDC ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE;

      // Service fee + hold fee handling. For the default 'current' scenario we
      // preserve the stored values exactly (so the projection is byte-identical
      // to the pre-refactor logic). For the 'flat8AboveTwoHundred' scenario we
      // recompute SF (8% on base ≥ $200), the hold fee derived from the new
      // SF, and proportionally scale future installments to model "what the
      // customer would have paid if the book had been priced at 8%."
      const origServiceFee = isFlightHotel ? 0 : parseFloat(flight.service_fee || 0);
      const origSFRate = origServiceFee / (flightBase || 1);

      let serviceFee = origServiceFee;
      let stayServiceFee = (stayAmount > 0 && !isFlightHotel) ? stayAmount * origSFRate : 0;
      let holdFee = parseFloat(flight.hold_fee || 0);
      let stayHoldFee = (stayAmount > 0 && !isFlightHotel)
        ? stayAmount * holdFeeRate
        : 0;
      let adjGrandTotalWithHold = grandTotalWithHold;
      let installmentScale = 1;

      if (serviceFeeMode === 'flat8AboveTwoHundred') {
        if (!isFlightHotel) {
          if (flightBase >= 200) serviceFee = flightBase * 0.08;
          if (stayAmount >= 200) stayServiceFee = stayAmount * 0.08;
        }
        // Hold fee follows SF (it's a % of pre-hold which includes SF).
        holdFee = (flightBase + serviceFee) * holdFeeRate;
        stayHoldFee = (stayAmount > 0 && !isFlightHotel) ? stayAmount * holdFeeRate : 0;
        adjGrandTotalWithHold = flightBase + serviceFee + holdFee + stayAmount + stayServiceFee + stayHoldFee;
        installmentScale = grandTotalWithHold > 0 ? adjGrandTotalWithHold / grandTotalWithHold : 1;
      }

      const revenueB = flightBase + serviceFee + holdFee + stayAmount + stayServiceFee + stayHoldFee;
      const airfareCostB = getBaseForCost(flight) + stayAmount;
      const investorB = (serviceFee + stayServiceFee) * INVESTOR_FEE_RATE_ON_SERVICE_FEE;
      let backerB = 0;
      const backerId = flight.backer_id || flight.backerId;
      if (backerId) {
        const terms = backerTermsFor(flight) || { model: BACKER_MODEL_SERVICE_FEE, rate: 0.25 };
        backerB = terms.model === BACKER_MODEL_BASE_FARE
          ? (getBaseForCost(flight) + stayAmount) * terms.rate
          : (serviceFee + stayServiceFee) * terms.rate;
      }
      const isNmi = isNmiProcessor(flight);
      const grandForStripe = serviceFeeMode === 'flat8AboveTwoHundred' ? adjGrandTotalWithHold : grandTotalWithHold;
      // Faye premium: never revenue or profit here, but the customer pays it
      // on the installments (cash below) and processing is charged on it.
      const premiumB = insurance.premiumDollarsOf(flight);
      const processingB = isNmi
        ? (grandForStripe + premiumB) * NMI_PROCESSING_RATE
        : (grandForStripe + premiumB) * STRIPE_PROCESSING_RATE + DC_DZ_AVG_TRANSACTIONS * STRIPE_FIXED_FEE;
      const milesB = ((parseFloat(flight.milesEarned) || 0) * MILES_LIABILITY_CENTS_PER_MILE) / 100;

      // Airfare-timing handling: trips with a confirmation number on file have
      // already had the airline ticket purchased (cash already out of Defera's
      // account), so the airfare cost is sunk and is correctly prorated across
      // installments. Trips WITHOUT a confirmation number haven't been booked
      // yet — Defera will pay the airline closer to departure, so we exclude
      // airfare from the prorated per-installment net and instead drop the full
      // base-fare hit into the departure month (or this month if departure is
      // already in the past).
      const hasConf = hasConfirmationNumber(flight);
      const perInstallmentNet = hasConf
        ? revenueB - airfareCostB - investorB - backerB - processingB - milesB
        : revenueB - investorB - backerB - processingB - milesB;

      // --- Payment schedule ---
      const bookingDate = flight.created_at || flight.createdAt;
      const departureDate =
        flight.departure_date || flight.departureDate ||
        fd?.departure_date || fd?.departureDate ||
        fd?.slices?.[0]?.segments?.[0]?.departing_at || null;
      if (!bookingDate || !departureDate) return;

      const ps = fd.payment_schedule || {};
      const isDZ = pm === 'defera_zero';
      const preHoldTotal = grandTotalWithHold / (1 + holdFeeRate);
      const depositPct = ps.deposit_percentage ?? ps.depositPercentage ?? (isDZ ? 0 : 20);
      const paymentPlan = ps.payment_plan ?? ps.paymentPlan ?? 'weekly';
      const lastPaymentDays = isDZ
        ? DEFERA_ZERO_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE
        : DEFERA_CAPITAL_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE;

      let computed;
      try {
        computed = calculatePaymentSchedule({
          bookingDate: typeof bookingDate === 'string'
            ? bookingDate.split('T')[0]
            : format(parseISO(bookingDate), 'yyyy-MM-dd'),
          departureDate: typeof departureDate === 'string'
            ? departureDate.split('T')[0]
            : format(parseISO(departureDate), 'yyyy-MM-dd'),
          totalWithFee: preHoldTotal,
          depositPercentage: depositPct,
          customDepositAmount: ps.custom_deposit_amount ?? ps.customDepositAmount ?? null,
          daysBeforeDepartureForLastPayment: lastPaymentDays,
          deferaCapitalDepositTiers: isDC,
          holdFeeRate,
          respectStoredDepositPercentage: Number(ps.deposit_amount_cents) > 0,
        });
      } catch (_e) {
        return;
      }
      if (!computed?.available) return;

      const plan = paymentPlan === 'biweekly' ? computed.biweeklyPlan : computed.weeklyPlan;
      if (!plan?.schedule || plan.schedule.length === 0) return;

      // Scale installments under the scenario (e.g., at 8% the customer would
      // have paid ~1.8% less per installment because the SF and hold portions
      // are smaller).
      const installmentAmount = (plan.paymentAmount || 0) * installmentScale;
      if (installmentAmount <= 0) return;

      const installmentNetContribution = adjGrandTotalWithHold > 0
        ? (installmentAmount / adjGrandTotalWithHold) * perInstallmentNet
        : 0;
      // The premium rides evenly on the installments after the deposit: cash in,
      // passed on to Faye, so it never touches the net contribution.
      const premiumPerInstallment = premiumB > 0 ? premiumB / plan.schedule.length : 0;

      plan.schedule.forEach((dateStr) => {
        const d = parseISO(dateStr);
        const settlementDate = d < todayDate ? todayDate : d;
        applyPaymentSettlement(settlementDate, installmentAmount + premiumPerInstallment, installmentNetContribution, isNmi);
      });

      // Deposit reserve release: Stripe only — NMI has no rolling reserve holds.
      // If this booking was created during the Stripe reserve window, 25% of the
      // deposit was held by Stripe and will release 45 days later. The 75% that
      // landed immediately is already in Defera's past cash (and not part of the
      // forward projection), but the held 25% is future cash and counts here.
      let bookingDateObj = null;
      try {
        const rawBooking = typeof bookingDate === 'string'
          ? bookingDate.split('T')[0]
          : format(bookingDate, 'yyyy-MM-dd');
        bookingDateObj = parseISO(rawBooking);
      } catch (_e) {
        bookingDateObj = null;
      }
      if (
        !isNmi &&
        bookingDateObj &&
        bookingDateObj >= STRIPE_RESERVE_START &&
        bookingDateObj <= STRIPE_RESERVE_END
      ) {
        const depositAmount = (computed.depositAmount || 0) * installmentScale;
        if (depositAmount > 0 && adjGrandTotalWithHold > 0) {
          const reservedCash = depositAmount * STRIPE_RESERVE_PCT;
          const reservedNet = (depositAmount / adjGrandTotalWithHold) * perInstallmentNet * STRIPE_RESERVE_PCT;
          addToMonth(addDays(bookingDateObj, STRIPE_RESERVE_DAYS), reservedCash, reservedNet);
        }
      }

      // Unconfirmed bookings: Defera typically books the airline ticket ~30 days
      // before departure (a July departure gets booked in late June, a September
      // departure in early-to-mid August, etc.), so that's when the airfare cash
      // actually leaves Defera's account. We shift the airfare hit accordingly
      // (with a floor at today, in case the lead time pushes it into the past).
      // Airfare outflow is an ACH/wire to the airline, not a Stripe transaction,
      // so no reserve treatment.
      if (!hasConf && airfareCostB > 0) {
        let payoutDate;
        try {
          const rawDep = typeof departureDate === 'string'
            ? departureDate.split('T')[0]
            : format(departureDate, 'yyyy-MM-dd');
          payoutDate = subDays(parseISO(rawDep), UNBOOKED_AIRFARE_DAYS_BEFORE_DEPARTURE);
        } catch (_e) {
          payoutDate = null;
        }
        if (payoutDate) {
          addToMonth(payoutDate, 0, -airfareCostB);
        }
      }
    });

    // === HYPOTHETICAL NEW BOOKINGS ===
    // Layered on top of the existing book to model "if I keep booking at this
    // rate, can my cash cycle through?" Each cohort represents one calendar
    // month of new DC bookings. Airfare hits cash AND net at the cohort date
    // (Defera pays the airline upfront), so the running balance drops
    // immediately and then recovers as installments come in. Sum of cash =
    // installment_sum − airfare_outlay = SF + hold + net profit. Sum of net =
    // SF + hold − investor − backer − stripe − miles (per booking × cohort).
    if (newBookingsPerMonth > 0 && newBookingsAvgBase > 0) {
      const base = newBookingsAvgBase;
      const sfRate = serviceFeeMode === 'flat8AboveTwoHundred'
        ? (base >= 200 ? 0.08 : 0.40)
        : (base < 200 ? 0.40 : base < 700 ? 0.10 : 0.08);
      const sfPer = base * sfRate;
      const preHoldPer = base + sfPer;
      const holdPer = preHoldPer * DEFERA_CAPITAL_HOLD_FEE_RATE;
      const grandPer = preHoldPer + holdPer;
      const investorPer = sfPer * INVESTOR_FEE_RATE_ON_SERVICE_FEE;
      const stripePer = grandPer * STRIPE_PROCESSING_RATE + DC_DZ_AVG_TRANSACTIONS * STRIPE_FIXED_FEE;
      // Per-payment NET contribution (deposit + installments). Each customer
      // payment contributes its share of grand_total − investor − stripe to
      // net. We track airfare as a separate −base cash/net hit at the cohort
      // date below, so summing installments + deposit and SUBTRACTING the
      // cohort airfare hit yields the booking's true Net Profit:
      //   (grand − investor − stripe) − base = sf + hold − investor − stripe.
      const installmentDollarNetRate = grandPer > 0
        ? (grandPer - investorPer - stripePer) / grandPer
        : 0;

      for (let monthOffset = 0; monthOffset < newBookingsLookaheadMonths; monthOffset++) {
        const cohortDate = addDays(todayDate, monthOffset * 30);
        const departureDate = addDays(cohortDate, newBookingsLeadDaysToDeparture);

        // Airfare cash AND net drop at cohort time — Defera writes a wire to
        // the airline. This is the actual "cash cycling" mechanic: the running
        // balance dips here and (hopefully) recovers via installments.
        addToMonth(cohortDate, -base * newBookingsPerMonth, -base * newBookingsPerMonth);

        let cohortPlan;
        try {
          // Track the lowest currently-offered DC tier for the assumed lead time so this
          // projection automatically stays in sync with `getDeferaCapitalDepositOptionsForLeadDays`
          // when bands shift (e.g. 2026-06-16: 5% became available at 90+ day lead).
          const cohortDepositPct = getDefaultInstallmentDepositPercentForLeadDays(
            newBookingsLeadDaysToDeparture,
            { deferaCapital: true },
          );
          cohortPlan = calculatePaymentSchedule({
            bookingDate: format(cohortDate, 'yyyy-MM-dd'),
            departureDate: format(departureDate, 'yyyy-MM-dd'),
            totalWithFee: preHoldPer,
            depositPercentage: cohortDepositPct,
            daysBeforeDepartureForLastPayment: DEFERA_CAPITAL_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE,
            deferaCapitalDepositTiers: true,
            holdFeeRate: DEFERA_CAPITAL_HOLD_FEE_RATE,
          });
        } catch (_e) {
          continue;
        }
        if (!cohortPlan?.available) continue;
        const plan = cohortPlan.weeklyPlan;
        if (!plan?.schedule || plan.schedule.length === 0) continue;

        // Deposit hits at cohort date (via Stripe, so reserve treatment applies
        // if cohort lands in the reserve window).
        const depositAmount = (cohortPlan.depositAmount || 0) * newBookingsPerMonth;
        if (depositAmount > 0) {
          const depositNet = depositAmount * installmentDollarNetRate;
          applyStripeTransaction(cohortDate, depositAmount, depositNet);
        }

        // Recurring installments
        const installmentAmount = (plan.paymentAmount || 0) * newBookingsPerMonth;
        if (installmentAmount <= 0) continue;
        const installmentNet = installmentAmount * installmentDollarNetRate;
        plan.schedule.forEach((dateStr) => {
          const d = parseISO(dateStr);
          const settlementDate = d < todayDate ? todayDate : d;
          applyStripeTransaction(settlementDate, installmentAmount, installmentNet);
        });
      }
    }

    const months = Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => ({ monthKey: key, cash: value.cash, net: value.net }));

    let running = 0;
    let runningMin = 0;
    let runningMinMonth = null;
    for (const m of months) {
      running += m.net;
      if (running < runningMin) {
        runningMin = running;
        runningMinMonth = m.monthKey;
      }
    }
    const cashTotal = months.reduce((s, m) => s + m.cash, 0);
    const netTotal = months.reduce((s, m) => s + m.net, 0);
    return { months, cashTotal, netTotal, runningEnd: running, runningMin, runningMinMonth };
  };

  const currentProjection = buildFutureProjection();
  const whatIfProjection = buildFutureProjection({
    serviceFeeMode: whatIfServiceFeeMode,
    newBookingsPerMonth: whatIfNewBookingsPerMonth,
    newBookingsAvgBase: whatIfNewBookingsAvgBase,
  });
  const isWhatIfActive =
    whatIfServiceFeeMode !== 'current' || whatIfNewBookingsPerMonth > 0;
  const activeProjection = isWhatIfActive ? whatIfProjection : currentProjection;
  const futureFreeCashByMonth = activeProjection.months;
  const futureFreeCashTotal = activeProjection.netTotal;
  const futureCashInflowTotal = activeProjection.cashTotal;

  // === DEFERA CAPITAL FINANCIAL BREAKDOWN ===
  // Uses ACTUAL customer payments (via /api/admin/bookings-paid-amounts →
  // bookingsPaidCents, which unifies both Stripe-routed and NMI/Vector-routed
  // Defera Capital bookings on the server), not schedule estimates, for the
  // headline metrics:
  //   - Total Backed: CAPITAL ACTUALLY DEPLOYED — airline base + hotel stay
  //     across trips with a confirmation number on file. Service fee and hold
  //     fee are Defera's REVENUE owed by the customer, not capital Defera
  //     pays out to the airline/hotel, so they're excluded here.
  //   - Total Recovered: capital repaid so far — customer payments applied to
  //     the base+stay slice of each booking, capped at that booking's capital
  //     so it can't exceed Total Backed.
  //   - Outstanding: Total Backed − Total Recovered (capital still owed).
  //   - Hold / Investor / Backer / processing fees: scale with dcTotalCollected
  //     (raw cash collected across Stripe + NMI), not capital recovery, because
  //     a fee dollar collected is a real-cash dollar whether or not the
  //     customer has paid down their capital yet.
  //   - Total Defaulted: unrecovered capital on real (booked) defaults,
  //     denominated on the same capital basis as Total Backed.
  const totalCancelledCount = filteredFlights.filter(f => isCancelledOrRefunded(f)).length;
  const slicePayCancellationRate = filteredFlights.length > 0 ? (totalCancelledCount / filteredFlights.length) * 100 : 0;

  // Helper: recovered cash for a single flight. Prefers actual customer
  // payments from `bookingsPaidCents` (unified Stripe + NMI feed) once the
  // async fetch has resolved; falls back to the schedule-based estimate
  // (deposit + 1/4 installments for cancelled/defaulted, computed schedule
  // for active) during the initial load so downstream aggregates stay
  // stable and don't jump when paid data lands.
  const paidReadyForRecovery =
    paidLoadState !== 'idle'
    && paidLoadState !== 'loading'
    && paidLoadState !== 'unavailable'
    && paidLoadState !== 'error';
  // Travel cash: a Faye premium's share of what was paid is not a recovery.
  const getFlightRecovered = (flight) => (
    paidReadyForRecovery
      ? (insurance.travelCashCentsOf(flight) / 100)
      : calculateRecoveredFromBooking(flight, nowForRecovery)
  );

  // Total Defaulted = unrecovered CAPITAL on real (booked) defaults. Uses
  // actual customer payments (Stripe + NMI, via bookingsPaidCents) once
  // available so the number reflects what really came in through the
  // processors. Capital basis (base + stay) matches Total Backed, and
  // Math.min caps recovered at capital so a customer who happens to have
  // paid past their capital doesn't push the line into a phantom recovery.
  const totalDefaultedAmount = realDefaultedFlights.reduce((sum, flight) => {
    const base = getBaseForCost(flight);
    const stayAmount = flight.stay_data ? (flight.stay_data.totalAmount || flight.stay_data.total_amount || 0) : 0;
    const capital = base + stayAmount;
    const recovered = getFlightRecovered(flight);
    const recoveredCapital = Math.min(recovered, capital);
    return sum + Math.max(0, capital - recoveredCapital);
  }, 0);

  // Profit from (prevented) loss on phantom defaults — customer payments
  // (Stripe + NMI) collected on defaulted trips that were never actually
  // booked with the airline/hotel.
  const lossPreventedAmount = phantomDefaultedFlights.reduce(
    (sum, flight) => sum + Math.max(0, getFlightRecovered(flight)),
    0,
  );

  // Reference-only metric: the loss Defera WOULD have eaten on phantom defaulted
  // trips if they had actually been booked with the airline (Base Fare − amount
  // recovered from customer). Pure informational, never used in any calculation.
  const lossPreventedReference = phantomDefaultedFlights.reduce((sum, flight) => {
    const base = getBaseForCost(flight);
    return sum + Math.max(0, base - getFlightRecovered(flight));
  }, 0);

  // === CANCELLATION-FOR-CREDIT PROFIT =====================================
  // When a customer cancels an UNBOOKED trip (no airline confirmation yet) on
  // Defera Capital (Stripe + NMI) or Defera Zero, the three cancel-trip edge
  // functions take a different path from a normal Stripe refund: the cash the
  // customer paid stays with Defera and an equal amount is issued as Defera
  // Credit (a future-use, house-credit liability). That decision is stamped
  // on the booking as `flight_data._cancelled_for_credit_cents` (the dollar
  // value, in cents, that was converted to credit) by all three cancel
  // endpoints — see supabase/functions/_shared/cancel-credit.ts and the
  // three callers (`defera-zero-cancel-trip`, `defera-capital-cancel-trip`,
  // `defera-capital-nmi-cancel-trip`).
  //
  // Accounting-wise this is analogous to "Profit from Loss" on phantom
  // defaults: no funds were paid to the airline/hotel, so the cash leg is
  // pure profit. We surface it explicitly under P&L Adjustments so the line
  // is auditable instead of silently rolled into Cancellation Refunds. The
  // credit-leg liability is recognized later, when the customer redeems the
  // credit on a future booking and the future trip absorbs the offset.
  const cancelForCreditFlights = filteredFlights.filter((flight) => {
    if (!isCancelledOrRefunded(flight)) return false;
    const fd = flight.flight_data || flight.flightData || {};
    return (Number(fd._cancelled_for_credit_cents) || 0) > 0;
  });
  const cancellationCreditAmount = cancelForCreditFlights.reduce((sum, flight) => {
    const fd = flight.flight_data || flight.flightData || {};
    return sum + ((Number(fd._cancelled_for_credit_cents) || 0) / 100);
  }, 0);
  const cancellationCreditCount = cancelForCreditFlights.length;
  // DC-only slice for the Defera Capital tab. Defera Zero credit-cancels stay
  // in the Defera (umbrella) view but are excluded from the DC-specific math.
  const isDeferaCapitalBooking = (flight) => {
    const fd = flight.flight_data || flight.flightData || {};
    const pm = fd._payment_method || fd.payment_method || fd.booking_option || flight.payment_method || 'installments';
    return pm === 'defera_capital';
  };
  const dcCancelForCreditFlights = cancelForCreditFlights.filter(isDeferaCapitalBooking);
  const dcCancellationCreditAmount = dcCancelForCreditFlights.reduce((sum, flight) => {
    const fd = flight.flight_data || flight.flightData || {};
    return sum + ((Number(fd._cancelled_for_credit_cents) || 0) / 100);
  }, 0);
  const dcCancellationCreditCount = dcCancelForCreditFlights.length;

  // === CREDIT REDEEMED (revenue offset) ===================================
  // Symmetric counterpart of Profit from Cancellation Credit: when a customer
  // applies Defera Credit at checkout, the booking row stores the GROSS
  // amounts (base + service + hold + stay) so revenue_total counts the full
  // sticker price — but Defera only collected (gross − credit_applied) in
  // actual cash. The `defera_credit_allocations` table (loaded async via
  // /api/admin/credit-redemptions-by-booking) records the committed amount
  // per (user, trip).
  //
  // Per-trip lifetime balance: a cancellation-for-credit grants $X of Profit
  // (cash stays with Defera). A later booking that redeems that $X reverses
  // the recognition here — the booking's accounting margin then reduces to
  // its true (service + hold − processing/investor/backer) margin instead of
  // over-counting the credit portion as fresh cash revenue. Reversed
  // allocations (status='reversed', set by `restore_defera_credit_on_cancel`)
  // are already excluded server-side, so cancel-for-credit on a credit-using
  // booking nets out cleanly.
  //
  // We only count allocations on non-pending bookings in the date range. The
  // total committed credit equals deposit_credit + installment_credit_total —
  // we use total (not applied) so the accrual matches the gross revenue that
  // was already counted at booking time.
  const creditRedeemedFlights = flightsForFinancials.filter((flight) => {
    const r = bookingsCreditRedeemedCents[flight.id];
    if (!r) return false;
    return ((Number(r.deposit_credit_cents) || 0) + (Number(r.installment_credit_total_cents) || 0)) > 0;
  });
  const creditRedeemedAmount = creditRedeemedFlights.reduce((sum, flight) => {
    const r = bookingsCreditRedeemedCents[flight.id] || {};
    const cents = (Number(r.deposit_credit_cents) || 0) + (Number(r.installment_credit_total_cents) || 0);
    return sum + cents / 100;
  }, 0);
  const creditRedeemedCount = creditRedeemedFlights.length;
  const dcCreditRedeemedFlights = creditRedeemedFlights.filter(isDeferaCapitalBooking);
  const dcCreditRedeemedAmount = dcCreditRedeemedFlights.reduce((sum, flight) => {
    const r = bookingsCreditRedeemedCents[flight.id] || {};
    const cents = (Number(r.deposit_credit_cents) || 0) + (Number(r.installment_credit_total_cents) || 0);
    return sum + cents / 100;
  }, 0);
  const dcCreditRedeemedCount = dcCreditRedeemedFlights.length;

  // === PARTIAL CASH REFUND PROFIT (manual override) =======================
  // dcBackedFlights (below) excludes cancelled/refunded trips from revenue
  // ENTIRELY, so a cancelled DC trip normally nets to $0 profit here — correct
  // when the customer got everything back. But when an admin records a
  // PARTIAL cash refund via "Amount Refunded" (Edit Flight Details), Defera
  // actually kept (amount paid − credit converted − amount refunded) in cash.
  // That kept difference would otherwise vanish along with the rest of the
  // trip's excluded revenue, so we surface it explicitly here — symmetric
  // with Profit from Cancellation Credit above (cash-kept-as-credit vs.
  // cash-kept-outright). The `_cancelled_for_credit_cents` slice is
  // subtracted because it's already recognized as profit on the
  // Cancellation Credit line — without this, a trip with both a credit
  // conversion and a manual cash refund would count the credit leg twice.
  // Clamped at 0 per trip so an over-entered refund can't drag profit
  // negative here (the umbrella Cancellation Refunds expense already
  // accounts for the refunded cash itself).
  const getKeptCashAfterManualRefund = (flight) => {
    const manualRefund = getManualRefundAmount(flight);
    if (manualRefund <= 0) return 0;
    const fd = flight.flight_data || flight.flightData || {};
    // Travel cash: a Faye premium kept on a cancelled trip is not Defera's.
    const paidDollars = insurance.travelCashCentsOf(flight) / 100;
    const creditDollars = (Number(fd._cancelled_for_credit_cents) || 0) / 100;
    return Math.max(0, paidDollars - creditDollars - manualRefund);
  };
  const dcManualPartialRefundFlights = filteredFlights.filter((flight) =>
    isCancelledOrRefunded(flight)
    && isDeferaCapitalBooking(flight)
    && getKeptCashAfterManualRefund(flight) > 0
  );
  const dcManualPartialRefundProfit = dcManualPartialRefundFlights.reduce(
    (sum, flight) => sum + getKeptCashAfterManualRefund(flight),
    0
  );
  const dcManualPartialRefundCount = dcManualPartialRefundFlights.length;

  // Headline metrics — actual customer payments (Stripe + NMI).
  const dcBackedFlights = filteredFlights.filter(
    (f) => hasConfirmationNumber(f) && !isCancelledOrRefunded(f)
  );
  // Per-booking capital = money Defera actually laid out to book the trip
  // (airline base + hotel stay). Excludes service fee and hold fee — those
  // are revenue owed by the customer, not capital deployed.
  const dcCapitalPerFlight = (flight) => {
    const base = getBaseForCost(flight);
    const stayAmount = flight.stay_data ? (flight.stay_data.totalAmount || flight.stay_data.total_amount || 0) : 0;
    return base + stayAmount;
  };
  const dcTotalBacked = dcBackedFlights.reduce(
    (sum, flight) => sum + dcCapitalPerFlight(flight),
    0,
  );
  // Total customer billing = full sticker on the customer's plan (capital +
  // service fee + hold fee). Used as the denominator when we pro-rate accrued
  // fee revenue by cash actually collected — so a customer who has paid 25%
  // of their bill recognizes 25% of accrued service/hold-fee revenue,
  // regardless of which slice of the plan those dollars nominally covered.
  const dcTotalBilling = dcBackedFlights.reduce((sum, flight) => {
    const base = getBaseForCost(flight);
    const stayAmount = flight.stay_data ? (flight.stay_data.totalAmount || flight.stay_data.total_amount || 0) : 0;
    const stay = flight.stay_data || (flight.stays && flight.stays[0]);
    const isFlightHotel = !!(stay && (stay.totalAmount || stay.total_amount));
    const serviceFee = isFlightHotel ? 0 : parseFloat(flight.service_fee || 0);
    const holdFee = parseFloat(flight.hold_fee || 0);
    return sum + base + stayAmount + serviceFee + holdFee;
  }, 0);

  // Profit / Loss from Fare: captured (or absorbed) when Defera ends up paying the
  // airline a different price than the customer agreed to pay. Two cases feed it:
  //   1. Confirmed trip with admin-set `_actual_cost` — we know the exact spread
  //      between what the customer was billed (Base Fare) and what we paid the
  //      airline.
  //   2. Unconfirmed trip with a `change_flight_history` entry — the customer
  //      (or admin) changed the flight before it was booked. For installment
  //      trips the booking row's `total_amount` is preserved at the customer's
  //      original locked-in base, while `flight_data.base_amount` reflects the
  //      new (anticipated) airline cost. The delta lands on Defera the moment
  //      the trip is actually booked, so we surface it now.
  //
  // Customer base = `total_amount` (locked-in base, net of credits) plus the
  // base portion of any `_change_flight_upfront_charges` (when the customer paid
  // upfront for an upgrade on an installment trip, that upgrade money lifts
  // their commitment proportionally — `amount_cents = base_diff_cents * 1.11`).
  // `pricing_snapshot.original_base_fare` is the pre-discount sticker price and
  // would overstate the spread, so we don't use it here.
  const getCustomerBaseFare = (flight) => {
    const fd = flight.flight_data || flight.flightData || {};
    // Booking-level totals are ALL-IN (service + hold fees included), so the
    // fees must be stripped or the fare delta silently measures fee margin +
    // fare spread — and NET then double-counts the fees it already recognizes
    // in Hold/Service Fees. flight_data-level amounts are engine-raw fares
    // that never had Defera fees added, so they pass through untouched.
    const feeStrip = (parseFloat(flight.service_fee) || 0) + (parseFloat(flight.hold_fee) || 0);
    // Credits reduced total_amount at checkout, but the LOCKED fare is the
    // pre-credit price — credit redemption is already netted in NET via
    // Credit Redeemed, so leaving it here would double-penalize the fare line.
    const creditAddback = parseFloat(fd.credits_total) || 0;
    const candidates = [
      [flight.totalPrice, feeStrip - creditAddback],
      [flight.total_price, feeStrip - creditAddback],
      [flight.total_amount, feeStrip - creditAddback],
      [fd.base_amount, 0],
      [fd.baseAmount, 0],
      [fd.total_amount, 0],
      [fd.totalAmount, 0],
    ];
    let baseFare = 0;
    for (const [v, strip] of candidates) {
      if (v == null) continue;
      const n = parseFloat(v);
      if (!Number.isNaN(n) && n > 0) { baseFare = Math.max(0, n - strip); break; }
    }
    if (baseFare <= 0) return 0;
    const upfrontCharges = Array.isArray(fd?._change_flight_upfront_charges)
      ? fd._change_flight_upfront_charges
      : [];
    if (upfrontCharges.length > 0) {
      // A travel insurance re-price rides in the same charge (insurance_cents): not fare.
      const upfrontBaseDollars = upfrontCharges.reduce(
        (s, c) => s + (Number(c?.amount_cents) || 0) - (Number(c?.insurance_cents) || 0), 0
      ) / 1.11 / 100;
      baseFare += upfrontBaseDollars;
    }
    return baseFare;
  };
  const getFareDelta = (flight) => {
    const fd = flight.flight_data || flight.flightData || {};
    const actualCost = parseFloat(fd._actual_cost ?? fd.actual_cost ?? 0) || 0;
    let anticipatedCost = actualCost;
    if (anticipatedCost <= 0 && !hasConfirmationNumber(flight) && hasChangeFlightHistory(flight)) {
      const history = flight.change_flight_history || flight.changeFlightHistory || [];
      const latest = history[history.length - 1];
      const newBaseCents = Number(latest?.new_base_fare_cents) || 0;
      if (newBaseCents > 0) {
        anticipatedCost = newBaseCents / 100;
      } else {
        const fdBase = parseFloat(fd?.base_amount ?? 0) || 0;
        if (fdBase > 0) anticipatedCost = fdBase;
      }
    }
    if (anticipatedCost <= 0) return 0;
    const baseFare = getCustomerBaseFare(flight);
    if (baseFare <= 0) return 0;
    return baseFare - anticipatedCost; // positive = profit, negative = loss
  };
  // Cohort for realized fare P&L: confirmed trips (where `_actual_cost` is the
  // signal) plus unconfirmed trips that have been changed at least once (where
  // the new vs. original base is the signal). Cancelled / refunded trips are
  // always excluded — the airline credits us back on those.
  const unconfirmedChangedFlights = filteredFlights.filter(
    (f) => !hasConfirmationNumber(f) && !isCancelledOrRefunded(f) && hasChangeFlightHistory(f)
  );
  const fareImpactFlights = [...dcBackedFlights, ...unconfirmedChangedFlights];
  const profitFromFareFlights = fareImpactFlights.filter((f) => getFareDelta(f) > 0);
  const profitFromFareCount = profitFromFareFlights.length;
  const profitFromFareAmount = profitFromFareFlights.reduce(
    (sum, f) => sum + getFareDelta(f),
    0
  );
  const lossFromFareFlights = fareImpactFlights.filter((f) => getFareDelta(f) < 0);
  const lossFromFareCount = lossFromFareFlights.length;
  const lossFromFareAmount = lossFromFareFlights.reduce(
    (sum, f) => sum + Math.abs(getFareDelta(f)),
    0
  );

  // Defera tab accrued versions: same delta, but on the COGS basis (excl. cancelled),
  // representing the realized fare-timing P&L as if every backed trip pays off in full.
  const accruedProfitFromFareAmount = flightsExclCancelled.reduce(
    (sum, f) => sum + Math.max(0, getFareDelta(f)),
    0
  );
  const accruedProfitFromFareCount = flightsExclCancelled.filter(
    (f) => getFareDelta(f) > 0
  ).length;
  const accruedLossFromFareAmount = flightsExclCancelled.reduce(
    (sum, f) => sum + Math.max(0, -getFareDelta(f)),
    0
  );
  const accruedLossFromFareCount = flightsExclCancelled.filter(
    (f) => getFareDelta(f) < 0
  ).length;
  // Raw cash collected across backed trips — sourced from Stripe AND NMI
  // (the /api/admin/bookings-paid-amounts endpoint unifies both processors).
  // This is the number that drives fee-revenue recognition below.
  // Travel cash: the premium's share of what customers paid is Faye's, so it
  // drives no fee revenue, recovery or investor/backer fee.
  const dcTotalCollected = dcBackedFlights.reduce((sum, flight) => {
    const cents = insurance.travelCashCentsOf(flight);
    return sum + cents / 100;
  }, 0);
  // Total Recovered on the DC "Capital Deployed & Recovered" section = the
  // slice of collected cash that has actually paid down deployed capital
  // (capped per-booking at that booking's base + stay). Everything past the
  // capital cap is fee revenue and stays in dcTotalCollected instead.
  const dcTotalRecovered = dcBackedFlights.reduce((sum, flight) => {
    const paid = insurance.travelCashCentsOf(flight) / 100;
    const capital = dcCapitalPerFlight(flight);
    return sum + Math.min(paid, capital);
  }, 0);
  const dcOutstanding = Math.max(0, dcTotalBacked - dcTotalRecovered);

  // Hold Fees: flat 15% of cash collected (Stripe + NMI). Accrued sums the
  // per-booking `hold_fee` field so we get the exact billed hold-fee across
  // Defera Capital, Defera Zero and Slice Pay-era plans, instead of applying
  // a flat 15% to Total Backed (which now excludes SF, and hold fee itself
  // is calculated off base + SF at booking time, not off capital alone).
  const DC_HOLD_FEE_RATE = 0.15;
  const dcHoldFees = dcTotalCollected * DC_HOLD_FEE_RATE;
  const dcAccruedHoldFees = dcBackedFlights.reduce(
    (sum, flight) => sum + (parseFloat(flight.hold_fee) || 0),
    0,
  );

  // Service Fees: each booking has its own service fee dollar amount. Total accrued
  // service fees = sum across backed trips. Recognized (real-time) = pro-rate by
  // cash collected / total customer billing so it scales linearly with what
  // the customer has actually paid off across their entire plan (capital +
  // fees together, matching how installment payments implicitly service
  // every slice at once).
  const dcAccruedServiceFees = dcBackedFlights.reduce((sum, flight) => {
    const stay = flight.stay_data || (flight.stays && flight.stays[0]);
    const isFlightHotel = !!(stay && (stay.totalAmount || stay.total_amount));
    const serviceFee = isFlightHotel ? 0 : parseFloat(flight.service_fee || 0);
    return sum + serviceFee;
  }, 0);
  const dcRecoveryRatio = dcTotalBilling > 0 ? Math.min(1, dcTotalCollected / dcTotalBilling) : 0;
  const dcServiceFees = dcAccruedServiceFees * dcRecoveryRatio;


  // === EXPENSES ===
  // Investor Fees: 14% of cash collected (Stripe + NMI). Recognized as
  // customers actually pay, matching the same collection-based recognition
  // we use for Hold Fees. Accrued = 14% of total customer billing (full
  // obligation if every trip pays off).
  const DC_INVESTOR_FEE_RATE = 0.14;
  const dcInvestorFees = dcTotalCollected * DC_INVESTOR_FEE_RATE;
  const dcAccruedInvestorFees = dcTotalBilling * DC_INVESTOR_FEE_RATE;

  // Backer Fees: paid out only on backer-assigned trips. Trips with NO backer have
  // dcBackerFees = 0 (filtered out below) but still contribute fully to dcHoldFees
  // and dcServiceFees — so Defera keeps the entire customer hold + service fee on
  // those trips, exactly as expected.
  // Which model applies comes from each backer's own recorded terms
  // (`resolveBackerTerms`), never from who they are — this used to name Ravi
  // Teja by email address:
  //   - base_fare_share backers: rate × Base Fare on their trips. The customer
  //     pays the rate on (base + service); the backer only earns on base, so the
  //     service-fee slice of the hold fee stays with Defera. Real-time
  //     recognition scales with collection (paid / cost); accrued assumes full
  //     payoff.
  //   - service_fee_share backers: rate × recovered cash. Accrued = rate × cost.
  /** Last-resort rate for a backer whose terms we could not load at all. Matches
   *  DEFAULT_BACKER_RATE.service_fee_share — kept local because it is the
   *  fallback for a MISSING user, not a statement about anyone's deal. */
  const NON_RAVI_BACKER_RATE = 0.25;
  const backerAssignedFlights = dcBackedFlights.filter((f) => f.backer_id || f.backerId);
  const tripCost = (flight) => {
    const base = getBaseForCost(flight);
    const stayAmount = flight.stay_data ? (flight.stay_data.totalAmount || flight.stay_data.total_amount || 0) : 0;
    const stay = flight.stay_data || (flight.stays && flight.stays[0]);
    const isFlightHotel = !!(stay && (stay.totalAmount || stay.total_amount));
    const serviceFee = isFlightHotel ? 0 : parseFloat(flight.service_fee || 0);
    return base + stayAmount + serviceFee;
  };
  /** Same terms lookup as the Defera-tab block above — identity is no longer the
   *  discriminator, the recorded deal is. The two SEMANTICS here are unchanged
   *  and deliberately different from each other: a base-fare-share backer earns
   *  against the fare and is scaled by collection, while a service-fee-share
   *  backer earns against cash actually recovered. Only the "which model" test
   *  and the rates moved. */
  const dcBackerTerms = backerTermsById(users);
  const dcBackerTermsFor = (flight) =>
    dcBackerTerms.get(flight.backer_id || flight.backerId)
    || { model: BACKER_MODEL_SERVICE_FEE, rate: NON_RAVI_BACKER_RATE };
  const dcBackerFees = backerAssignedFlights.reduce((sum, flight) => {
    const paid = insurance.travelCashCentsOf(flight) / 100;
    const terms = dcBackerTermsFor(flight);
    if (terms.model === BACKER_MODEL_BASE_FARE) {
      const base = getBaseForCost(flight);
      const cost = tripCost(flight);
      const recoveryRatio = cost > 0 ? Math.min(1, paid / cost) : 0;
      return sum + base * terms.rate * recoveryRatio;
    }
    return sum + paid * terms.rate;
  }, 0);
  const dcAccruedBackerFees = backerAssignedFlights.reduce((sum, flight) => {
    const terms = dcBackerTermsFor(flight);
    if (terms.model === BACKER_MODEL_BASE_FARE) {
      return sum + getBaseForCost(flight) * terms.rate;
    }
    return sum + tripCost(flight) * terms.rate;
  }, 0);

  // Payment processing fees:
  //   Stripe — 4.4% on volume (2.9% + 1.5% instant transfer) + $0.30/charge on
  //     Defera Capital (Stripe track), Defera Zero, and Reserve.
  //   NMI — 3.95% on volume for Defera Capital bookings on the NMI processor
  //     track (`flight_data._payment_processor === 'nmi'`). No rolling reserves.
  //   Live view: % × actually-collected cash (skips fixed per-charge fee mid-cycle).
  //   Accrued view: full obligation if every backed trip pays off in full.
  const isStripeRoutedBooking = (flight) => {
    const fd = flight.flight_data || flight.flightData || {};
    const pm = fd._payment_method || fd.payment_method || fd.booking_option || flight.payment_method || 'installments';
    return (pm === 'defera_capital' || pm === 'defera_zero' || pm === 'reserve') && !isNmiProcessor(flight);
  };
  const isNmiRoutedBooking = (flight) => {
    const fd = flight.flight_data || flight.flightData || {};
    const pm = fd._payment_method || fd.payment_method || fd.booking_option || flight.payment_method || 'installments';
    return pm === 'defera_capital' && isNmiProcessor(flight);
  };
  // Processing runs on every dollar collected, Faye premium included: whole
  // cash for the live view, the whole plan (travel + premium) for accrued.
  const dcStripeFees = dcBackedFlights.reduce((sum, flight) => {
    if (!isStripeRoutedBooking(flight)) return sum;
    const paid = insurance.cashCentsOf(flight) / 100;
    return sum + paid * STRIPE_PROCESSING_RATE;
  }, 0);
  const dcNmiFees = dcBackedFlights.reduce((sum, flight) => {
    if (!isNmiRoutedBooking(flight)) return sum;
    const paid = insurance.cashCentsOf(flight) / 100;
    return sum + paid * NMI_PROCESSING_RATE;
  }, 0);
  const dcProcessingFees = dcStripeFees + dcNmiFees;
  const dcAccruedStripeFees = dcBackedFlights.reduce((sum, flight) => {
    if (!isStripeRoutedBooking(flight)) return sum;
    const fd = flight.flight_data || flight.flightData || {};
    const pm = fd._payment_method || fd.payment_method || fd.booking_option || flight.payment_method || 'installments';
    const grandTotal = parseFloat(flight.grand_total_with_hold || flight.grandTotalWithHold || flight.grand_total || flight.grandTotal || flight.totalPrice || 0)
      + insurance.premiumDollarsOf(flight);
    if (grandTotal <= 0) return sum;
    if (pm === 'reserve') return sum + (grandTotal * STRIPE_PROCESSING_RATE + STRIPE_FIXED_FEE) * 2;
    return sum + grandTotal * STRIPE_PROCESSING_RATE + DC_DZ_AVG_TRANSACTIONS * STRIPE_FIXED_FEE;
  }, 0);
  const dcAccruedNmiFees = dcBackedFlights.reduce((sum, flight) => {
    if (!isNmiRoutedBooking(flight)) return sum;
    const grandTotal = parseFloat(flight.grand_total_with_hold || flight.grandTotalWithHold || flight.grand_total || flight.grandTotal || flight.totalPrice || 0)
      + insurance.premiumDollarsOf(flight);
    if (grandTotal <= 0) return sum;
    return sum + grandTotal * NMI_PROCESSING_RATE;
  }, 0);
  const dcAccruedProcessingFees = dcAccruedStripeFees + dcAccruedNmiFees;

  // Default rate denominated against actual deployed capital.
  const dcDefaultRate = dcTotalBacked > 0 ? (totalDefaultedAmount / dcTotalBacked) * 100 : 0;

  // Net (real-time) = recognized revenue − recognized expenses − capital outstanding
  //                   + Profit from Loss + Profit from Cancellation Credit
  //                   − Credit Redeemed (revenue offset)
  //                   + Profit from Fare − Loss from Fare.
  // Profit from Cancellation Credit mirrors Profit from Loss: customer cash on
  // unbooked-then-cancelled DC trips stays with Defera (issued as Defera Credit
  // instead of refunded), so it's pure profit at the cash leg.
  // Credit Redeemed is the symmetric drag — gross trip revenue on bookings that
  // partially paid with previously-issued credit isn't new cash. Over a full
  // credit lifetime the two cancel out, leaving only the new trip's true
  // service-fee + hold margin as recognised profit.
  const dcNetProfit =
    (dcHoldFees + dcServiceFees) - dcInvestorFees - dcBackerFees - dcProcessingFees - dcOutstanding
    + lossPreventedAmount + dcCancellationCreditAmount - dcCreditRedeemedAmount
    + profitFromFareAmount - lossFromFareAmount + dcManualPartialRefundProfit;
  // Net excluding outstanding = same as Net but without the capital-still-out drag.
  // Reflects only what has actually happened so far (recognized revenue & expenses).
  const dcNetExclOutstanding =
    (dcHoldFees + dcServiceFees) - dcInvestorFees - dcBackerFees - dcProcessingFees
    + lossPreventedAmount + dcCancellationCreditAmount - dcCreditRedeemedAmount
    + profitFromFareAmount - lossFromFareAmount + dcManualPartialRefundProfit;
  // Accrued Net = Net assuming every backed trip paid off in full. Independent calc:
  //   accrued revenue (Hold + Service) − accrued expenses (Investor + Defera Capital + processing)
  //   + Profit from Loss + Profit from Cancellation Credit − Credit Redeemed
  //   + Profit from Fare − Loss from Fare + Profit from Partial Refund. No outstanding drag.
  const dcAccruedNet =
    (dcAccruedHoldFees + dcAccruedServiceFees)
    - dcAccruedInvestorFees
    - dcAccruedBackerFees
    - dcAccruedProcessingFees
    + lossPreventedAmount
    + dcCancellationCreditAmount
    - dcCreditRedeemedAmount
    + profitFromFareAmount
    - lossFromFareAmount
    + dcManualPartialRefundProfit;
  
  // Abandoned carts value (for cart metrics, not revenue)
  // Include both flight cost AND hotel cost if present
  const abandonedValue = filteredAbandonedTrips
    .filter(t => t.status !== 'confirmed' && t.status !== 'completed')
    .reduce((sum, trip) => {
      const flightCost = trip.total_amount || 0;
      const hotelCost = trip.stay_data ? (trip.stay_data.totalAmount || trip.stay_data.total_amount || 0) : 0;
      return sum + flightCost + hotelCost;
    }, 0);

  // Cart stage breakdown
  const stepBreakdown = {
    review: filteredAbandonedTrips.filter(trip => trip.step_reached === 'review').length,
    passenger_details: filteredAbandonedTrips.filter(trip => trip.step_reached === 'passenger_details').length,
    payment_initiated: filteredAbandonedTrips.filter(trip => trip.step_reached === 'payment_initiated').length
  };

  // Conversion Timeline: Calculate time from user sign-up to first booking
  const conversionTimelineData = filteredFlights
    .map((booking) => {
      // Find the user who made this booking
      const bookingUserId = booking.user_id || booking.userId;
      const user = users.find(u => u.id === bookingUserId);
      
      if (!user) return null;
      
      // Calculate days from user sign-up to booking creation
      try {
        const userSignUpDate = parseISO(user.created_at || user.createdAt);
        const bookingDate = parseISO(booking.created_at || booking.createdAt);
        const daysDiff = differenceInCalendarDays(bookingDate, userSignUpDate);
        
        // Extract flight data for display (name-first logo — admin airline
        // renames leave stored logo URLs pointing at the old carrier)
        const flightData = booking.flight_data || booking.flightData;
        const bookingCarrier = flightData?.slices?.[0]?.segments?.[0]?.marketing_carrier;
        const airlineLogo = resolveCarrierLogo(bookingCarrier);
        const airlineCode = resolveAirlineIata(bookingCarrier?.name) || bookingCarrier?.iata_code || booking.marketing_airline || 'FL';
        
        // Extract route info
        const originCode = flightData?.slices?.[0]?.origin?.id || 
                          flightData?.slices?.[0]?.origin?.iata_code || 
                          booking.departure_city || '';
        const destCode = flightData?.slices?.[0]?.destination?.id || 
                        flightData?.slices?.[0]?.destination?.iata_code || 
                        booking.arrival_city || '';
        
        // Get custom title if available
        const customTitle = booking.customTitle || booking.custom_title || 
                           flightData?.custom_title || '';
        
        return {
          id: booking.id,
          userEmail: user.email,
          conversionDays: daysDiff,
          bookingDate: bookingDate,
          airlineLogo,
          airlineCode,
          originCode,
          destCode,
          customTitle,
          bookingDateFormatted: format(bookingDate, 'MMM dd, yyyy')
        };
      } catch (e) {
        return null;
      }
    })
    .filter(item => item !== null)
    .sort((a, b) => b.bookingDate - a.bookingDate); // Most recent first
  
  // Calculate average conversion time
  const avgConversionDays = conversionTimelineData.length > 0
    ? Math.round(conversionTimelineData.reduce((sum, item) => sum + item.conversionDays, 0) / conversionTimelineData.length)
    : 0;

  // Volume Projections: Monthly breakdown with growth predictions
  const volumeProjectionData = [];
  
  // Build historical months (last 4 months including current)
  const historicalMonths = [];
  for (let i = -3; i <= 0; i++) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const monthStart = startOfDay(monthDate);
    const monthEnd = i === 0 
      ? now 
      : endOfDay(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0));
    
    // Monthly GMV on the same basis as the headline: cancelled/refunded
    // excluded. This series is the projection engine's ground truth — the
    // chart's "Actual" points, the month-over-month growth rates, the
    // current-month run rate, and the milestone cards all derive from it, so
    // when it summed every booking regardless of status the entire Volume
    // Projections card (and its compounding forecast) was inflated by
    // book-and-cancel churn the headline had already learned to exclude.
    const monthRevenue = travelFlights.filter((f) => {
      if (isCancelledOrRefunded(f)) return false;
      const flightDate = parseISO(f.created_at || f.createdAt);
      return flightDate >= monthStart && flightDate <= monthEnd;
    }).reduce((sum, f) => sum + bookedValueOf(f), 0);
    
    // Total signups (cumulative - all users up to this date)
    const totalSignups = users.filter((u) => {
      try {
        const userDate = parseISO(u.created_at || u.createdAt);
        return userDate <= monthEnd;
      } catch (e) {
        return false;
      }
    }).length;
    
    // New signups this month (for growth calculation)
    const newSignups = users.filter((u) => {
      try {
        const userDate = parseISO(u.created_at || u.createdAt);
        return userDate >= monthStart && userDate <= monthEnd;
      } catch (e) {
        return false;
      }
    }).length;
    
    historicalMonths.push({
      month: format(monthDate, 'MMM yyyy'),
      revenue: monthRevenue,
      signups: totalSignups,
      newSignups: newSignups,
      isProjected: false,
      isCurrent: i === 0
    });
  }
  
  // ENHANCED: Project current month to completion using daily rate
  const currentMonthData = historicalMonths[historicalMonths.length - 1];
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const daysElapsedInMonth = Math.ceil((now - currentMonthStart) / (1000 * 60 * 60 * 24));
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  
  // Calculate daily rates for current month
  const dailyRevenueRate = daysElapsedInMonth > 0 ? currentMonthData.revenue / daysElapsedInMonth : 0;
  const dailySignupRate = daysElapsedInMonth > 0 ? currentMonthData.newSignups / daysElapsedInMonth : 0;
  
  // Project full month based on current daily rate
  const projectedCurrentMonthRevenue = dailyRevenueRate * daysInMonth;
  const projectedCurrentMonthNewSignups = Math.round(dailySignupRate * daysInMonth);
  
  // Calculate month-over-month growth rates from COMPLETED historical months only (exclude current partial month)
  const completedMonths = historicalMonths.slice(0, -1);
  const revenueGrowthRates = [];
  const signupGrowthRates = [];
  
  for (let i = 1; i < completedMonths.length; i++) {
    const prevRevenue = completedMonths[i - 1].revenue;
    const currRevenue = completedMonths[i].revenue;
    const prevNewSignups = completedMonths[i - 1].newSignups;
    const currNewSignups = completedMonths[i].newSignups;
    
    // Only calculate growth if we have meaningful baseline data
    if (prevRevenue >= 1000 && currRevenue > 0) {
      revenueGrowthRates.push((currRevenue - prevRevenue) / prevRevenue);
    }
    if (prevNewSignups >= 10 && currNewSignups > 0) {
      signupGrowthRates.push((currNewSignups - prevNewSignups) / prevNewSignups);
    }
  }
  
  // If we have current month data, compare projected current month to last completed month
  if (completedMonths.length > 0 && projectedCurrentMonthRevenue > 0) {
    const lastCompletedMonth = completedMonths[completedMonths.length - 1];
    if (lastCompletedMonth.revenue >= 1000) {
      const projectedGrowth = (projectedCurrentMonthRevenue - lastCompletedMonth.revenue) / lastCompletedMonth.revenue;
      revenueGrowthRates.push(projectedGrowth);
    }
    if (lastCompletedMonth.newSignups >= 10 && projectedCurrentMonthNewSignups > 0) {
      const projectedSignupGrowth = (projectedCurrentMonthNewSignups - lastCompletedMonth.newSignups) / lastCompletedMonth.newSignups;
      signupGrowthRates.push(projectedSignupGrowth);
    }
  }
  
  // Calculate average growth rate with conservative defaults
  const avgRevenueGrowth = revenueGrowthRates.length > 0
    ? revenueGrowthRates.reduce((sum, rate) => sum + rate, 0) / revenueGrowthRates.length
    : 0.20; // Default 20% growth if no history
  
  const avgSignupGrowth = signupGrowthRates.length > 0
    ? signupGrowthRates.reduce((sum, rate) => sum + rate, 0) / signupGrowthRates.length
    : 0.20;
  
  // Cap growth rates to reasonable bounds (max 50% month-over-month)
  const revenueGrowthRate = Math.max(0, Math.min(0.5, avgRevenueGrowth));
  const signupGrowthRate = Math.max(0, Math.min(0.5, avgSignupGrowth));
  
  // FALLBACK BASELINE: When current month has no revenue, use last completed month WITH GROWTH APPLIED
  // This ensures projections work even at the start of a new month with no bookings yet
  const lastCompletedMonthRevenue = completedMonths.length > 0 
    ? completedMonths[completedMonths.length - 1].revenue 
    : 0;
  const lastCompletedMonthNewSignups = completedMonths.length > 0 
    ? completedMonths[completedMonths.length - 1].newSignups 
    : 0;
  
  // Determine if we need to use fallback (current month has no actual or projected revenue)
  const needsFallback = projectedCurrentMonthRevenue <= 0 && currentMonthData.revenue <= 0;
  const effectiveGrowthRate = Math.max(revenueGrowthRate, 0.10); // At least 10% growth
  const effectiveSignupGrowthRate = Math.max(signupGrowthRate, 0.10);
  
  // Use PROJECTED current month as baseline, OR last completed month WITH GROWTH APPLIED if current is empty
  // When falling back, apply growth because current month is a PROJECTION, not a repeat of last month
  const currentMonthRevenue = projectedCurrentMonthRevenue > 0 
    ? projectedCurrentMonthRevenue 
    : (currentMonthData.revenue > 0 
        ? currentMonthData.revenue 
        : lastCompletedMonthRevenue * (1 + effectiveGrowthRate)); // Apply growth to fallback
  const currentTotalSignups = currentMonthData.signups;
  const currentNewSignups = projectedCurrentMonthNewSignups > 0 
    ? projectedCurrentMonthNewSignups 
    : (currentMonthData.newSignups > 0 
        ? currentMonthData.newSignups 
        : Math.round(lastCompletedMonthNewSignups * (1 + effectiveSignupGrowthRate))); // Apply growth to fallback
  
  // Calculate projected cumulative signups for current month
  // Use currentNewSignups (which has fallback applied) instead of projectedCurrentMonthNewSignups
  const effectiveNewSignups = currentNewSignups; // Already has growth applied if fallback
  const remainingProjectedSignupsForDisplay = effectiveNewSignups - currentMonthData.newSignups;
  const projectedCumulativeSignupsForCurrentMonth = currentTotalSignups + Math.max(0, remainingProjectedSignupsForDisplay);
  
  // Add historical months with projected values for smooth transition
  historicalMonths.forEach((month, idx) => {
    const isCurrentMonth = idx === historicalMonths.length - 1;
    
    volumeProjectionData.push({
      month: month.month,
      // Show actual revenue for completed months, null for current month (will use projected line)
      revenue: isCurrentMonth ? null : month.revenue,
      signups: isCurrentMonth ? null : month.signups,
      // For current month, show projected full month value to bridge actual → projected
      revenueProjected: isCurrentMonth ? currentMonthRevenue : null,
      signupsProjected: isCurrentMonth ? projectedCumulativeSignupsForCurrentMonth : null,
      isProjected: isCurrentMonth // Mark current month as "projected" since it's extrapolated
    });
  });
  
  // Project future months with DIMINISHING RETURNS (startup growth fatigue model)
  // Early months: High growth | Later months: Plateau/stabilization
  const GROWTH_DECAY_RATE = 0.85; // Each month, growth rate decays by 15%
  const MIN_GROWTH_FLOOR = 0.05; // Floor at 5% growth (prevents stagnation/decline)
  
  let projectedRevenue = currentMonthRevenue;
  let projectedNewSignups = Math.max(1, currentNewSignups);
  
  // Start with current growth rates, but decay them over time
  let currentRevenueGrowth = Math.max(revenueGrowthRate, 0.10); // Start at least 10%
  let currentSignupGrowth = Math.max(signupGrowthRate, 0.10);
  
  // CRITICAL: Use projected cumulative signups (with fallback applied) as baseline for future months
  const baselineCumulativeSignups = projectedCumulativeSignupsForCurrentMonth;
  let cumulativeSignups = baselineCumulativeSignups; // Start from clean baseline
  
  for (let i = 1; i <= 12; i++) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
    
    // Apply DECAYING growth rate (simulates market saturation)
    projectedRevenue = projectedRevenue * (1 + currentRevenueGrowth);
    
    // Apply DECAYING signup growth
    projectedNewSignups = Math.round(projectedNewSignups * (1 + currentSignupGrowth));
    cumulativeSignups += Math.max(1, projectedNewSignups);
    
    volumeProjectionData.push({
      month: format(monthDate, 'MMM yyyy'),
      revenue: null,
      signups: null,
      revenueProjected: projectedRevenue,
      signupsProjected: cumulativeSignups,
      isProjected: true
    });
    
    // DECAY the growth rate for next month (startup fatigue)
    currentRevenueGrowth = Math.max(currentRevenueGrowth * GROWTH_DECAY_RATE, MIN_GROWTH_FLOOR);
    currentSignupGrowth = Math.max(currentSignupGrowth * GROWTH_DECAY_RATE, MIN_GROWTH_FLOOR);
  }
  
  // Get specific month projections for milestone cards (with SAME diminishing returns)
  const month3Date = new Date(now.getFullYear(), now.getMonth() + 3, 1);
  const month6Date = new Date(now.getFullYear(), now.getMonth() + 6, 1);
  const month12Date = new Date(now.getFullYear(), now.getMonth() + 12, 1);
  
  // Calculate milestones using DECAYING growth rates
  let milestoneRevenue = currentMonthRevenue;
  let milestoneRevenueGrowth = Math.max(revenueGrowthRate, 0.10);
  let projected3MonthRevenue = 0;
  let projected6MonthRevenue = 0;
  let projected12MonthRevenue = 0;
  
  for (let i = 1; i <= 12; i++) {
    milestoneRevenue = milestoneRevenue * (1 + milestoneRevenueGrowth);
    if (i === 3) projected3MonthRevenue = milestoneRevenue;
    if (i === 6) projected6MonthRevenue = milestoneRevenue;
    if (i === 12) projected12MonthRevenue = milestoneRevenue;
    milestoneRevenueGrowth = Math.max(milestoneRevenueGrowth * GROWTH_DECAY_RATE, MIN_GROWTH_FLOOR);
  }
  
  // Calculate cumulative signups for milestones (with SAME diminishing returns)
  // CRITICAL: Use CLEAN baseline, not post-loop cumulativeSignups (which already added 12 months)
  let milestone3Signups = baselineCumulativeSignups;
  let milestone6Signups = baselineCumulativeSignups;
  let milestone12Signups = baselineCumulativeSignups;
  let tempNewSignups = Math.max(1, currentNewSignups);
  let tempSignupGrowth = Math.max(signupGrowthRate, 0.10);
  
  for (let i = 1; i <= 12; i++) {
    tempNewSignups = Math.round(tempNewSignups * (1 + tempSignupGrowth));
    const additionalSignups = Math.max(1, tempNewSignups);
    
    if (i <= 3) milestone3Signups += additionalSignups;
    if (i <= 6) milestone6Signups += additionalSignups;
    if (i <= 12) milestone12Signups += additionalSignups;
    
    tempSignupGrowth = Math.max(tempSignupGrowth * GROWTH_DECAY_RATE, MIN_GROWTH_FLOOR);
  }
  
  const projected3MonthSignups = milestone3Signups;
  const projected6MonthSignups = milestone6Signups;
  const projected12MonthSignups = milestone12Signups;
  
  const month3Name = format(month3Date, 'MMM yyyy');
  const month6Name = format(month6Date, 'MMM yyyy');
  const month12Name = format(month12Date, 'MMM yyyy');
  
  // Calculate baseline metrics for display
  const avgMonthlyRevenue = currentMonthRevenue;
  const avgMonthlySignups = currentNewSignups;

  // ===========================================================================
  // AI VOLUME PROJECTION — richer monthly history + leading indicators
  // Builds a longer monthly time series (GMV, bookings, signups, and CART
  // activity as a leading indicator) that gets sent to the server for an
  // AI-driven forecast. This is separate from the deterministic series above
  // so it never disturbs the existing baseline chart.
  // ===========================================================================
  const safeParse = (d) => {
    if (!d) return null;
    try { const p = parseISO(d); return isNaN(p) ? null : p; } catch (e) { return null; }
  };

  // Find the earliest activity date across bookings, users, and carts.
  let earliestActivity = null;
  const considerDate = (d) => {
    const p = safeParse(d);
    if (p && (!earliestActivity || p < earliestActivity)) earliestActivity = p;
  };
  flights.forEach((f) => considerDate(f.created_at || f.createdAt));
  users.forEach((u) => considerDate(u.created_at || u.createdAt));
  abandonedTrips.forEach((t) => considerDate(t.created_at || t.createdAt));

  // Cap history window to 18 months to keep the payload compact.
  let monthsOfHistoryAvailable = 1;
  if (earliestActivity) {
    monthsOfHistoryAvailable =
      (now.getFullYear() - earliestActivity.getFullYear()) * 12 +
      (now.getMonth() - earliestActivity.getMonth()) + 1;
  }
  const aiHistoryWindow = Math.max(2, Math.min(18, monthsOfHistoryAvailable));

  const aiMonthlyHistory = [];
  for (let i = aiHistoryWindow - 1; i >= 0; i--) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthStart = startOfDay(monthDate);
    const isThisMonth = i === 0;
    const monthEnd = isThisMonth
      ? now
      : endOfDay(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0));

    let gmv = 0;
    let bookings = 0;
    travelFlights.forEach((f) => {
      const d = safeParse(f.created_at || f.createdAt);
      if (d && d >= monthStart && d <= monthEnd) {
        // `bookings` counts every booking created (it pairs with `carts` as
        // the cart→booking conversion signal), but the GMV the forecast is
        // asked to project is the headline basis: cancelled/refunded
        // contribute $0, exactly like the deterministic series above.
        bookings += 1;
        if (!isCancelledOrRefunded(f)) gmv += bookedValueOf(f);
      }
    });

    let newSignups = 0;
    let cumulativeSignups = 0;
    users.forEach((u) => {
      const d = safeParse(u.created_at || u.createdAt);
      if (!d) return;
      if (d <= monthEnd) cumulativeSignups += 1;
      if (d >= monthStart && d <= monthEnd) newSignups += 1;
    });

    let carts = 0;
    let cartValue = 0;
    abandonedTrips.forEach((t) => {
      const d = safeParse(t.created_at || t.createdAt);
      if (d && d >= monthStart && d <= monthEnd) {
        carts += 1;
        cartValue += parseFloat(t.total_amount || t.totalAmount || 0) || 0;
      }
    });

    aiMonthlyHistory.push({
      month: format(monthDate, 'MMM yyyy'),
      gmv: Math.round(gmv * 100) / 100,
      bookings,
      newSignups,
      cumulativeSignups,
      carts,
      cartValue: Math.round(cartValue * 100) / 100,
      isPartial: isThisMonth,
    });
  }

  // Future month labels — MUST match the deterministic future loop (now+1..now+12)
  // so the AI forecast lines up with the chart's x-axis.
  const aiFutureMonths = [];
  for (let i = 1; i <= 12; i++) {
    aiFutureMonths.push(format(new Date(now.getFullYear(), now.getMonth() + i, 1), 'MMM yyyy'));
  }

  // When an AI forecast is loaded, overlay it onto the projection chart by
  // replacing the dashed "projected" values for the future months. The forecast
  // labels (now+1..now+12) don't include the current partial month, so that
  // node keeps its data-derived projection and the handoff stays smooth.
  if (aiProjection?.forecast?.length) {
    const aiByMonth = new Map(aiProjection.forecast.map((f) => [f.month, f]));
    volumeProjectionData.forEach((row) => {
      const hit = aiByMonth.get(row.month);
      if (hit) {
        row.revenueProjected = hit.gmv;
        row.signupsProjected = hit.cumulativeSignups;
      }
    });
  }

  // Top airlines data
  const airlineCount = {};
  filteredFlights.forEach((flight) => {
    const airline = flight.marketing_airline || 'Unknown';
    airlineCount[airline] = (airlineCount[airline] || 0) + 1;
  });
  filteredAbandonedTrips.forEach((trip) => {
    const airline = trip.marketing_airline || 'Unknown';
    airlineCount[airline] = (airlineCount[airline] || 0) + 1;
  });

  const topAirlines = Object.entries(airlineCount)
    .map(([airline, count]) => ({ airline, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 20); // Show only top 20 airlines

  // Referral source data is now fetched from server (pre-aggregated for performance)
  const REFERRAL_COLORS = ['#2A3868', '#ec4899', '#f97316', '#10b981', '#4E6199'];

  // Calculate referral sources correlated with actual bookings
  // Find users who have bookings and count by their referral source
  const referralToBookingsData = (() => {
    // Get all unique user IDs from bookings
    const usersWithBookings = new Set(
      flights
        .filter(f => f.user_id || f.userId)
        .map(f => f.user_id || f.userId)
    );
    
    // Count referral sources for users who have bookings
    const referralBookingCount = {};
    users.forEach(user => {
      if (usersWithBookings.has(user.id) && user.referral_source) {
        const displayName = referralSourceLabel(user.referral_source);
        referralBookingCount[displayName] = (referralBookingCount[displayName] || 0) + 1;
      }
    });
    
    // Convert to array format for pie chart
    return Object.entries(referralBookingCount)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  })();

  // Calculate number of days in range for chart progression (exact calendar days, inclusive)
  const daysInRange = differenceInCalendarDays(endDate, startDate) + 1;
  
  // Create array for EVERY day in range (Robinhood-style progression)
  const allDaysInRange = Array.from({ length: daysInRange }, (_, i) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    return {
      date: format(date, 'MMM dd'),
      fullDate: startOfDay(date),
      dateKey: format(date, 'yyyy-MM-dd'),
      revenue: 0,
      uploads: 0
    };
  });

  // Aggregate actual revenue and uploads into the day buckets.
  // `revenue` is the headline basis (cancelled/refunded contribute $0), so the
  // cumulative Volume chart's final point equals the Total Volume tile above
  // it. `uploads` stays a bookings-created count across all statuses — the
  // status pie next to it is where the cancelled share is shown.
  filteredFlights.forEach((flight) => {
    const flightDate = startOfDay(parseISO(flight.created_at || flight.createdAt));
    const dayIndex = allDaysInRange.findIndex(d =>
      d.fullDate.getTime() === flightDate.getTime()
    );

    if (dayIndex !== -1) {
      if (!isCancelledOrRefunded(flight)) {
        allDaysInRange[dayIndex].revenue += bookedValueOf(flight);
      }
      allDaysInRange[dayIndex].uploads += 1;
    }
  });
  
  // Do NOT add abandoned trips to revenue - revenue only comes from bookings table

  // Calculate CUMULATIVE totals for Robinhood-style progression
  let cumulativeRevenue = 0;
  let cumulativeUploads = 0;
  let cumulativeCarts = 0;
  let cumulativeUsers = 0;
  
  const revenueChartData = allDaysInRange.map(day => {
    cumulativeRevenue += day.revenue;
    return {
      date: day.date,
      revenue: parseFloat(cumulativeRevenue.toFixed(2))
    };
  });

  const uploadsChartData = allDaysInRange.map(day => {
    cumulativeUploads += day.uploads;
    return {
      date: day.date,
      uploads: cumulativeUploads
    };
  });

  // Calculate cumulative carts for chart (pending bookings + abandoned trips)
  // Count pending bookings as carts
  filteredFlights.forEach((flight) => {
    if (flight.status === 'pending') {
      const flightDate = startOfDay(parseISO(flight.created_at || flight.createdAt));
      const dayIndex = allDaysInRange.findIndex(d => 
        d.fullDate.getTime() === flightDate.getTime()
      );
      
      if (dayIndex !== -1) {
        if (!allDaysInRange[dayIndex].carts) {
          allDaysInRange[dayIndex].carts = 0;
        }
        allDaysInRange[dayIndex].carts += 1;
      }
    }
  });
  
  // Count abandoned trips as carts
  filteredAbandonedTrips.forEach((trip) => {
    const tripDate = startOfDay(parseISO(trip.created_at || trip.createdAt));
    const dayIndex = allDaysInRange.findIndex(d => 
      d.fullDate.getTime() === tripDate.getTime()
    );
    
    if (dayIndex !== -1) {
      if (!allDaysInRange[dayIndex].carts) {
        allDaysInRange[dayIndex].carts = 0;
      }
      allDaysInRange[dayIndex].carts += 1;
    }
  });

  const cartsChartData = allDaysInRange.map(day => {
    cumulativeCarts += (day.carts || 0);
    return {
      date: day.date,
      carts: cumulativeCarts
    };
  });

  // Calculate cumulative users for chart
  // First, count users that existed before the time range (baseline)
  const usersBeforeRange = users.filter((user) => {
    try {
      const userDate = startOfDay(parseISO(user.created_at || user.createdAt));
      return userDate < startDate;
    } catch (e) {
      return false;
    }
  }).length;
  
  // Seed the cumulative counter with the baseline
  cumulativeUsers = usersBeforeRange;
  
  // Then add users within the selected range
  users.forEach((user) => {
    try {
      const userDate = startOfDay(parseISO(user.created_at || user.createdAt));
      const dayIndex = allDaysInRange.findIndex(d => 
        d.fullDate.getTime() === userDate.getTime()
      );
      
      if (dayIndex !== -1) {
        if (!allDaysInRange[dayIndex].users) {
          allDaysInRange[dayIndex].users = 0;
        }
        allDaysInRange[dayIndex].users += 1;
      }
    } catch (e) {
      // Skip invalid dates
    }
  });

  const usersChartData = allDaysInRange.map(day => {
    cumulativeUsers += (day.users || 0);
    return {
      date: day.date,
      users: cumulativeUsers
    };
  });

  // Non-cumulative (daily new) data for separate charts
  const newVolumeChartData = allDaysInRange.map(day => ({
    date: day.date,
    revenue: parseFloat(day.revenue.toFixed(2))
  }));

  const newCartsChartData = allDaysInRange.map(day => ({
    date: day.date,
    carts: day.carts || 0
  }));

  const newUsersChartData = allDaysInRange.map(day => ({
    date: day.date,
    users: day.users || 0
  }));

  // Bookings = count of bookings created per day (one per filtered flight).
  // Mirrors the Volume series but in count units rather than $.
  const newBookingsChartData = allDaysInRange.map(day => ({
    date: day.date,
    bookings: day.uploads || 0
  }));

  let cumulativeBookings = 0;
  const bookingsChartData = allDaysInRange.map(day => {
    cumulativeBookings += (day.uploads || 0);
    return {
      date: day.date,
      bookings: cumulativeBookings
    };
  });

  // Status breakdown (`cancelledTrips` is computed with the revenue split above,
  // so the pie chart and the headline's excluded-trips count can never diverge)
  const completedTrips = filteredFlights.filter((f) => statusOf(f) === 'completed').length;
  const defaultedTrips = filteredFlights.filter((f) => statusOf(f) === 'defaulted').length;
  
  const statusData = [
    { name: 'Pending', value: pendingTrips, color: '#7A8ABF' },
    { name: 'Confirmed', value: confirmedTrips, color: '#10b981' },
    { name: 'Completed', value: completedTrips, color: '#2A3868' },
    { name: 'Cancelled', value: cancelledTrips, color: '#ef4444' },
    { name: 'Defaulted', value: defaultedTrips, color: '#ea580c' }
  ].filter((item) => item.value > 0);

  // Prepare combined carts data (pending bookings + abandoned carts) for display
  const allCartsData = [
    // Pending bookings from bookings table
    ...filteredFlights
      .filter(f => f.status === 'pending')
      .map(trip => {
        const cartValue = parseFloat(trip.grand_total_with_hold || trip.grandTotalWithHold || 0);
        return {
          ...trip,
          type: 'booking',
          cartValue: Number.isFinite(cartValue) ? cartValue : 0,
          createdDate: parseISO(trip.created_at || trip.createdAt)
        };
      }),
    // Abandoned carts from abandoned_trips table
    ...filteredAbandonedTrips
      .filter(t => t.status !== 'confirmed' && t.status !== 'completed')
      .map(trip => {
        const flightCost = parseFloat(trip.total_amount || 0);
        const hotelCost = parseFloat(trip.stay_data ? (trip.stay_data.totalAmount || trip.stay_data.total_amount || 0) : 0);
        const totalValue = (Number.isFinite(flightCost) ? flightCost : 0) + (Number.isFinite(hotelCost) ? hotelCost : 0);
        return {
          ...trip,
          type: 'abandoned',
          cartValue: totalValue,
          createdDate: parseISO(trip.created_at || trip.createdAt)
        };
      })
  ].sort((a, b) => b.createdDate - a.createdDate); // Sort by newest first

  // Map data
  const todayString = format(new Date(), 'yyyy-MM-dd');
  const todaysFlights = filteredFlights.filter(flight => {
    if (!flight.departure_date) return false;
    // A cancelled trip's flight isn't departing today — keep it off the map.
    if (isCancelledOrRefunded(flight)) return false;
    try {
      const flightDate = format(parseISO(flight.departure_date), 'yyyy-MM-dd');
      return flightDate === todayString;
    } catch (e) {
      return false;
    }
  }).map(flight => {
    const fromCity = getCityName(flight.departure_city);
    const toCity = getCityName(flight.arrival_city);
    const fromCoords = cityCoordinates[fromCity];
    const toCoords = cityCoordinates[toCity];
    if (fromCoords && toCoords) {
      return { ...flight, fromCoords, toCoords, fromCity, toCity };
    }
    return null;
  }).filter(Boolean);

  const runCancellationAnalysis = async () => {
    setIsLoadingCancellationAnalysis(true);
    setCancellationAnalysisError(null);
    setCancellationAnalysis(null);
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setCancellationAnalysisError('Not authenticated');
        return;
      }
      const res = await fetch('/api/admin/cancellation-analysis', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCancellationAnalysisError(data.error || `Request failed (${res.status})`);
        return;
      }
      setCancellationAnalysis(data);
    } catch (err) {
      setCancellationAnalysisError(err?.message || 'Analysis failed');
    } finally {
      setIsLoadingCancellationAnalysis(false);
    }
  };

  const runAiProjection = async () => {
    setIsLoadingAiProjection(true);
    setAiProjectionError(null);
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setAiProjectionError('Not authenticated');
        return;
      }
      const res = await fetch('/api/admin/volume-projection', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          months: aiMonthlyHistory,
          futureMonths: aiFutureMonths,
          asOf: now.toISOString(),
          currentMonthDaysElapsed: daysElapsedInMonth,
          currentMonthDays: daysInMonth,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setAiProjectionError(data.error || `Request failed (${res.status})`);
        return;
      }
      setAiProjection(data);
    } catch (err) {
      setAiProjectionError(err?.message || 'Forecast failed');
    } finally {
      setIsLoadingAiProjection(false);
    }
  };

  const pulsingIcon = (color) => new L.DivIcon({
    className: 'pulsing-dot-container',
    html: `<div class="pulsing-dot" style="background-color: ${color};"></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6]
  });

  return (
    <div className="min-h-screen p-4 md:p-6 relative">
      <RoyalAmbientBackground />
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Robinhood-Style Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start"
        >
          <div className="min-w-0">
            <h1 className="text-2xl font-light text-white mb-2 tracking-tight">
              Analytics
            </h1>
            <p className="text-white/60 text-sm font-normal">
              Operations insights and performance metrics
            </p>
          </div>
          
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="bg-white/5 border-white/10 text-white w-full sm:w-44 font-normal rounded-xl hover:bg-white/10 transition-colors">
              <SelectValue className="text-white" />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-white/10">
              <SelectItem value="0" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Today</SelectItem>
              <SelectItem value="yesterday" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Yesterday</SelectItem>
              <SelectItem value="this_week" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">This Week</SelectItem>
              <SelectItem value="last_week" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Last Week</SelectItem>
              <SelectItem value="last_month" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Last Month</SelectItem>
              <SelectItem value="last_year" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Last Year</SelectItem>
              <SelectItem value="30" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Last 30 days</SelectItem>
              <SelectItem value="90" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Last 90 days</SelectItem>
              <SelectItem value="month_to_date" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Month to Date</SelectItem>
              <SelectItem value="year_to_date" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Year to Date</SelectItem>
              <SelectItem value="365" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Last 365 days</SelectItem>
              <SelectItem value="all" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">All Time</SelectItem>
            </SelectContent>
          </Select>
        </motion.div>

        {/* Large Revenue Display - Robinhood Style */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <div className="text-5xl md:text-6xl font-light text-white tracking-tight mb-3">
            ${formatCurrency(totalRevenue)}
          </div>
          
          <div className="flex items-center gap-2 md:gap-4 text-sm md:text-base font-normal mb-1">
            <div className="flex items-center text-green-400">
              <span className="mr-1">▲</span>
              {conversionRate}% trip conversion
            </div>
            <div className="flex items-center text-brand-400">
              <span className="mr-1">▲</span>
              {userConversionRate}% user conversion
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs md:text-sm font-normal text-white/40">
            <span>{totalTrips} trips</span>
            <span>•</span>
            <span>{confirmedTrips} confirmed</span>
            <span>•</span>
            <span>{totalCarts} carts</span>
            <span>•</span>
            <span>${formatCurrency(abandonedValue)} in carts</span>
            {/* The headline above is net of cancellations. Show what was taken
                out so the number reconciles on sight (net + cancelled = gross)
                rather than looking like revenue quietly went missing. */}
            {cancelledTrips > 0 && (
              <>
                <span>•</span>
                <span
                  className="text-red-300/70"
                  title={`Excluded from the headline: ${cancelledTrips} cancelled/refunded trip${cancelledTrips === 1 ? '' : 's'}. Gross booked value was $${formatCurrency(grossRevenue)}.`}
                >
                  {cancelledTrips} cancelled · ${formatCurrency(cancelledRevenue)} excluded
                </span>
              </>
            )}
            {/* The headline is BOOKED value. On installment plans the cash lands
                over the following months, so this is context, not a red flag —
                the tooltip says so rather than leaving it to be read as a gap. */}
            {collectedIsReady && (
              <>
                <span>•</span>
                <span
                  className="text-emerald-300/70"
                  title={`Cash actually collected so far on these trips (unified Stripe + NMI). The headline is booked value — what customers agreed to pay. The remaining $${formatCurrency(Math.max(0, totalRevenue - collectedCash))} is scheduled installments not yet due, which is how the plans are designed to work.`}
                >
                  ${formatCurrency(collectedCash)} collected ({collectedPct.toFixed(0)}%)
                </span>
              </>
            )}
          </div>
        </motion.div>

        {/* Where customers come from — channel, geography, and signup waves.
            Sits above Customer Rankings because acquisition is the top of the
            funnel that everything below it describes the outcome of. */}
        <AcquisitionGeography data={acquisitionData} loading={isLoading && !acquisitionData} />

        {/* Faye travel insurance: plans sold, take rate, and policies to buy. */}
        {(FAYE_INSURANCE_ENABLED || insurancePolicies?.length > 0) && (
          <TravelInsuranceSales
            policies={insurancePolicies}
            bookings={flights}
            startDate={startDate}
            endDate={endDate}
            loading={insuranceLoadState === 'loading' && !insurancePolicies}
            error={insuranceLoadState === 'error' ? 'the summary endpoint did not answer' : null}
          />
        )}

        {/* Customer Rankings & Conversion Timeline - Side by Side on Desktop */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="mb-12 grid grid-cols-1 lg:grid-cols-2 gap-6"
        >
          {/* Customer Rankings - Top spenders by volume and trips */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-white font-light text-xl tracking-tight">Customer Rankings</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setCustomerRankingView('volume')}
                  className={`px-3 py-1 text-xs rounded-lg transition-all ${
                    customerRankingView === 'volume'
                      ? 'bg-white/10 text-white border border-white/20'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 border border-white/10'
                  }`}
                >
                  By Volume
                </button>
                <button
                  onClick={() => setCustomerRankingView('trips')}
                  className={`px-3 py-1 text-xs rounded-lg transition-all ${
                    customerRankingView === 'trips'
                      ? 'bg-white/10 text-white border border-white/20'
                      : 'bg-white/5 text-white/60 hover:bg-white/10 border border-white/10'
                  }`}
                >
                  By Trips
                </button>
              </div>
            </div>
            <p className="text-white/50 text-sm font-normal mb-4">
              Top customers by {customerRankingView === 'volume' ? 'total spending' : 'number of trips'}
            </p>
            
            <div className="space-y-1 max-h-[600px] overflow-y-auto">
              {isLoadingRankings ? (
                <p className="text-white/40 text-sm font-normal text-center py-12">Loading rankings...</p>
              ) : (customerRankingView === 'volume' ? customerRankings.byVolume : customerRankings.byTrips).length === 0 ? (
                <p className="text-white/40 text-sm font-normal text-center py-12">No customer data yet</p>
              ) : (
                (customerRankingView === 'volume' ? customerRankings.byVolume : customerRankings.byTrips).slice(0, 20).map((customer, index) => (
                  <div 
                    key={customer.email || index}
                    className="flex items-center gap-3 p-3 hover:bg-white/5 transition-colors rounded-xl"
                  >
                    {/* Rank Indicator */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border ${
                      index === 0 ? 'bg-gradient-to-br from-brand-500/20 to-brand-500/20 border-brand-500/30' :
                      index === 1 ? 'bg-gradient-to-br from-gray-400/20 to-gray-500/20 border-gray-400/30' :
                      index === 2 ? 'bg-gradient-to-br from-orange-500/20 to-orange-600/20 border-orange-500/30' :
                      'bg-white/5 border-white/10'
                    }`}>
                      <span className={`text-sm font-medium ${
                        index === 0 ? 'text-brand-400' :
                        index === 1 ? 'text-gray-300' :
                        index === 2 ? 'text-orange-400' :
                        'text-white/60'
                      }`}>
                        {index + 1}
                      </span>
                    </div>
                    
                    {/* Customer Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-white text-sm font-normal truncate">
                          {customer.name || customer.email || 'Unknown'}
                        </p>
                        <VipBadge userIds={customer.userIds || null} email={customer.email || null} />
                        {/* Status Badge */}
                        {customer.status === 'blacklisted' && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-normal bg-red-500/20 text-red-400 border border-red-500/30">
                            Blacklisted
                          </span>
                        )}
                        {customer.status === 'high_cancellation' && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-normal bg-orange-500/20 text-orange-400 border border-orange-500/30">
                            High Cancellation
                          </span>
                        )}
                        {customer.status === 'good_standing' && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-normal bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                            Good Standing
                          </span>
                        )}
                      </div>
                      <p className="text-white/60 text-xs font-normal truncate">
                        {customer.email}
                      </p>
                      {customer.passengerNames && customer.passengerNames.length > 0 && customer.passengerNames.length !== 1 && (
                        <p className="text-white/40 text-xs font-normal">
                          {customer.passengerNames.length} travelers
                        </p>
                      )}
                      {customer.avgDaysBetweenTrips !== null && customer.avgDaysBetweenTrips !== undefined && (
                        <p className="text-white/40 text-xs font-normal">
                          Avg: {customer.avgDaysBetweenTrips} days between trips
                        </p>
                      )}
                    </div>
                    
                    {/* Stats */}
                    <div className="text-right flex-shrink-0">
                      {customerRankingView === 'volume' ? (
                        <>
                          <p className="text-white text-sm font-normal whitespace-nowrap">
                            ${formatCurrency(customer.totalSpent)}
                          </p>
                          <p className="text-emerald-400 text-xs font-normal whitespace-nowrap">
                            Profit: ${formatCurrency(customer.totalProfit || 0)}
                          </p>
                          <p className="text-white/60 text-xs font-normal whitespace-nowrap">
                            {customer.tripCount} {customer.tripCount === 1 ? 'trip' : 'trips'}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-white text-sm font-normal whitespace-nowrap">
                            {customer.tripCount} {customer.tripCount === 1 ? 'trip' : 'trips'}
                          </p>
                          <p className="text-emerald-400 text-xs font-normal whitespace-nowrap">
                            Profit: ${formatCurrency(customer.totalProfit || 0)}
                          </p>
                          <p className="text-white/60 text-xs font-normal whitespace-nowrap">
                            ${formatCurrency(customer.totalSpent)}
                          </p>
                        </>
                      )}
                      {customer.cancellationRate > 0 && (
                        <p className="text-orange-400 text-xs font-normal whitespace-nowrap">
                          {customer.cancellationRate}% cancelled
                        </p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Conversion Timeline - Same format as Recent Carts */}
          <div>
            <h2 className="text-white font-light text-xl mb-2 tracking-tight">Conversion Timeline</h2>
            <p className="text-white/50 text-sm font-normal mb-4">
              Average: {avgConversionDays === 0 ? 'Same day' : avgConversionDays === 1 ? '1 day' : `${avgConversionDays} days`}
            </p>
            <div className="space-y-1">
              {conversionTimelineData.length === 0 ? (
                <p className="text-white/40 text-sm font-normal text-center py-12">No bookings yet</p>
              ) : (
                <>
                  {conversionTimelineData.slice(0, conversionTimelineVisible).map((item, index) => (
                    <div 
                      key={item.id || index}
                      className="flex items-center gap-3 p-3 hover:bg-white/5 transition-colors rounded-xl"
                    >
                      {/* Airline Logo - Real or Fallback */}
                      {item.airlineLogo ? (
                        <img 
                          src={item.airlineLogo} 
                          alt={item.airlineCode}
                          className="w-8 h-8 rounded-lg object-contain flex-shrink-0 p-0.5"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`w-8 h-8 rounded-lg bg-black flex-shrink-0 items-center justify-center ${item.airlineLogo ? 'hidden' : 'flex'}`}
                        style={{ display: item.airlineLogo ? 'none' : 'flex' }}
                      >
                        <span className="text-white/60 text-xs font-normal">
                          {item.airlineCode}
                        </span>
                      </div>
                      
                      {/* Route Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-normal truncate">
                          {item.customTitle && item.customTitle.trim() !== '' 
                            ? item.customTitle.trim() 
                            : `${getCityName(item.originCode)} → ${getCityName(item.destCode)}`}
                        </p>
                        <p className="text-white/40 text-xs font-normal whitespace-nowrap">
                          {item.bookingDateFormatted}
                        </p>
                      </div>
                      
                      {/* Conversion Time */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-white text-sm font-normal whitespace-nowrap">
                          {item.conversionDays === 0 ? 'Same day' : 
                           item.conversionDays === 1 ? '1 day' : 
                           `${item.conversionDays} days`}
                        </p>
                      </div>
                    </div>
                  ))}
                  
                  {/* Show More Button - Only show if there are more trips to display */}
                  {conversionTimelineVisible < conversionTimelineData.length && (
                    <button
                      onClick={() => setConversionTimelineVisible(prev => prev + 10)}
                      className="w-full py-2 mt-2 text-white/60 hover:text-white text-sm font-normal transition-colors"
                    >
                      Show More
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </motion.div>

        {/* Volume Projections - Growth trajectory */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.14 }}
          className="mb-12"
        >
          <div className="flex items-start justify-between gap-4 mb-2">
            <h2 className="text-white font-light text-xl tracking-tight">Monthly Volume Projections</h2>
            <button
              onClick={runAiProjection}
              disabled={isLoadingAiProjection}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 font-normal text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingAiProjection ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Forecasting...
                </>
              ) : (
                <>
                  <Brain className="w-4 h-4" />
                  {aiProjection ? 'Re-run AI Forecast' : 'Forecast with AI'}
                </>
              )}
            </button>
          </div>
          <p className="text-white/50 text-sm font-normal mb-6">
            {aiProjection ? (
              <>
                AI forecast across {aiMonthlyHistory.length} months of history + cart/intent signals
                {aiProjection.analysis?.confidence && <> • {aiProjection.analysis.confidence} confidence</>}
              </>
            ) : (
              <>
                Projected month: ${avgMonthlyRevenue.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})} GMV • {avgMonthlySignups} new signups • Based on {daysElapsedInMonth}-day rate with diminishing returns (run AI for a deeper forecast)
              </>
            )}
          </p>

          {aiProjectionError && (
            <div className="mb-6 px-4 py-3 rounded-xl flex items-center gap-3 text-red-300 bg-red-500/10 border border-red-500/20">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm font-normal">{aiProjectionError}</p>
            </div>
          )}

          {/* Dual Charts: Revenue and Signups */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Revenue Projection Chart */}
            <div>
              <h3 className="text-white font-normal text-base mb-4 tracking-tight">Volume Projections (GMV)</h3>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={volumeProjectionData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="revenueProjectedGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.15}/>
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis 
                      dataKey="month" 
                      stroke="#ffffff30" 
                      style={{ fontSize: '11px', fontWeight: '400', fill: '#ffffff60' }}
                      tickLine={false}
                      axisLine={false}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      interval={0}
                    />
                    <YAxis 
                      stroke="#ffffff20"
                      style={{ fontSize: '11px', fontWeight: '400', fill: '#ffffff40' }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#000000f5', 
                        border: '1px solid #ffffff15',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontWeight: '500',
                        padding: '8px 12px'
                      }}
                      formatter={(value, name) => {
                        const formatted = `$${value.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}`;
                        if (name === 'revenue') return [formatted, 'Actual'];
                        if (name === 'revenueProjected') return [formatted, 'Projected'];
                        return [value, name];
                      }}
                      cursor={{ stroke: '#10b981', strokeWidth: 1, strokeDasharray: '3 3' }}
                    />
                    {/* Actual revenue - solid line */}
                    <Line 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#10b981" 
                      strokeWidth={2.5}
                      fill="url(#revenueGradient)"
                      dot={{ r: 4 }}
                      animationDuration={1500}
                      connectNulls={false}
                    />
                    {/* Projected revenue - dashed line */}
                    <Line 
                      type="monotone" 
                      dataKey="revenueProjected" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      fill="url(#revenueProjectedGradient)"
                      dot={{ r: 3 }}
                      animationDuration={1500}
                      connectNulls={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Signups Projection Chart */}
            <div>
              <h3 className="text-white font-normal text-base mb-4 tracking-tight">User Projections</h3>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={volumeProjectionData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="signupsGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2A3868" stopOpacity={0.3}/>
                        <stop offset="100%" stopColor="#2A3868" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="signupsProjectedGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2A3868" stopOpacity={0.15}/>
                        <stop offset="100%" stopColor="#2A3868" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis 
                      dataKey="month" 
                      stroke="#ffffff30" 
                      style={{ fontSize: '11px', fontWeight: '400', fill: '#ffffff60' }}
                      tickLine={false}
                      axisLine={false}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      interval={0}
                    />
                    <YAxis 
                      stroke="#ffffff20"
                      style={{ fontSize: '11px', fontWeight: '400', fill: '#ffffff40' }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#000000f5', 
                        border: '1px solid #ffffff15',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontWeight: '500',
                        padding: '8px 12px'
                      }}
                      formatter={(value, name) => {
                        if (name === 'signups') return [value.toLocaleString(), 'Actual'];
                        if (name === 'signupsProjected') return [value.toLocaleString(), 'Projected'];
                        return [value, name];
                      }}
                      cursor={{ stroke: '#2A3868', strokeWidth: 1, strokeDasharray: '3 3' }}
                    />
                    {/* Actual signups - solid line */}
                    <Line 
                      type="monotone" 
                      dataKey="signups" 
                      stroke="#2A3868" 
                      strokeWidth={2.5}
                      fill="url(#signupsGradient)"
                      dot={{ r: 4 }}
                      animationDuration={1500}
                      connectNulls={false}
                    />
                    {/* Projected signups - dashed line */}
                    <Line 
                      type="monotone" 
                      dataKey="signupsProjected" 
                      stroke="#2A3868" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      fill="url(#signupsProjectedGradient)"
                      dot={{ r: 3 }}
                      animationDuration={1500}
                      connectNulls={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* AI forecast insight panel */}
          {aiProjection?.analysis && (
            <div className="mt-6 rounded-xl border border-emerald-500/15 bg-emerald-500/[0.03] overflow-hidden">
              <div className="px-5 py-3 flex items-center gap-2 border-b border-white/5">
                <Brain className="w-4 h-4 text-emerald-300" />
                <span className="text-white font-normal text-sm tracking-tight">AI Forecast Analysis</span>
                {aiProjection.analysis.confidence && (
                  <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider ${
                    aiProjection.analysis.confidence === 'high' ? 'bg-emerald-500/20 text-emerald-400' :
                    aiProjection.analysis.confidence === 'medium' ? 'bg-brand-500/20 text-brand-400' :
                    'bg-white/10 text-white/50'
                  }`}>{aiProjection.analysis.confidence} confidence</span>
                )}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="p-5 space-y-4 border-b lg:border-b-0 lg:border-r border-white/5">
                  {aiProjection.analysis.executive_summary && (
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-white/40 font-normal mb-1.5">Summary</p>
                      <p className="text-white/90 text-sm font-normal leading-relaxed">{aiProjection.analysis.executive_summary}</p>
                    </div>
                  )}
                  {aiProjection.analysis.methodology && (
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-white/40 font-normal mb-1.5">Methodology</p>
                      <p className="text-white/70 text-xs font-normal leading-relaxed">{aiProjection.analysis.methodology}</p>
                    </div>
                  )}
                </div>
                <div className="p-5">
                  {aiProjection.analysis.key_drivers?.length > 0 && (
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-white/40 font-normal mb-2">Key Drivers</p>
                      <ul className="space-y-1.5">
                        {aiProjection.analysis.key_drivers.map((d, i) => (
                          <li key={i} className="flex items-start gap-2 text-white/80 text-xs font-normal leading-relaxed">
                            <span className="text-emerald-400/70 mt-0.5">•</span>
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Financial Breakdown - Balance Sheet Style */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-white font-light text-xl tracking-tight">Financial Breakdown</h2>
            <div className="flex rounded-lg bg-white/5 border border-white/10 p-0.5">
              <button
                onClick={() => setFinancialBreakdownTab('defera')}
                className={`px-3 py-1.5 rounded-md text-sm font-normal transition-colors ${
                  financialBreakdownTab === 'defera'
                    ? 'bg-white/10 text-white'
                    : 'text-white/60 hover:text-white/80'
                }`}
              >
                Defera
              </button>
              <button
                onClick={() => setFinancialBreakdownTab('slicepay')}
                className={`px-3 py-1.5 rounded-md text-sm font-normal transition-colors ${
                  financialBreakdownTab === 'slicepay'
                    ? 'bg-white/10 text-white'
                    : 'text-white/60 hover:text-white/80'
                }`}
              >
                {ic.analyticsPartnerName}
              </button>
            </div>
          </div>
          
          <div className="space-y-1">
            {financialBreakdownTab === 'defera' ? (
            <>
            {/* REVENUE SECTION */}
            <div className="py-2 border-b border-white/5">
              <p className="text-white/60 text-xs font-normal mb-3 uppercase tracking-wider">Revenue</p>
              
              {/* Airfare Revenue */}
              <div className="mb-3">
                <p className="text-white/50 text-xs font-normal mb-1.5 uppercase tracking-wide">Airfare</p>
                <div className="space-y-0.5 ml-2">
                  <div className="flex justify-between items-center py-0.5">
                    <span className="text-white/40 font-normal text-sm whitespace-nowrap">Base Fares</span>
                    <span className="text-white/60 font-normal text-sm whitespace-nowrap">
                      ${formatCurrency(airfareBaseFares)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-0.5">
                    <span className="text-white/40 font-normal text-sm whitespace-nowrap">Service Fees</span>
                    <span className="text-white/60 font-normal text-sm whitespace-nowrap">
                      ${formatCurrency(airfareServiceFees)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-0.5">
                    <span className="text-white/40 font-normal text-sm whitespace-nowrap">Hold Fees (15%)</span>
                    <span className="text-white/60 font-normal text-sm whitespace-nowrap">
                      ${formatCurrency(airfareHoldFees)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-0.5 border-t border-white/5 mt-1 pt-1">
                    <span className="text-white/60 font-normal text-sm whitespace-nowrap">Total</span>
                    <span className="text-white font-normal text-sm whitespace-nowrap">
                      ${formatCurrency(airfareRevenueTotal)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Stays Revenue */}
              {staysBaseCosts > 0 && (
                <div className="mb-2">
                  <p className="text-white/50 text-xs font-normal mb-1.5 uppercase tracking-wide">Stays</p>
                  <div className="space-y-0.5 ml-2">
                    <div className="flex justify-between items-center py-0.5">
                      <span className="text-white/40 font-normal text-sm whitespace-nowrap">Stays</span>
                      <span className="text-white/60 font-normal text-sm whitespace-nowrap">
                        ${formatCurrency(staysBaseCosts)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-0.5">
                      <span className="text-white/40 font-normal text-sm whitespace-nowrap">Service Fees</span>
                      <span className="text-white/60 font-normal text-sm whitespace-nowrap">
                        ${formatCurrency(staysServiceFees)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-0.5">
                      <span className="text-white/40 font-normal text-sm whitespace-nowrap">Hold Fees (15%)</span>
                      <span className="text-white/60 font-normal text-sm whitespace-nowrap">
                        ${formatCurrency(staysHoldFees)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-0.5 border-t border-white/5 mt-1 pt-1">
                      <span className="text-white/60 font-normal text-sm whitespace-nowrap">Total</span>
                      <span className="text-white font-normal text-sm whitespace-nowrap">
                        ${formatCurrency(staysRevenueTotal)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Skip Payment Fees — $20-per-skip revenue from the Skip Upcoming Payment
                  feature. Independent one-off charges, so they sit outside Airfare/Stays
                  and add directly to Total Revenue. */}
              {(skipFeesRevenue > 0 || seatPurchaseNetRevenue > 0) && (
                <div className="mb-2">
                  <p className="text-white/50 text-xs font-normal mb-1.5 uppercase tracking-wide">Fees</p>
                  <div className="space-y-0.5 ml-2">
                    {skipFeesRevenue > 0 && (
                      <div className="flex justify-between items-center py-0.5">
                        <span className="text-white/40 font-normal text-sm whitespace-nowrap">Skip Fees</span>
                        <span className="text-white/60 font-normal text-sm whitespace-nowrap">
                          ${formatCurrency(skipFeesRevenue)}
                        </span>
                      </div>
                    )}
                    {/* Net of the airline's seat cost — the label says so, because the
                        customer paid materially more than this and the difference is a
                        pass-through, not margin. */}
                    {seatPurchaseNetRevenue > 0 && (
                      <div className="flex justify-between items-center py-0.5">
                        <span className="text-white/40 font-normal text-sm whitespace-nowrap">Seat Fees (net)</span>
                        <span className="text-white/60 font-normal text-sm whitespace-nowrap">
                          ${formatCurrency(seatPurchaseNetRevenue)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Revenue Grand Total */}
              <div className="flex justify-between items-center py-1 border-t border-white/10 mt-2 pt-2">
                <span className="text-white font-normal text-sm whitespace-nowrap uppercase tracking-wide">Total Revenue</span>
                <span className="text-green-400 font-normal text-base whitespace-nowrap">
                  ${formatCurrency(revenueGrandTotal)}
                </span>
              </div>
            </div>
            
            {/* EXPENSES SECTION */}
            <div className="py-2 border-b border-white/5">
              <p className="text-white/60 text-xs font-normal mb-3 uppercase tracking-wider">Expenses</p>
              
              {/* Airfare Expenses */}
              <div className="mb-3">
                <p className="text-white/50 text-xs font-normal mb-1.5 uppercase tracking-wide">Airfare</p>
                <div className="space-y-0.5 ml-2">
                  <div className="flex justify-between items-center py-0.5">
                    <span className="text-white/40 font-normal text-sm whitespace-nowrap">Base Costs</span>
                    <span className="text-white/60 font-normal text-sm whitespace-nowrap">
                      ${formatCurrency(airfareBaseCosts)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-0.5">
                    <span className="text-white/40 font-normal text-sm whitespace-nowrap">Investor Fees</span>
                    <span className="text-white/60 font-normal text-sm whitespace-nowrap">
                      ${formatCurrency(airfareInvestorFees)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-0.5">
                    <span className="text-white/40 font-normal text-sm whitespace-nowrap">Defera Capital</span>
                    <span className="text-white/60 font-normal text-sm whitespace-nowrap">
                      ${formatCurrency(airfareBackerFees)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-0.5 border-t border-white/5 mt-1 pt-1">
                    <span className="text-white/60 font-normal text-sm whitespace-nowrap">Total</span>
                    <span className="text-white font-normal text-sm whitespace-nowrap">
                      ${formatCurrency(airfareExpensesTotal)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Stays Expenses */}
              {staysBaseCostsForExpense > 0 && (
                <div className="mb-2">
                  <p className="text-white/50 text-xs font-normal mb-1.5 uppercase tracking-wide">Stays</p>
                  <div className="space-y-0.5 ml-2">
                    <div className="flex justify-between items-center py-0.5">
                      <span className="text-white/40 font-normal text-sm whitespace-nowrap">Stays</span>
                      <span className="text-white/60 font-normal text-sm whitespace-nowrap">
                        ${formatCurrency(staysBaseCostsForExpense)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-0.5">
                      <span className="text-white/40 font-normal text-sm whitespace-nowrap">Investor Fees</span>
                      <span className="text-white/60 font-normal text-sm whitespace-nowrap">
                        ${formatCurrency(staysInvestorFees)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-0.5">
                      <span className="text-white/40 font-normal text-sm whitespace-nowrap">Defera Capital</span>
                      <span className="text-white/60 font-normal text-sm whitespace-nowrap">
                        ${formatCurrency(staysBackerFees)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-0.5 border-t border-white/5 mt-1 pt-1">
                      <span className="text-white/60 font-normal text-sm whitespace-nowrap">Total</span>
                      <span className="text-white font-normal text-sm whitespace-nowrap">
                        ${formatCurrency(staysExpensesTotal)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Miles Liability */}
              {milesLiabilityCost > 0 && (
                <div className="mb-2">
                  <p className="text-brand-400/80 text-xs font-normal mb-1.5 uppercase tracking-wide">Rewards Liability</p>
                  <div className="space-y-0.5 ml-2">
                    <div className="flex justify-between items-center py-0.5">
                      <span className="text-brand-400/60 font-normal text-sm whitespace-nowrap">{milesEarnedTotal.toLocaleString()} Miles Earned</span>
                      <span className="text-brand-400/80 font-normal text-sm whitespace-nowrap">
                        ${formatCurrency(milesLiabilityCost)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-0.5 text-xs">
                      <span className="text-white/30 font-normal whitespace-nowrap">({MILES_LIABILITY_CENTS_PER_MILE}¢ per mile)</span>
                      {/* A Mile's liability as a share of a dollar, from the same rate. */}
                      <span className="text-white/40 font-normal whitespace-nowrap">
                        {MILES_LIABILITY_CENTS_PER_MILE}% liability
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Processing Fees */}
              {(stripeProcessingFees > 0 || nmiProcessingFees > 0) && (
                <div className="mb-2">
                  <p className="text-white/50 text-xs font-normal mb-1.5 uppercase tracking-wide">Payment Processing</p>
                  <div className="space-y-0.5 ml-2">
                    {stripeProcessingFees > 0 && (
                      <div className="flex justify-between items-center py-0.5">
                        <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                          Stripe Fees
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                                <Info className="w-3 h-3" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                              Estimated Stripe processing cost across {stripeProcessingFeeBookings} Defera Capital (Stripe track), Defera Zero and Reserve booking{stripeProcessingFeeBookings === 1 ? '' : 's'}: 2.9% + $0.30 per transaction, plus 1.5% for instant transfers — 4.4% effective rate on volume. Defera Capital / Zero: deposit + ~5 installment charges per booking. Reserve: 2 charges per booking. NMI-track Defera Capital bookings are shown separately. Slice Pay-era and Miles bookings are excluded.
                            </PopoverContent>
                          </Popover>
                        </span>
                        <span className="text-red-400/80 font-normal text-sm whitespace-nowrap">
                          ${formatCurrency(stripeProcessingFees)}
                        </span>
                      </div>
                    )}
                    {nmiProcessingFees > 0 && (
                      <div className="flex justify-between items-center py-0.5">
                        <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                          NMI Fees
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                                <Info className="w-3 h-3" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                              Estimated NMI (Vector Payments) processing cost across {nmiProcessingFeeBookings} Defera Capital booking{nmiProcessingFeeBookings === 1 ? '' : 's'} on the NMI processor track: 3.95% of volume. No Stripe-style rolling reserve holds apply to NMI settlements.
                            </PopoverContent>
                          </Popover>
                        </span>
                        <span className="text-red-400/80 font-normal text-sm whitespace-nowrap">
                          ${formatCurrency(nmiProcessingFees)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Slice Pay Payouts (legacy installments era) */}
              {slicePayHoldFeesExpense > 0 && (
                <div className="mb-2">
                  <p className="text-white/50 text-xs font-normal mb-1.5 uppercase tracking-wide">Slice Pay Payouts</p>
                  <div className="space-y-0.5 ml-2">
                    <div className="flex justify-between items-center py-0.5">
                      <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                        Hold Fees
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                              <Info className="w-3 h-3" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-72 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                            Hold fees Defera collected from customers on {slicePayHoldFeesCount} legacy installments-era trip{slicePayHoldFeesCount === 1 ? '' : 's'} (pre-April 2026, before Defera Capital launched). These were passed through to Slice Pay as their loan-origination fee — counted as revenue on the way in, so they net to $0 here. Defera Capital bookings (Apr 2026+) keep the hold fee as real revenue and are excluded. Cancelled trips are excluded too.
                          </PopoverContent>
                        </Popover>
                      </span>
                      <span className="text-red-400/80 font-normal text-sm whitespace-nowrap">
                        ${formatCurrency(slicePayHoldFeesExpense)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* After-the-fact Adjustments */}
              {/* Gate the whole block on paid-amount data being loaded: Default
                  Losses and Cancellation Refunds are computed against actual
                  customer payments (bookingsPaidCents — unified Stripe + NMI),
                  which starts as an empty map on first render. Showing them
                  before the fetch returns would surface a phantom "we lost
                  everything the customer was billed" figure, so wait until
                  we have real numbers. */}
              {(() => {
                const isPaidLoading = paidLoadState === 'idle' || paidLoadState === 'loading';
                const isPaidReady = !isPaidLoading && paidLoadState !== 'unavailable' && paidLoadState !== 'error';
                const shouldShowSection = isPaidReady && (
                  defaultLossesExpense > 0
                  || cancellationRefundsExpense > 0
                  || manualRefundsOutsideCancellation > 0
                );
                if (!shouldShowSection) return null;
                return (
                <div className="mb-2">
                  <p className="text-white/50 text-xs font-normal mb-1.5 uppercase tracking-wide">Adjustments</p>
                  <div className="space-y-0.5 ml-2">
                    {defaultLossesExpense > 0 && (
                      <div className="flex justify-between items-center py-0.5">
                        <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                          Default Losses
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                                <Info className="w-3 h-3" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                              Customer billing on real defaulted trips (with confirmation #) minus what we actually recovered — sourced from live customer payments across BOTH Stripe (Defera Capital Stripe track, Defera Zero, Reserve) AND NMI/Vector (Defera Capital NMI track). Writes off gross billed revenue we counted but won't actually collect. Higher than Total Defaulted on the Defera Capital tab because it also writes off the service fee + hold fee slice (both were booked as revenue upfront), whereas Total Defaulted only counts unrecovered capital.
                            </PopoverContent>
                          </Popover>
                        </span>
                        <span className="text-red-400/80 font-normal text-sm whitespace-nowrap">
                          ${formatCurrency(defaultLossesExpense)}
                        </span>
                      </div>
                    )}
                    {cancellationRefundsExpense > 0 && (
                      <div className="flex justify-between items-center py-0.5">
                        <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                          Cancellation Refunds
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                                <Info className="w-3 h-3" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                              What customers actually paid on cancelled/refunded trips AND got back as cash (sourced from Stripe and NMI/Vector — both processors are unified in the paid-amounts feed — or an admin-entered "Amount Refunded" override on the booking when set). These amounts were returned to the customer, so they reverse the corresponding revenue. Excludes cash that was converted to Defera Credit on unbooked-trip cancels — that portion stays with Defera and is surfaced under P&amp;L Adjustments → Profit from Cancellation Credit.
                            </PopoverContent>
                          </Popover>
                        </span>
                        <span className="text-red-400/80 font-normal text-sm whitespace-nowrap">
                          ${formatCurrency(cancellationRefundsExpense)}
                        </span>
                      </div>
                    )}
                    {manualRefundsOutsideCancellation > 0 && (
                      <div className="flex justify-between items-center py-0.5">
                        <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                          Manual Refunds
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                                <Info className="w-3 h-3" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                              Cash refunds an admin manually entered in "Amount Refunded" (Edit Flight Details) on trips that aren't cancelled/refunded — e.g. a goodwill refund on an active or completed trip.
                            </PopoverContent>
                          </Popover>
                        </span>
                        <span className="text-red-400/80 font-normal text-sm whitespace-nowrap">
                          ${formatCurrency(manualRefundsOutsideCancellation)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                );
              })()}

              {/* Expenses Grand Total */}
              <div className="flex justify-between items-center py-1 border-t border-white/10 mt-2 pt-2">
                <span className="text-white font-normal text-sm whitespace-nowrap uppercase tracking-wide">Total Expenses</span>
                <span className="text-red-400 font-normal text-base whitespace-nowrap">
                  ${formatCurrency(expensesGrandTotal)}
                </span>
              </div>
            </div>
            
            {/* P&L ADJUSTMENTS — same line items as Defera Capital Capital Deployed & Recovered */}
            {(lossPreventedAmount > 0 || cancellationCreditAmount > 0 || creditRedeemedAmount > 0 || accruedProfitFromFareAmount > 0 || accruedLossFromFareAmount > 0 || lossPreventedReference > 0) && (
              <div className="py-2 border-b border-white/5">
                <p className="text-white/60 text-xs font-normal mb-3 uppercase tracking-wider">P&amp;L Adjustments</p>
                <div className="space-y-0.5 ml-2">
                  {lossPreventedAmount > 0 && (
                    <div className="flex justify-between items-center py-0.5">
                      <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                        Profit from Loss
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                              <Info className="w-3 h-3" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-72 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                            Customer payments collected on {phantomDefaultedCount} defaulted trip{phantomDefaultedCount === 1 ? '' : 's'} that were never actually booked (no confirmation number on file). Since no funds were paid out to airlines or hotels, these amounts are recognized as profit instead of losses.
                          </PopoverContent>
                        </Popover>
                      </span>
                      <span className="text-green-400/80 font-normal text-sm whitespace-nowrap">
                        ${formatCurrency(lossPreventedAmount)}
                      </span>
                    </div>
                  )}
                  {cancellationCreditAmount > 0 && (
                    <div className="flex justify-between items-center py-0.5">
                      <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                        Profit from Cancellation Credit
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                              <Info className="w-3 h-3" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-80 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                            Customer cash on {cancellationCreditCount} cancelled trip{cancellationCreditCount === 1 ? '' : 's'} that were never booked with the airline/hotel and were refunded as Defera Credit instead of cash. Since no funds were paid out to airlines or hotels, the cash stays with Defera and is recognized as profit (analogous to Profit from Loss). The Defera Credit issued is a future-use liability — it's realized as a revenue offset when the customer redeems it on a future trip via the Credit Redeemed line below. Cancellation Refunds excludes this amount to avoid double-counting.
                          </PopoverContent>
                        </Popover>
                      </span>
                      <span className="text-green-400/80 font-normal text-sm whitespace-nowrap">
                        ${formatCurrency(cancellationCreditAmount)}
                      </span>
                    </div>
                  )}
                  {creditRedeemedAmount > 0 && (
                    <div className="flex justify-between items-center py-0.5">
                      <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                        Credit Redeemed
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                              <Info className="w-3 h-3" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-80 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                            Defera Credit committed at checkout across {creditRedeemedCount} booking{creditRedeemedCount === 1 ? '' : 's'} (deposit credit + total installment credit). Booking revenue is recorded at gross sticker price, but this slice wasn't new cash — it was a previously-issued Defera Credit being burned down. Subtracted here so the new trip's recognised profit collapses to its real service-fee + hold margin (minus processing/investor/backer fees). Pairs with Profit from Cancellation Credit on the issue side: over a full credit lifetime they net to $0.
                          </PopoverContent>
                        </Popover>
                      </span>
                      <span className="text-red-400/80 font-normal text-sm whitespace-nowrap">
                        ${formatCurrency(creditRedeemedAmount)}
                      </span>
                    </div>
                  )}
                  {accruedProfitFromFareAmount > 0 && (
                    <div className="flex justify-between items-center py-0.5">
                      <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                        Profit from Fare
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                              <Info className="w-3 h-3" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-72 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                            Accrued profit (as if every trip pays off in full) on {accruedProfitFromFareCount} trip{accruedProfitFromFareCount === 1 ? '' : 's'} where the customer's locked-in Base Fare is higher than what Defera will pay (or paid) the airline. Includes (a) confirmed trips with a lower Actual Cost and (b) unconfirmed trips whose flight was changed to a cheaper fare before booking. Formula: Customer Base Fare − Actual / New Base Fare (only counted when positive). Already embedded in Net Profit as (Base Fares − Base Costs); shown here for transparency.
                          </PopoverContent>
                        </Popover>
                      </span>
                      <span className="text-green-400/80 font-normal text-sm whitespace-nowrap">
                        ${formatCurrency(accruedProfitFromFareAmount)}
                      </span>
                    </div>
                  )}
                  {accruedLossFromFareAmount > 0 && (
                    <div className="flex justify-between items-center py-0.5">
                      <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                        Loss from Fare
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                              <Info className="w-3 h-3" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-72 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                            Accrued loss (as if every trip pays off in full) on {accruedLossFromFareCount} trip{accruedLossFromFareCount === 1 ? '' : 's'} where Defera will pay (or paid) the airline more than the customer's locked-in Base Fare. Includes (a) confirmed trips with a higher Actual Cost and (b) unconfirmed trips whose flight was changed to a costlier fare before booking. Formula: Customer Base Fare − Actual / New Base Fare (only counted when negative, shown as absolute amount). Already embedded in Net Profit as (Base Costs − Base Fares); shown here for transparency.
                          </PopoverContent>
                        </Popover>
                      </span>
                      <span className="text-red-400/80 font-normal text-sm whitespace-nowrap">
                        ${formatCurrency(accruedLossFromFareAmount)}
                      </span>
                    </div>
                  )}
                  {lossPreventedReference > 0 && (
                    <div className="flex justify-between items-center py-0.5">
                      <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                        Loss Prevented
                        <Popover>
                          <PopoverTrigger asChild>
                            <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                              <Info className="w-3 h-3" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-72 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                            Reference only — not used in any Net or profit calculation. Sum of Base Fare minus customer-paid amount across phantom defaulted trips. Represents the loss Defera would have absorbed if these trips had actually been booked with the airline.
                          </PopoverContent>
                        </Popover>
                      </span>
                      <span className="text-white/40 font-normal text-sm whitespace-nowrap italic">
                        ${formatCurrency(lossPreventedReference)}
                      </span>
                    </div>
                  )}
                  {(accruedProfitFromFareAmount > 0 || accruedLossFromFareAmount > 0) && (
                    <div className="flex justify-between items-center py-0.5 border-t border-white/5 mt-1 pt-1">
                      <span className="text-white/60 font-normal text-sm whitespace-nowrap">Net Fare Impact</span>
                      <span className={`font-normal text-sm whitespace-nowrap ${(accruedProfitFromFareAmount - accruedLossFromFareAmount) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {(accruedProfitFromFareAmount - accruedLossFromFareAmount) >= 0 ? '+' : '−'}${formatCurrency(Math.abs(accruedProfitFromFareAmount - accruedLossFromFareAmount))}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TOTAL SECTION */}
            <div className="py-2">
              <p className="text-white/60 text-xs font-normal mb-3 uppercase tracking-wider">Total</p>
              <div className="space-y-1.5">
                <div className="flex justify-between items-center py-0.5">
                  <span className="text-white/60 font-normal text-sm whitespace-nowrap">Total Revenue</span>
                  <span className="text-green-400 font-normal text-sm whitespace-nowrap">
                    ${formatCurrency(revenueGrandTotal)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-0.5">
                  <span className="text-white/60 font-normal text-sm whitespace-nowrap">Total Expenses</span>
                  <span className="text-red-400 font-normal text-sm whitespace-nowrap">
                    ${formatCurrency(expensesGrandTotal)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-t border-white/10 mt-2 pt-2">
                  <span className="text-white font-normal text-base whitespace-nowrap uppercase tracking-wide">Net Profit</span>
                  <span className={`font-light text-lg whitespace-nowrap ${netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    ${formatCurrency(Math.abs(netProfit))}
                  </span>
                </div>
              </div>
            </div>

            {/* Future Cash & Net Profit Projection — when accrued cash actually lands */}
            {futureFreeCashByMonth.length > 0 && (() => {
              // Running balance accumulates the per-month Net Profit (net cash flow
              // after Stripe/investor/backer and unbooked-airfare outflows) — the
              // cash pile growing/shrinking from today forward.
              let running = 0;
              const rows = futureFreeCashByMonth.map(({ monthKey, cash, net }) => {
                running += net;
                return { monthKey, cash, net, running };
              });
              const minRunning = activeProjection.runningMin;
              const minRunningMonth = activeProjection.runningMinMonth;
              const minRunningLabel = minRunningMonth
                ? (() => {
                    const [y, m] = minRunningMonth.split('-');
                    return format(new Date(Number(y), Number(m) - 1, 1), 'MMM yyyy');
                  })()
                : null;
              return (
                <div className="py-2 border-t border-white/5 mt-2">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <p className="text-white/60 text-xs font-normal uppercase tracking-wider">
                      Projected Cash & Net Profit by Month
                      {isWhatIfActive && (
                        <span className="ml-2 text-brand-300/80 normal-case tracking-normal">(what-if scenario)</span>
                      )}
                    </p>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="text-white/40 hover:text-white/80 text-xs font-normal uppercase tracking-wide border border-white/10 rounded px-2 py-1">
                          {isWhatIfActive ? 'Edit scenario' : 'What-if scenario'}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 bg-[#111] border-white/20 text-white text-xs font-normal p-4" align="end">
                        <div className="space-y-3">
                          <div>
                            <p className="text-white/60 uppercase tracking-wider mb-1.5">Service Fee schedule</p>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => setWhatIfServiceFeeMode('current')}
                                className={`flex-1 py-1.5 px-2 rounded border text-xs ${whatIfServiceFeeMode === 'current' ? 'border-white/60 bg-white/10 text-white' : 'border-white/10 text-white/50 hover:text-white/70'}`}
                              >
                                Current (10% / 8%)
                              </button>
                              <button
                                type="button"
                                onClick={() => setWhatIfServiceFeeMode('flat8AboveTwoHundred')}
                                className={`flex-1 py-1.5 px-2 rounded border text-xs ${whatIfServiceFeeMode === 'flat8AboveTwoHundred' ? 'border-brand-300/60 bg-brand-300/10 text-brand-100' : 'border-white/10 text-white/50 hover:text-white/70'}`}
                              >
                                Flat 8% ≥ $200
                              </button>
                            </div>
                          </div>
                          <div>
                            <p className="text-white/60 uppercase tracking-wider mb-1.5">New bookings per month</p>
                            <input
                              type="number"
                              min="0"
                              max="500"
                              step="1"
                              value={whatIfNewBookingsPerMonth}
                              onChange={(e) => setWhatIfNewBookingsPerMonth(Math.max(0, parseInt(e.target.value, 10) || 0))}
                              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white tabular-nums focus:outline-none focus:border-white/40"
                            />
                            <p className="text-white/40 text-[10px] mt-1">Layered on top of existing book — airfare hits cash at booking month, installments come back in.</p>
                          </div>
                          <div>
                            <p className="text-white/60 uppercase tracking-wider mb-1.5">Avg base fare ($)</p>
                            <input
                              type="number"
                              min="0"
                              max="20000"
                              step="50"
                              value={whatIfNewBookingsAvgBase}
                              onChange={(e) => setWhatIfNewBookingsAvgBase(Math.max(0, parseFloat(e.target.value) || 0))}
                              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white tabular-nums focus:outline-none focus:border-white/40"
                            />
                            <p className="text-white/40 text-[10px] mt-1">Recent avg: $1,335 (April–May DC/DZ).</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setWhatIfServiceFeeMode('current');
                              setWhatIfNewBookingsPerMonth(0);
                            }}
                            className="w-full text-white/50 hover:text-white/80 text-xs py-1 border-t border-white/10 pt-2 mt-1"
                          >
                            Reset to current book
                          </button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Min Cash Floor — answers "how low does my running balance dip?" */}
                  <div className="mb-3 flex flex-wrap gap-x-6 gap-y-1 text-xs font-normal">
                    <div className="flex items-center gap-2">
                      <span className="text-white/40 uppercase tracking-wide">Min Cash Floor:</span>
                      <span className={`tabular-nums ${minRunning >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {minRunning >= 0 ? '+' : '−'}${formatCurrency(Math.abs(minRunning))}
                      </span>
                      {minRunningLabel && minRunning < 0 && (
                        <span className="text-white/40">@ {minRunningLabel}</span>
                      )}
                      <Popover>
                        <PopoverTrigger asChild>
                          <button type="button" className="text-white/40 hover:text-white/60 p-0.5 align-middle inline-flex" aria-label="Info">
                            <Info className="w-3 h-3" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                          The deepest point the running balance hits before recovering. Defera's working capital plus this number must stay ≥ $0 to keep funding new flight bookings. A negative floor means you'd need to dip into cash on hand by that amount during the projection window.
                        </PopoverContent>
                      </Popover>
                    </div>
                    {isWhatIfActive && (
                      <div className="flex items-center gap-2">
                        <span className="text-white/40 uppercase tracking-wide">vs Current:</span>
                        <span className={`tabular-nums ${(activeProjection.runningEnd - currentProjection.runningEnd) >= 0 ? 'text-green-400/80' : 'text-red-400/80'}`}>
                          {(activeProjection.runningEnd - currentProjection.runningEnd) >= 0 ? '+' : '−'}${formatCurrency(Math.abs(activeProjection.runningEnd - currentProjection.runningEnd))}
                        </span>
                        <span className="text-white/40">end-of-projection net</span>
                      </div>
                    )}
                  </div>

                  {/* Single grid spanning header + body + total so all four columns share widths.
                      Mobile: content-sized columns + tight gaps + short headers so the four
                      columns fit a 375px phone; the overflow-x-auto wrapper scrolls the grid
                      inside the card (never the page) if a value still doesn't fit. */}
                  <div className="overflow-x-auto">
                  <div className="grid grid-cols-[minmax(auto,1fr)_auto_auto_auto] sm:grid-cols-[minmax(0,1fr)_minmax(7rem,auto)_minmax(7rem,auto)_minmax(7rem,auto)] gap-x-2 sm:gap-x-12 items-center">
                    {/* Header row */}
                    <span className="text-white/30 text-xs font-normal uppercase tracking-wide pb-2"></span>
                    <span className="text-white/40 text-xs font-normal uppercase tracking-wide text-right pb-2"><span className="sm:hidden">Cash In</span><span className="hidden sm:inline">Incoming Cash</span></span>
                    <span className="text-white/40 text-xs font-normal uppercase tracking-wide text-right pb-2">Net Profit</span>
                    <span className="text-white/40 text-xs font-normal uppercase tracking-wide text-right pb-2"><span className="sm:hidden">Balance</span><span className="hidden sm:inline">Running Balance</span></span>

                    {/* Body rows */}
                    {rows.map(({ monthKey, cash, net, running: runningAtMonth }) => {
                      const [y, m] = monthKey.split('-');
                      const monthDate = new Date(Number(y), Number(m) - 1, 1);
                      const label = format(monthDate, 'MMM yyyy');
                      return (
                        <React.Fragment key={monthKey}>
                          <span className="text-white/60 font-normal text-sm whitespace-nowrap py-0.5 pl-2">{label}</span>
                          <span className={`font-normal text-sm whitespace-nowrap text-right py-0.5 tabular-nums ${cash >= 0 ? 'text-green-400/80' : 'text-red-400/80'}`}>
                            {cash < 0 ? '−' : ''}${formatCurrency(Math.abs(cash))}
                          </span>
                          <span className={`font-normal text-sm whitespace-nowrap text-right py-0.5 tabular-nums ${net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {net < 0 ? '−' : ''}${formatCurrency(Math.abs(net))}
                          </span>
                          <span className={`font-normal text-sm whitespace-nowrap text-right py-0.5 tabular-nums ${runningAtMonth >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {runningAtMonth < 0 ? '−' : ''}${formatCurrency(Math.abs(runningAtMonth))}
                          </span>
                        </React.Fragment>
                      );
                    })}

                    {/* Total row (border drawn via classes on each cell) */}
                    <span className="text-white/80 font-normal text-sm whitespace-nowrap uppercase tracking-wide py-1 mt-2 pt-2 border-t border-white/10"><span className="sm:hidden">Total</span><span className="hidden sm:inline">Total Projected</span></span>
                    <span className={`font-normal text-sm whitespace-nowrap text-right py-1 mt-2 pt-2 border-t border-white/10 tabular-nums ${futureCashInflowTotal >= 0 ? 'text-green-400/80' : 'text-red-400/80'}`}>
                      {futureCashInflowTotal < 0 ? '−' : ''}${formatCurrency(Math.abs(futureCashInflowTotal))}
                    </span>
                    <span className={`font-normal text-sm whitespace-nowrap text-right py-1 mt-2 pt-2 border-t border-white/10 tabular-nums ${futureFreeCashTotal >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {futureFreeCashTotal < 0 ? '−' : ''}${formatCurrency(Math.abs(futureFreeCashTotal))}
                    </span>
                    <span className="text-white/30 font-normal text-sm whitespace-nowrap text-right py-1 mt-2 pt-2 border-t border-white/10">—</span>
                  </div>
                  </div>
                </div>
              );
            })()}
            </>
            ) : (
            <>
            <div className="py-2 border-b border-white/5">
              <div className="flex items-center gap-1.5 mb-3">
                <p className="text-white/60 text-xs font-normal uppercase tracking-wider">{ic.analyticsViewLabel}</p>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="text-white/40 hover:text-white/60 p-0.5 -mt-0.5" aria-label={`${ic.analyticsPartnerName} view info`}>
                      <Info className="w-3.5 h-3.5" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                    Hold Fees (15%) are their only revenue. Real-time recovery uses payment schedule + due dates vs today. Cancelled = deposit + 1/4 installments assumed recovered.
                  </PopoverContent>
                </Popover>
              </div>
              
              {/* Capital Deployed & Recovered */}
              <div className="mb-3">
                <p className="text-white/50 text-xs font-normal mb-1.5 uppercase tracking-wide">Capital Deployed & Recovered</p>
                <div className="space-y-0.5 ml-2">
                  {(() => {
                    const totalBacked = dcTotalBacked;
                    const isPaidLoading = paidLoadState === 'idle' || paidLoadState === 'loading';
                    const isPaidReady = !isPaidLoading && paidLoadState !== 'unavailable' && paidLoadState !== 'error';
                    const cancelledRefunded = cancellationRefundsExpense;
                    const totalVolume = totalBacked + (isPaidReady ? cancelledRefunded : 0);
                    const pct = (v) => totalBacked > 0 ? ((v / totalBacked) * 100).toFixed(1) : '0';
                    const pctOfTotal = (v) => totalVolume > 0 ? ((v / totalVolume) * 100).toFixed(1) : '0';
                    const paidPlaceholder = paidLoadState === 'unavailable'
                      ? <span className="text-white/40">Payments unavailable</span>
                      : paidLoadState === 'error'
                        ? <span className="text-red-400/80">Failed to load</span>
                        : <span className="text-white/40">Loading…</span>;
                    const InfoIcon = ({ content }) => (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                            <Info className="w-3 h-3" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                          {content}
                        </PopoverContent>
                      </Popover>
                    );
                    return (
                      <>
                        <div className="flex justify-between items-center py-0.5">
                          <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                            Total Backed
                            <InfoIcon content="Capital actually deployed: airline base + hotel stay across trips with a confirmation number on file (i.e. booked with the airline/hotel) and not cancelled. Service fee and hold fee are revenue owed by the customer, not capital Defera pays to the airline/hotel, so they're excluded here and recognized as fee revenue below." />
                          </span>
                          <span className="text-white/60 font-normal text-sm whitespace-nowrap">
                            <span className="text-white/50 mr-2">100%</span>${formatCurrency(totalBacked)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-0.5">
                          <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                            Total Recovered
                            <InfoIcon content="Capital repaid so far — actual customer payments applied to the base + stay slice of each backed trip, capped per booking at that trip's deployed capital. Sourced from BOTH Stripe (Defera Capital Stripe track, Defera Zero, Reserve) AND NMI/Vector (Defera Capital NMI track). Anything customers have paid beyond capital is fee revenue and shows up under Hold Fees / Service Fees below, not here. Served from a 3-tier cache (in-memory → DB fallback → live processor) so outages don't zero out the dashboard." />
                            {paidMeta && (paidMeta.stale > 0 || paidMeta.failed > 0) && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    type="button"
                                    className="text-brand-400/70 hover:text-brand-400 p-0.5 ml-1 align-middle inline-flex"
                                    aria-label="Some bookings served from cache"
                                  >
                                    <Info className="w-3 h-3" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                                  Some bookings are served from cached values because live processor lookups failed.
                                  <div className="mt-2 space-y-0.5 text-white/70">
                                    <div>Live Stripe: {paidMeta.from_stripe ?? 0}</div>
                                    <div>Live NMI (Vector): {paidMeta.from_nmi ?? 0}</div>
                                    <div>Memory cache: {paidMeta.from_memory ?? 0}</div>
                                    <div className="text-brand-400/80">DB fallback (stale): {paidMeta.stale ?? 0}</div>
                                    <div className="text-red-400/80">Failed (no cache): {paidMeta.failed ?? 0}</div>
                                    <div className="text-white/40">No subscription: {paidMeta.no_sub ?? 0}</div>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                          </span>
                          <span className="text-green-400/80 font-normal text-sm whitespace-nowrap">
                            {isPaidLoading || paidLoadState === 'unavailable' || paidLoadState === 'error' ? (
                              paidPlaceholder
                            ) : (
                              <>
                                <span className="text-white/50 mr-2">{pct(dcTotalRecovered)}%</span>${formatCurrency(dcTotalRecovered)}
                              </>
                            )}
                          </span>
                        </div>
                        {(totalCancelledCount > 0) && (
                          <div className="flex justify-between items-center py-0.5">
                            <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                              Total Cancelled
                              <InfoIcon content="Customer-paid amounts that were refunded as CASH on cancelled/refunded trips (sourced from Stripe and NMI/Vector — both processors are unified in the paid-amounts feed). Customers who paid $0 before cancelling don't appear here. Cash that was converted to Defera Credit on unbooked-trip cancels is excluded — it stayed with Defera and shows up under Profit from Cancellation Credit below." />
                            </span>
                            <span className="text-red-400/80 font-normal text-sm whitespace-nowrap">
                              {isPaidReady ? (
                                cancelledRefunded > 0 ? (
                                  <>
                                    <span className="text-white/50 mr-2">{pctOfTotal(cancelledRefunded)}%</span>${formatCurrency(cancelledRefunded)}
                                  </>
                                ) : (
                                  <span className="text-white/40">$0</span>
                                )
                              ) : (
                                paidPlaceholder
                              )}
                            </span>
                          </div>
                        )}
                        {totalDefaultedAmount > 0 && (
                          <div className="flex justify-between items-center py-0.5">
                            <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                              Total Defaulted
                              <InfoIcon content="Unrecovered capital on defaulted trips that were actually booked (confirmation number on file) — base + stay minus what customers paid (Stripe + NMI). Subset of Outstanding." />
                            </span>
                            <span className="text-red-400/80 font-normal text-sm whitespace-nowrap">
                              <span className="text-white/50 mr-2">{pct(totalDefaultedAmount)}%</span>${formatCurrency(totalDefaultedAmount)}
                            </span>
                          </div>
                        )}
                        {lossPreventedAmount > 0 && (
                          <div className="flex justify-between items-center py-0.5">
                            <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                              Profit from Loss
                              <InfoIcon content={`Customer payments collected on ${phantomDefaultedCount} defaulted trip${phantomDefaultedCount === 1 ? '' : 's'} that were never actually booked (no confirmation number on file). Since no funds were paid out to airlines or hotels, these amounts are recognized as profit instead of losses.`} />
                            </span>
                            <span className="text-green-400/80 font-normal text-sm whitespace-nowrap">
                              ${formatCurrency(lossPreventedAmount)}
                            </span>
                          </div>
                        )}
                        {dcCancellationCreditAmount > 0 && (
                          <div className="flex justify-between items-center py-0.5">
                            <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                              Profit from Cancellation Credit
                              <InfoIcon content={`Customer cash on ${dcCancellationCreditCount} cancelled Defera Capital trip${dcCancellationCreditCount === 1 ? '' : 's'} that were never booked with the airline/hotel and were refunded as Defera Credit instead of cash. The cash stayed with Defera and is recognized as profit (analogous to Profit from Loss). Realized as a revenue offset via the Credit Redeemed line below when the customer redeems the credit on a future trip. Excluded from Total Cancelled to avoid double-counting.`} />
                            </span>
                            <span className="text-green-400/80 font-normal text-sm whitespace-nowrap">
                              ${formatCurrency(dcCancellationCreditAmount)}
                            </span>
                          </div>
                        )}
                        {dcCreditRedeemedAmount > 0 && (
                          <div className="flex justify-between items-center py-0.5">
                            <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                              Credit Redeemed
                              <InfoIcon content={`Defera Credit committed at checkout across ${dcCreditRedeemedCount} Defera Capital booking${dcCreditRedeemedCount === 1 ? '' : 's'} (deposit credit + total installment credit). Trip revenue is recorded at gross sticker price, but this slice wasn't new cash — it was previously-issued Defera Credit being burned down. Subtracted from Net so the booking's recognised profit collapses to its real Hold + Service margin (less processing/investor/backer). Pairs with Profit from Cancellation Credit on the issue side: over a credit lifetime they net to $0.`} />
                            </span>
                            <span className="text-red-400/80 font-normal text-sm whitespace-nowrap">
                              ${formatCurrency(dcCreditRedeemedAmount)}
                            </span>
                          </div>
                        )}
                        {dcManualPartialRefundProfit > 0 && (
                          <div className="flex justify-between items-center py-0.5">
                            <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                              Profit from Partial Refund
                              <InfoIcon content={`Cancelled Defera Capital trips are excluded from revenue entirely (Total Cancelled above nets to $0 profit by default), which is correct when the customer got everything back. But on ${dcManualPartialRefundCount} trip${dcManualPartialRefundCount === 1 ? '' : 's'} an admin recorded a PARTIAL cash refund via "Amount Refunded" (Edit Flight Details) — Defera kept the difference between what the customer paid and what was refunded. That kept amount is recognized here as profit instead of vanishing along with the rest of the excluded trip revenue. Any portion already converted to Defera Credit is excluded (it's recognized under Profit from Cancellation Credit instead).`} />
                            </span>
                            <span className="text-green-400/80 font-normal text-sm whitespace-nowrap">
                              ${formatCurrency(dcManualPartialRefundProfit)}
                            </span>
                          </div>
                        )}
                        {profitFromFareAmount > 0 && (
                          <div className="flex justify-between items-center py-0.5">
                            <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                              Profit from Fare
                              <InfoIcon content={`Profit captured on ${profitFromFareCount} trip${profitFromFareCount === 1 ? '' : 's'} where the customer's locked-in Base Fare is higher than what Defera will pay (or paid) the airline. Two paths feed this: (1) confirmed trips where Defera booked later at a cheaper Actual Cost; (2) unconfirmed trips where the customer or admin changed flights and the new Base Fare came in lower than the original — the installment plan stays at the higher original, so the difference is realized as profit. Formula: Customer Base Fare − Actual / New Base Fare (only counted when positive).`} />
                            </span>
                            <span className="text-green-400/80 font-normal text-sm whitespace-nowrap">
                              ${formatCurrency(profitFromFareAmount)}
                            </span>
                          </div>
                        )}
                        {lossFromFareAmount > 0 && (
                          <div className="flex justify-between items-center py-0.5">
                            <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                              Loss from Fare
                              <InfoIcon content={`Loss absorbed on ${lossFromFareCount} trip${lossFromFareCount === 1 ? '' : 's'} where Defera will pay (or paid) the airline more than the customer's locked-in Base Fare. Two paths feed this: (1) confirmed trips where the Actual Cost ended up higher than the billed Base Fare; (2) unconfirmed trips where the customer or admin changed flights and the new Base Fare came in higher than the original — the installment plan stays at the lower original, so the extra cost is eaten by Defera. Formula: Customer Base Fare − Actual / New Base Fare (only counted when negative, shown here as the absolute amount).`} />
                            </span>
                            <span className="text-red-400/80 font-normal text-sm whitespace-nowrap">
                              ${formatCurrency(lossFromFareAmount)}
                            </span>
                          </div>
                        )}
                        {lossPreventedReference > 0 && (
                          <div className="flex justify-between items-center py-0.5">
                            <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                              Loss Prevented
                              <InfoIcon content="Reference only — not used in any Net or profit calculation. Sum of Base Fare minus customer-paid amount across phantom defaulted trips. Represents the loss Defera would have absorbed if these trips had actually been booked with the airline." />
                            </span>
                            <span className="text-white/40 font-normal text-sm whitespace-nowrap italic">
                              ${formatCurrency(lossPreventedReference)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between items-center py-0.5 border-t border-white/5 mt-1 pt-1">
                          <span className="text-white/60 font-normal text-sm whitespace-nowrap flex items-center">
                            Outstanding
                            <InfoIcon content="Total Backed minus Total Recovered. Capital still owed by customers." />
                          </span>
                          <span className="text-brand-400/80 font-normal text-sm whitespace-nowrap">
                            {isPaidLoading || paidLoadState === 'unavailable' || paidLoadState === 'error' ? (
                              paidPlaceholder
                            ) : (
                              <>
                                <span className="text-white/50 mr-2">{pct(dcOutstanding)}%</span>${formatCurrency(dcOutstanding)}
                              </>
                            )}
                          </span>
                        </div>
                        {dcBackedFlights.length > 0 && (
                          <div className="flex justify-between items-center py-0.5 border-t border-white/5 mt-1 pt-1">
                            <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                              Default Rate
                              <InfoIcon content="Defaulted (unrecovered) amount as a share of Total Backed." />
                            </span>
                            <span className="text-white/60 font-normal text-sm whitespace-nowrap">
                              {dcDefaultRate.toFixed(1)}%
                            </span>
                          </div>
                        )}
                        {filteredFlights.length > 0 && (
                          <div className="flex justify-between items-center py-0.5">
                            <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                              Cancellation Rate
                              <InfoIcon content="Share of trips cancelled over the period." />
                            </span>
                            <span className="text-white/60 font-normal text-sm whitespace-nowrap">
                              {slicePayCancellationRate.toFixed(1)}%
                            </span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
              
              {/* Revenue */}
              {(() => {
                const isPaidLoading = paidLoadState === 'idle' || paidLoadState === 'loading';
                const paidPlaceholder = paidLoadState === 'unavailable'
                  ? <span className="text-white/40">Payments unavailable</span>
                  : paidLoadState === 'error'
                    ? <span className="text-red-400/80">Failed to load</span>
                    : <span className="text-white/40">Loading…</span>;
                const showRecoveredValue = !isPaidLoading && paidLoadState !== 'unavailable' && paidLoadState !== 'error';
                return (
                  <div className="mb-3">
                    <p className="text-white/50 text-xs font-normal mb-1.5 uppercase tracking-wide">Revenue</p>
                    <div className="space-y-0.5 ml-2">
                      <div className="flex justify-between items-center py-0.5">
                        <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                          Hold Fees
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                                <Info className="w-3 h-3" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                              15% of cash collected from customers (Stripe + NMI). Recognized as customer payments are settled by either processor.
                            </PopoverContent>
                          </Popover>
                        </span>
                        <span className="text-green-400 font-normal text-sm whitespace-nowrap">
                          {showRecoveredValue ? (
                            <>
                              {dcTotalBacked > 0 && (
                                <span className="text-white/50 mr-2">
                                  {((dcHoldFees / dcTotalBacked) * 100).toFixed(1)}%
                                </span>
                              )}
                              ${formatCurrency(dcHoldFees)}
                            </>
                          ) : (
                            paidPlaceholder
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-0.5">
                        <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                          Service Fees
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                                <Info className="w-3 h-3" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                              Service fee revenue recognized in proportion to cash actually collected across Stripe + NMI (cash collected ÷ total customer billing × accrued service fees).
                            </PopoverContent>
                          </Popover>
                        </span>
                        <span className="text-green-400 font-normal text-sm whitespace-nowrap">
                          {showRecoveredValue ? (
                            <>
                              {dcTotalBacked > 0 && (
                                <span className="text-white/50 mr-2">
                                  {((dcServiceFees / dcTotalBacked) * 100).toFixed(1)}%
                                </span>
                              )}
                              ${formatCurrency(dcServiceFees)}
                            </>
                          ) : (
                            paidPlaceholder
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Expenses */}
              {(() => {
                const isPaidLoading = paidLoadState === 'idle' || paidLoadState === 'loading';
                const paidPlaceholder = paidLoadState === 'unavailable'
                  ? <span className="text-white/40">Payments unavailable</span>
                  : paidLoadState === 'error'
                    ? <span className="text-red-400/80">Failed to load</span>
                    : <span className="text-white/40">Loading…</span>;
                const showRecoveredValue = !isPaidLoading && paidLoadState !== 'unavailable' && paidLoadState !== 'error';
                return (
                  <div className="mb-3">
                    <p className="text-white/50 text-xs font-normal mb-1.5 uppercase tracking-wide">Expenses</p>
                    <div className="space-y-0.5 ml-2">
                      <div className="flex justify-between items-center py-0.5">
                        <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                          Investor Fees
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                                <Info className="w-3 h-3" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                              14% of cash collected from customers (Stripe + NMI). Recognized as customers actually pay; accrued = 14% × total customer billing (base + stay + service fee + hold fee, i.e. what we would collect if everything paid off).
                            </PopoverContent>
                          </Popover>
                        </span>
                        <span className="text-red-400/80 font-normal text-sm whitespace-nowrap">
                          {showRecoveredValue ? (
                            <>
                              {dcTotalBacked > 0 && (
                                <span className="text-white/50 mr-2">
                                  {((dcInvestorFees / dcTotalBacked) * 100).toFixed(1)}%
                                </span>
                              )}
                              ${formatCurrency(dcInvestorFees)}
                            </>
                          ) : (
                            paidPlaceholder
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-0.5">
                        <span className="text-white/40 font-normal text-sm whitespace-nowrap">Defera Capital</span>
                        <span className="text-red-400/80 font-normal text-sm whitespace-nowrap">
                          {showRecoveredValue ? (
                            <>
                              {dcTotalBacked > 0 && (
                                <span className="text-white/50 mr-2">
                                  {((dcBackerFees / dcTotalBacked) * 100).toFixed(1)}%
                                </span>
                              )}
                              ${formatCurrency(dcBackerFees)}
                            </>
                          ) : (
                            paidPlaceholder
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-0.5">
                        <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                          Stripe Fees
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                                <Info className="w-3 h-3" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                              4.4% of cash actually collected via Stripe on Stripe-routed plans (Defera Capital Stripe track, Defera Zero, Reserve). Accrued = 4.4% × grand total with hold + per-charge fixed fees — what we would owe Stripe if every backed Stripe-routed trip paid off in full. NMI-track Defera Capital bookings are shown separately.
                            </PopoverContent>
                          </Popover>
                        </span>
                        <span className="text-red-400/80 font-normal text-sm whitespace-nowrap">
                          {showRecoveredValue ? (
                            <>
                              {dcTotalBacked > 0 && (
                                <span className="text-white/50 mr-2">
                                  {((dcStripeFees / dcTotalBacked) * 100).toFixed(1)}%
                                </span>
                              )}
                              ${formatCurrency(dcStripeFees)}
                            </>
                          ) : (
                            paidPlaceholder
                          )}
                        </span>
                      </div>
                      {(dcNmiFees > 0 || dcAccruedNmiFees > 0) && (
                        <div className="flex justify-between items-center py-0.5">
                          <span className="text-white/40 font-normal text-sm whitespace-nowrap flex items-center">
                            NMI Fees
                            <Popover>
                              <PopoverTrigger asChild>
                                <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                                  <Info className="w-3 h-3" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-72 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                                3.95% of cash actually collected via NMI on Defera Capital bookings routed through NMI (Vector Payments). Accrued = 3.95% × grand total with hold — what we would owe NMI if every backed NMI-track trip paid off in full. No Stripe rolling reserve holds.
                              </PopoverContent>
                            </Popover>
                          </span>
                          <span className="text-red-400/80 font-normal text-sm whitespace-nowrap">
                            {showRecoveredValue ? (
                              <>
                                {dcTotalBacked > 0 && (
                                  <span className="text-white/50 mr-2">
                                    {((dcNmiFees / dcTotalBacked) * 100).toFixed(1)}%
                                  </span>
                                )}
                                ${formatCurrency(dcNmiFees)}
                              </>
                            ) : (
                              paidPlaceholder
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Net + Accrued */}
              <div className="flex justify-between items-center py-1 border-t border-white/10 mt-2 pt-2">
                <span className="text-white font-normal text-sm whitespace-nowrap uppercase tracking-wide flex items-center">
                  Net
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-1 align-middle inline-flex" aria-label="Info">
                        <Info className="w-3.5 h-3.5" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                      Hold Fees + Service Fees − Investor Fees − Defera Capital − Processing Fees (Stripe + NMI) − Outstanding + Profit from Loss + Profit from Fare − Loss from Fare + Profit from Partial Refund. Real-time, based on actual collections.
                    </PopoverContent>
                  </Popover>
                </span>
                <span className={`font-normal text-sm whitespace-nowrap ${dcNetProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {dcTotalBacked > 0 && (
                    <span className="text-white/50 mr-2">
                      {((dcNetProfit / dcTotalBacked) * 100).toFixed(1)}%
                    </span>
                  )}
                  ${formatCurrency(Math.abs(dcNetProfit))}
                </span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-white/60 font-normal text-sm whitespace-nowrap flex items-center">
                  Net exc. outstanding
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                        <Info className="w-3 h-3" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                      Hold Fees + Service Fees − Investor Fees − Defera Capital − Processing Fees (Stripe + NMI) + Profit from Loss + Profit from Fare − Loss from Fare + Profit from Partial Refund. Real-time, based only on what has actually occurred (no outstanding capital drag).
                    </PopoverContent>
                  </Popover>
                </span>
                <span className={`font-normal text-sm whitespace-nowrap ${dcNetExclOutstanding >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {dcTotalBacked > 0 && (
                    <span className="text-white/50 mr-2">
                      {((dcNetExclOutstanding / dcTotalBacked) * 100).toFixed(1)}%
                    </span>
                  )}
                  ${formatCurrency(Math.abs(dcNetExclOutstanding))}
                </span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-white/60 font-normal text-sm whitespace-nowrap flex items-center">
                  Accrued Net
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                        <Info className="w-3 h-3" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                      Net assuming everything backed has been paid off: (Accrued Hold Fees + Accrued Service Fees) − Accrued Investor Fees − Accrued Defera Capital − Accrued Processing Fees (Stripe + NMI) + Profit from Loss + Profit from Fare − Loss from Fare + Profit from Partial Refund.
                    </PopoverContent>
                  </Popover>
                </span>
                <span className={`font-normal text-sm whitespace-nowrap ${dcAccruedNet >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {dcTotalBacked > 0 && (
                    <span className="text-white/50 mr-2">
                      {((dcAccruedNet / dcTotalBacked) * 100).toFixed(1)}%
                    </span>
                  )}
                  ${formatCurrency(Math.abs(dcAccruedNet))}
                </span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-white/60 font-normal text-sm whitespace-nowrap flex items-center">
                  Accrued Service Fees
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                        <Info className="w-3 h-3" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                      Sum of service fees across all backed trips — what we'd recognize if every backed trip paid off in full.
                    </PopoverContent>
                  </Popover>
                </span>
                <span className="text-green-400/80 font-normal text-sm whitespace-nowrap">
                  {dcTotalBacked > 0 && (
                    <span className="text-white/50 mr-2">
                      {((dcAccruedServiceFees / dcTotalBacked) * 100).toFixed(1)}%
                    </span>
                  )}
                  ${formatCurrency(dcAccruedServiceFees)}
                </span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-white/60 font-normal text-sm whitespace-nowrap flex items-center">
                  Accrued Hold Fees
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                        <Info className="w-3 h-3" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                      Sum of the hold fee charged on each backed trip (booking-level `hold_fee`, roughly 15% of base + service fee at booking time) — what we would recognize if every backed trip paid off in full.
                    </PopoverContent>
                  </Popover>
                </span>
                <span className="text-green-400/80 font-normal text-sm whitespace-nowrap">
                  {dcTotalBacked > 0 && (
                    <span className="text-white/50 mr-2">
                      {((dcAccruedHoldFees / dcTotalBacked) * 100).toFixed(1)}%
                    </span>
                  )}
                  ${formatCurrency(dcAccruedHoldFees)}
                </span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-white/60 font-normal text-sm whitespace-nowrap flex items-center">
                  Accrued Stripe Fees
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                        <Info className="w-3 h-3" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                      4.4% (2.9% Stripe + 1.5% instant transfer) of each Stripe-routed booking&apos;s grand total with hold, plus per-charge fixed fees ($0.30 × ~6 charges on Defera Capital / Zero Stripe track, × 2 on Reserve) — what we would pay Stripe if every backed Stripe-routed trip paid off in full. NMI bookings excluded (see Accrued NMI Fees).
                    </PopoverContent>
                  </Popover>
                </span>
                <span className="text-red-400/80 font-normal text-sm whitespace-nowrap">
                  {dcTotalBacked > 0 && (
                    <span className="text-white/50 mr-2">
                      {((dcAccruedStripeFees / dcTotalBacked) * 100).toFixed(1)}%
                    </span>
                  )}
                  ${formatCurrency(dcAccruedStripeFees)}
                </span>
              </div>
              {(dcAccruedNmiFees > 0) && (
              <div className="flex justify-between items-center py-1">
                <span className="text-white/60 font-normal text-sm whitespace-nowrap flex items-center">
                  Accrued NMI Fees
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="text-white/40 hover:text-white/60 p-0.5 ml-0.5 align-middle inline-flex" aria-label="Info">
                        <Info className="w-3 h-3" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 bg-[#111] border-white/20 text-white text-xs font-normal" align="start">
                      3.95% of each NMI-routed booking&apos;s grand total with hold on Defera Capital bookings routed through NMI (Vector Payments) — what we would pay if every NMI-track backed trip paid off in full. No Stripe rolling reserve holds.
                    </PopoverContent>
                  </Popover>
                </span>
                <span className="text-red-400/80 font-normal text-sm whitespace-nowrap">
                  {dcTotalBacked > 0 && (
                    <span className="text-white/50 mr-2">
                      {((dcAccruedNmiFees / dcTotalBacked) * 100).toFixed(1)}%
                    </span>
                  )}
                  ${formatCurrency(dcAccruedNmiFees)}
                </span>
              </div>
              )}
            </div>
            </>
            )}
          </div>
        </motion.div>

        {/* New Charts Grid - Non-Cumulative (Daily New) */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 mb-12">
          {/* New Volume Chart (Daily) - Bar Chart for better daily comparison */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-white font-light text-lg mb-4 tracking-tight">New Volume</h2>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={newVolumeChartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <XAxis 
                    dataKey="date" 
                    stroke="#ffffff50" 
                    style={{ fontSize: '12px', fontWeight: '400', fill: '#ffffff80' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    stroke="#ffffff30"
                    style={{ fontSize: '12px', fontWeight: '400', fill: '#ffffff50' }}
                    tickFormatter={(value) => `$${value}`}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#000000f5', 
                      border: '1px solid #ffffff15',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontWeight: '500',
                      padding: '8px 12px'
                    }}
                    formatter={(value) => [`$${fmtAmount(parseFloat(value))}`, 'New Revenue']}
                    cursor={{ fill: '#10b98120' }}
                  />
                  <Bar 
                    dataKey="revenue" 
                    fill="#10b981" 
                    radius={[4, 4, 0, 0]}
                    animationDuration={1500}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* New Bookings Chart (Daily) - Count of new bookings, complements New Volume ($) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <h2 className="text-white font-light text-lg mb-4 tracking-tight">New Bookings</h2>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={newBookingsChartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <XAxis
                    dataKey="date"
                    stroke="#ffffff50"
                    style={{ fontSize: '12px', fontWeight: '400', fill: '#ffffff80' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#ffffff30"
                    style={{ fontSize: '12px', fontWeight: '400', fill: '#ffffff50' }}
                    tickFormatter={(value) => value.toLocaleString()}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#000000f5',
                      border: '1px solid #ffffff15',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontWeight: '500',
                      padding: '8px 12px'
                    }}
                    formatter={(value) => [value.toLocaleString(), 'New Bookings']}
                    cursor={{ fill: '#809AFA20' }}
                  />
                  <Bar
                    dataKey="bookings"
                    fill="#7A8ABF"
                    radius={[4, 4, 0, 0]}
                    animationDuration={1500}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* New Carts Chart (Daily) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="text-white font-light text-lg mb-4 tracking-tight">New Carts</h2>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={newCartsChartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <XAxis 
                    dataKey="date" 
                    stroke="#ffffff50" 
                    style={{ fontSize: '12px', fontWeight: '400', fill: '#ffffff80' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    stroke="#ffffff30"
                    style={{ fontSize: '12px', fontWeight: '400', fill: '#ffffff50' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#000000f5', 
                      border: '1px solid #ffffff15',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontWeight: '500',
                      padding: '8px 12px'
                    }}
                    formatter={(value) => [value.toLocaleString(), 'New Carts']}
                    cursor={{ fill: '#2A29EB20' }}
                  />
                  <Bar 
                    dataKey="carts" 
                    fill="#2A3868" 
                    radius={[4, 4, 0, 0]}
                    animationDuration={1500}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* New Users Chart (Daily) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
          >
            <h2 className="text-white font-light text-lg mb-4 tracking-tight">New Users</h2>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={newUsersChartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <XAxis 
                    dataKey="date" 
                    stroke="#ffffff50" 
                    style={{ fontSize: '12px', fontWeight: '400', fill: '#ffffff80' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    stroke="#ffffff30"
                    style={{ fontSize: '12px', fontWeight: '400', fill: '#ffffff50' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#000000f5', 
                      border: '1px solid #ffffff15',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontWeight: '500',
                      padding: '8px 12px'
                    }}
                    formatter={(value) => [value.toLocaleString(), 'New Users']}
                    cursor={{ fill: '#2A29EB20' }}
                  />
                  <Bar 
                    dataKey="users" 
                    fill="#2A3868" 
                    radius={[4, 4, 0, 0]}
                    animationDuration={1500}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </div>

        {/* Charts Grid - Side by side on Desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 mb-12">
          {/* Volume Chart - Robinhood Style */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
          >
            <h2 className="text-white font-light text-lg mb-4 tracking-tight">Volume</h2>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueChartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.4}/>
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="date" 
                    stroke="#ffffff50" 
                    style={{ fontSize: '12px', fontWeight: '400', fill: '#ffffff80' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    stroke="#ffffff30"
                    style={{ fontSize: '12px', fontWeight: '400', fill: '#ffffff50' }}
                    tickFormatter={(value) => `$${value}`}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#000000f5', 
                      border: '1px solid #ffffff15',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontWeight: '500',
                      padding: '8px 12px'
                    }}
                    formatter={(value) => [`$${fmtAmount(parseFloat(value))}`, 'Revenue']}
                    cursor={{ stroke: '#10b981', strokeWidth: 1, strokeDasharray: '3 3' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#10b981" 
                    strokeWidth={2.5}
                    fill="url(#revenueGradient)" 
                    animationDuration={1500}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Bookings Chart (Cumulative) - Count of bookings, complements Volume ($) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.475 }}
          >
            <h2 className="text-white font-light text-lg mb-4 tracking-tight">Bookings</h2>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={bookingsChartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="bookingsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7A8ABF" stopOpacity={0.4}/>
                      <stop offset="100%" stopColor="#7A8ABF" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    stroke="#ffffff50"
                    style={{ fontSize: '12px', fontWeight: '400', fill: '#ffffff80' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#ffffff30"
                    style={{ fontSize: '12px', fontWeight: '400', fill: '#ffffff50' }}
                    tickFormatter={(value) => value.toLocaleString()}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#000000f5',
                      border: '1px solid #ffffff15',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontWeight: '500',
                      padding: '8px 12px'
                    }}
                    formatter={(value) => [value.toLocaleString(), 'Bookings']}
                    cursor={{ stroke: '#7A8ABF', strokeWidth: 1, strokeDasharray: '3 3' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="bookings"
                    stroke="#7A8ABF"
                    strokeWidth={2.5}
                    fill="url(#bookingsGradient)"
                    animationDuration={1500}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Carts Chart - Robinhood Style */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h2 className="text-white font-light text-lg mb-4 tracking-tight">Carts</h2>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={cartsChartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="cartsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2A3868" stopOpacity={0.4}/>
                      <stop offset="100%" stopColor="#2A3868" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="date" 
                    stroke="#ffffff50" 
                    style={{ fontSize: '12px', fontWeight: '400', fill: '#ffffff80' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    stroke="#ffffff30"
                    style={{ fontSize: '12px', fontWeight: '400', fill: '#ffffff50' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#000000f5', 
                      border: '1px solid #ffffff15',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontWeight: '500',
                      padding: '8px 12px'
                    }}
                    formatter={(value) => [value.toLocaleString(), 'Carts']}
                    cursor={{ stroke: '#2A3868', strokeWidth: 1, strokeDasharray: '3 3' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="carts" 
                    stroke="#2A3868" 
                    strokeWidth={2.5}
                    fill="url(#cartsGradient)" 
                    animationDuration={1500}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Users Chart - Robinhood Style */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
          >
            <h2 className="text-white font-light text-lg mb-4 tracking-tight">Users</h2>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={usersChartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="usersGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2A3868" stopOpacity={0.4}/>
                      <stop offset="100%" stopColor="#2A3868" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="date" 
                    stroke="#ffffff50" 
                    style={{ fontSize: '12px', fontWeight: '400', fill: '#ffffff80' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    stroke="#ffffff30"
                    style={{ fontSize: '12px', fontWeight: '400', fill: '#ffffff50' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#000000f5', 
                      border: '1px solid #ffffff15',
                      borderRadius: '8px',
                      color: '#ffffff',
                      fontWeight: '500',
                      padding: '8px 12px'
                    }}
                    formatter={(value) => [value.toLocaleString(), 'Users']}
                    cursor={{ stroke: '#2A3868', strokeWidth: 1, strokeDasharray: '3 3' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="users" 
                    stroke="#2A3868" 
                    strokeWidth={2.5}
                    fill="url(#usersGradient)" 
                    animationDuration={1500}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </div>

        {/* How Did You Hear About Us - Referral Sources Pie Charts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="mb-12"
        >
          <h2 className="text-white font-light text-xl mb-6 tracking-tight">How Did You Hear About Us?</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* All Signups Referral Chart */}
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="text-white/70 text-sm font-normal mb-4">All Signups</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="h-48">
                  {referralSourceData.length === 0 ? (
                    <div className="h-full flex items-center justify-center">
                      <p className="text-white/40 text-sm font-normal">No referral data available yet</p>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={referralSourceData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={65}
                          paddingAngle={5}
                          dataKey="value"
                          animationDuration={1000}
                        >
                          {referralSourceData.map((entry, index) => (
                            <Cell key={`cell-signup-${index}`} fill={REFERRAL_COLORS[index % REFERRAL_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#000000f5', 
                            border: '1px solid #ffffff15',
                            borderRadius: '8px',
                            padding: '8px 12px'
                          }}
                          itemStyle={{ color: '#ffffff', fontWeight: '500' }}
                          labelStyle={{ color: '#ffffff', fontWeight: '500' }}
                          formatter={(value, name) => [`${value} users`, name]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
                <div className="flex flex-col justify-center space-y-2">
                  {referralSourceData.map((source, index) => {
                    const totalReferrals = referralSourceData.reduce((sum, s) => sum + s.value, 0);
                    return (
                      <div key={source.name} className="flex items-center gap-2">
                        <div 
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: REFERRAL_COLORS[index % REFERRAL_COLORS.length] }}
                        />
                        <span className="text-white/60 text-xs font-normal flex-1 truncate">{source.name}</span>
                        <span className="text-white text-xs font-medium">{source.value}</span>
                        <span className="text-white/40 text-xs font-normal">
                          ({((source.value / totalReferrals) * 100).toFixed(0)}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Converted to Bookings Referral Chart */}
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="text-white/70 text-sm font-normal mb-4">Converted to Bookings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="h-48">
                  {referralToBookingsData.length === 0 ? (
                    <div className="h-full flex items-center justify-center">
                      <p className="text-white/40 text-sm font-normal">No booking data yet</p>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={referralToBookingsData}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={65}
                          paddingAngle={5}
                          dataKey="value"
                          animationDuration={1000}
                        >
                          {referralToBookingsData.map((entry, index) => (
                            <Cell key={`cell-booking-${index}`} fill={REFERRAL_COLORS[index % REFERRAL_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#000000f5', 
                            border: '1px solid #ffffff15',
                            borderRadius: '8px',
                            padding: '8px 12px'
                          }}
                          itemStyle={{ color: '#ffffff', fontWeight: '500' }}
                          labelStyle={{ color: '#ffffff', fontWeight: '500' }}
                          formatter={(value, name) => [`${value} bookings`, name]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
                <div className="flex flex-col justify-center space-y-2">
                  {referralToBookingsData.length === 0 ? (
                    <p className="text-white/30 text-xs font-normal">No conversions yet</p>
                  ) : (
                    referralToBookingsData.map((source, index) => {
                      const totalBookings = referralToBookingsData.reduce((sum, s) => sum + s.value, 0);
                      return (
                        <div key={source.name} className="flex items-center gap-2">
                          <div 
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                            style={{ backgroundColor: REFERRAL_COLORS[index % REFERRAL_COLORS.length] }}
                          />
                          <span className="text-white/60 text-xs font-normal flex-1 truncate">{source.name}</span>
                          <span className="text-white text-xs font-medium">{source.value}</span>
                          <span className="text-white/40 text-xs font-normal">
                            ({((source.value / totalBookings) * 100).toFixed(0)}%)
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats Section: Top Airlines, Cart Stages, User Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12"
        >
          {/* Top Airlines */}
          <div>
            <div className="text-sm text-white/60 font-normal mb-2">Top Airlines</div>
            <div className="space-y-1.5">
              {topAirlines.map((airline, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-xs text-white/50 font-normal truncate">{airline.airline}</span>
                  <span className="text-sm text-white font-normal ml-2">{airline.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Cart Stages */}
          <div>
            <div className="text-sm text-white/60 font-normal mb-2">Cart Stages</div>
            <div className="space-y-1.5">
              <div className="flex justify-between items-center">
                <span className="text-xs text-white/50 font-normal">Review</span>
                <span className="text-sm text-white font-normal">{stepBreakdown.review}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-white/50 font-normal">Passengers</span>
                <span className="text-sm text-white font-normal">{stepBreakdown.passenger_details}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-white/50 font-normal">Payment</span>
                <span className="text-sm text-white font-normal">{stepBreakdown.payment_initiated}</span>
              </div>
            </div>
          </div>

          {/* User Stats */}
          <div>
            <div className="text-sm text-white/60 font-normal mb-2">User Stats</div>
            <div className="space-y-1.5">
              <div className="flex justify-between items-center">
                <span className="text-xs text-white/50 font-normal">Total</span>
                <span className="text-sm text-white font-normal">{userCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-white/50 font-normal">Abandoned</span>
                <span className="text-sm text-white font-normal">{abandonedCount}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Cancellation Tracker - AI-powered loss prevention */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-12"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white font-light text-xl tracking-tight flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-400/80" />
              Cancellation Tracker
            </h2>
            <button
              onClick={runCancellationAnalysis}
              disabled={isLoadingCancellationAnalysis}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 font-normal text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingCancellationAnalysis ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Brain className="w-4 h-4" />
                  Run AI Analysis
                </>
              )}
            </button>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
            {cancellationAnalysisError && (
              <div className="px-4 py-3 flex items-center gap-3 text-red-300 bg-red-500/10 border-b border-red-500/20">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <p className="text-sm">{cancellationAnalysisError}</p>
              </div>
            )}

            {!cancellationAnalysis && !cancellationAnalysisError && (
              <div className="px-6 py-10 text-center">
                <Shield className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/60 text-sm font-normal max-w-md mx-auto">
                  Run AI analysis to identify patterns across routes, airlines, demographics, and passenger data.
                </p>
                <p className="text-white/30 text-xs mt-1">Requires OPENAI_API_KEY. All-time cancelled bookings.</p>
              </div>
            )}

            {cancellationAnalysis && (
              <div className="divide-y divide-white/5">
                {/* Stats strip - prominent, scannable */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/5">
                  <div className="bg-black/50 px-5 py-4">
                    <p className="text-white/50 text-[11px] uppercase tracking-wider font-normal">Trips</p>
                    <p className="text-white text-2xl font-light mt-0.5">{cancellationAnalysis.cancelledCount}</p>
                  </div>
                  <div className="bg-black/50 px-5 py-4">
                    <p className="text-white/50 text-[11px] uppercase tracking-wider font-normal">Volume</p>
                    <p className="text-red-300 text-2xl font-light mt-0.5">${formatCurrency(cancellationAnalysis.totalRevenueLost || 0)}</p>
                  </div>
                  {cancellationAnalysis.rawSummary?.routes?.length > 0 && (
                    <div className="bg-black/50 px-5 py-4 hidden sm:block">
                      <p className="text-white/50 text-[11px] uppercase tracking-wider font-normal">Routes</p>
                      <p className="text-white text-lg font-light mt-0.5">{cancellationAnalysis.rawSummary.routes.length}</p>
                    </div>
                  )}
                  {cancellationAnalysis.rawSummary?.airlines?.length > 0 && (
                    <div className="bg-black/50 px-5 py-4 hidden sm:block">
                      <p className="text-white/50 text-[11px] uppercase tracking-wider font-normal">Airlines</p>
                      <p className="text-white text-lg font-light mt-0.5">{cancellationAnalysis.rawSummary.airlines.length}</p>
                    </div>
                  )}
                </div>

                {/* Content grid - two columns on desktop */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                  {/* Left: Summary + Patterns */}
                  <div className="p-5 space-y-4 border-b lg:border-b-0 lg:border-r border-white/5">
                    {cancellationAnalysis.analysis?.executive_summary && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-white/40 font-normal mb-1.5">Summary</p>
                        <p className="text-white/90 text-sm font-normal leading-relaxed">
                          {typeof cancellationAnalysis.analysis.executive_summary === 'string'
                            ? stripAnalysisLabels(cancellationAnalysis.analysis.executive_summary)
                            : JSON.stringify(cancellationAnalysis.analysis.executive_summary)}
                        </p>
                      </div>
                    )}
                    {cancellationAnalysis.analysis?.top_patterns?.length > 0 && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-white/40 font-normal mb-2">Patterns</p>
                        <div className="space-y-2">
                          {cancellationAnalysis.analysis.top_patterns.map((p, i) => (
                            <div key={i} className="py-2 border-b border-white/5 last:border-0">
                              <div className="flex items-center gap-2">
                                <span className="text-white text-sm font-medium">{typeof p.pattern === 'string' ? p.pattern : JSON.stringify(p.pattern)}</span>
                                <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase ${
                                  p.confidence === 'high' ? 'bg-emerald-500/20 text-emerald-400' :
                                  p.confidence === 'medium' ? 'bg-brand-500/20 text-brand-400' :
                                  'bg-white/10 text-white/50'
                                }`}>{p.confidence}</span>
                              </div>
                              <p className="text-white/60 text-xs mt-0.5">{typeof p.description === 'string' ? stripAnalysisLabels(p.description) : JSON.stringify(p.description)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right: Demographics + Risk Factors */}
                  <div className="p-5 space-y-4">
                    {cancellationAnalysis.analysis?.demographic_insights && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-white/40 font-normal mb-1.5">Demographics</p>
                        <div className="text-white/80 text-xs font-normal leading-relaxed whitespace-pre-line">
                          {typeof cancellationAnalysis.analysis.demographic_insights === 'string'
                            ? stripAnalysisLabels(cancellationAnalysis.analysis.demographic_insights)
                            : typeof cancellationAnalysis.analysis.demographic_insights === 'object' && cancellationAnalysis.analysis.demographic_insights !== null
                              ? Object.entries(cancellationAnalysis.analysis.demographic_insights).map(([key, val]) => (
                                  <div key={key} className="mb-3 last:mb-0">
                                    <span className="text-white/50 text-[10px] uppercase block mb-0.5">{key.replace(/_/g, ' ')}</span>
                                    <p className="text-white/80">{typeof val === 'string' ? stripAnalysisLabels(val) : (Array.isArray(val) ? (val.every((x) => typeof x === 'string') ? val.map(stripAnalysisLabels).join(', ') : JSON.stringify(val, null, 2)) : JSON.stringify(val, null, 2))}</p>
                                  </div>
                                ))
                              : String(cancellationAnalysis.analysis.demographic_insights)}
                        </div>
                      </div>
                    )}
                    {cancellationAnalysis.analysis?.risk_factors?.length > 0 && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wider text-white/40 font-normal mb-1.5">Risk Factors</p>
                        <ul className="space-y-0.5">
                          {cancellationAnalysis.analysis.risk_factors.map((r, i) => (
                            <li key={i} className="text-white/70 text-xs font-normal flex items-start gap-1.5">
                              <span className="text-red-400/70">•</span>
                              {typeof r === 'string' ? stripAnalysisLabels(r) : JSON.stringify(r)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {cancellationAnalysis.analysis?.data_quality_note && (
                      <p className="text-white/35 text-[11px] italic pt-2 border-t border-white/5">
                        {typeof cancellationAnalysis.analysis.data_quality_note === 'string'
                          ? stripAnalysisLabels(cancellationAnalysis.analysis.data_quality_note)
                          : JSON.stringify(cancellationAnalysis.analysis.data_quality_note)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Live Trips Map */}
        {todaysFlights.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-12"
          >
            <h2 className="text-white font-light text-lg mb-4 tracking-tight">
              Today's Live Trips ({todaysFlights.length})
            </h2>
            <div className="rounded-2xl overflow-hidden" style={{ height: '450px', width: '100%' }}>
              <style>{`
                .leaflet-container { background: #000000; border-radius: 1rem; }
                .leaflet-control-zoom { display: none; }
                .pulsing-dot-container { position: relative; }
                .pulsing-dot {
                  width: 12px; height: 12px; border-radius: 50%;
                  box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
                  animation: pulse 2.5s infinite;
                }
                @keyframes pulse {
                  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7); }
                  70% { transform: scale(1.2); box-shadow: 0 0 0 10px rgba(255, 255, 255, 0); }
                  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
                }
                .leaflet-tooltip-custom {
                  background-color: transparent !important; border: none !important; box-shadow: none !important;
                  color: #d1d5db !important; font-size: 10px !important; font-weight: bold; text-shadow: 0 0 3px #000;
                }
              `}</style>
              <MapContainer center={[20, 0]} zoom={2} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
                {/* The basemap's credit stays visible (src/utils/basemap.js). */}
                <DarkBasemap />
                {todaysFlights.map((flight) => (
                  <React.Fragment key={flight.id}>
                    <Marker position={flight.fromCoords} icon={pulsingIcon('#2A3868')}>
                      <LeafletTooltip direction="top" offset={[0, -5]} opacity={1} permanent className="leaflet-tooltip-custom">
                        {flight.fromCity}
                      </LeafletTooltip>
                    </Marker>
                    <Marker position={flight.toCoords} icon={pulsingIcon('#10b981')}>
                      <LeafletTooltip direction="top" offset={[0, -5]} opacity={1} permanent className="leaflet-tooltip-custom">
                        {flight.toCity}
                      </LeafletTooltip>
                    </Marker>
                    <Polyline positions={[flight.fromCoords, flight.toCoords]} color="#2A3868" weight={2} opacity={0.6} dashArray="5, 10" />
                  </React.Fragment>
                ))}
              </MapContainer>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
